import { Component, OnInit, OnDestroy, Input, HostListener, ViewChild, ViewChildren, ChangeDetectorRef } from '@angular/core';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { parseEncodedBarcode, decodedQuantity, DecodedBarcode } from '../../services/barcode-parser/barcode-parser';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { IoService } from '../../services/io/io.service';
import { ReportService } from '../../services/report/report.service';
import { LocationService } from '../../services/location/location.service';
import { TagPrintService } from '../../services/tag-print/tag-print.service';
import { switchMap } from 'rxjs/operators';
import { AuthorizationService } from '../../services/authorization/authorization.service';
import { CdkVirtualScrollViewport, ScrollDispatcher } from '@angular/cdk/scrolling';
import { SupportService } from '../../services/support/support.service';
import { SettingsService } from '../../services/settings/settings.service';
import { DeviceSettingsService } from '../../services/device-settings/device-settings.service';
import { ClientSettingsService } from '../../services/client-settings/client-settings.service';
import { ConnectivityService } from '../../services/connectivity/connectivity.service';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { InventoryCacheService, CachedItem } from '../../services/inventory-cache/inventory-cache.service';
import { TransferQueueService, AppliedBatch, FailedGroup, transferLabelOf } from '../../services/transfer-queue/transfer-queue.service';
import { HeaderService } from '../../services/header/header.service';
import { AppComponent } from '../../app.component';
import { LoadingComponent } from '../shared/loading/loading.component';
import { DialogButtonStyle, DialogConfig, DialogOption } from '../shared/ui/dialog/dialog-options';
import packageJson from '../../../../package.json';



@Component({
  selector: 'app-transfer',
  templateUrl: './transfer.component.html',
  styleUrls: ['./transfer.component.scss']
})
export class TransferComponent implements OnInit {

  static readonly CACHE_MISS_NOTE = 'not in this device\'s item cache';
  static readonly CACHE_INELIGIBLE_NOTE = 'not available for this transfer at this location';

  // In-flight line adds/removes by accountId (server mode; see trackLineWrite).
  private lineWritesInFlight = new Map<string, Promise<any>>();
  // Why the last cache lookup returned nothing usable (shown with "Item not found").
  lastCacheLookupNote = '';

  supportActive: boolean = false;
  settingsActive: boolean = false;

  purchaseTypeKey$: Observable<any>;
  purchaseTypeKey;

  purchaseId$: Observable<any>;
  purchaseId;


  locationsSet: Subscription;

  // purchase location is the from location
  purchase = {
    _id: null,
    key: null,
    status: 'open',
    type: 'retail-retail',
    subtype: 'null',
    typeObject: null,
    typeName: '-',
    name: 'New Transfer',
    locationId: null,
    customerId: null,
    timeUpdated: null,
    items: [],
    searching: false,
    savingUpdates: false,
    searchTimeout: null,
    searchText: null,
    searchResults: null,
    findModal: false,
    findInventoryMode: false,
    locationName: null,
    customerName: null,
    locationOptions: null,
    locationOptionsAll: null,
    customerOptions: null,
    bulkAdd: null,
    inventoryReplenishment: false,
    note: [],
    saving: null,
    itemSaving: null,
    adding: null,
    errors: null,
    countDateTime: {
      startDate: new Date(),
      endDate: new Date()
    },
    effectiveDate: {
      startDate: new Date(),
      endDate: new Date()
    },
    exportOptions: null,
    printOptions: null,
    costTotal: null,
    qtyTotal: null,
    unitTotal: null,
    authorization: {
      view: false,
      update: false,
      complete: false,
      upload: false,
      editPrice: false,
      retry: false,
      reopen: false,
    },
    // editingCostIndex: null,
    selectedCategory: null,
    barcodeIndexes: {},
    skuIndexes: {},
    lastRetrieved: null,
    countComplete: false,
    inputTargetField: 'qty',
    inputTargetStartValue: null,
    loading: true,
    retrying: false,
    uncounted: null,
    containsEditableFields: false,
    settingsEdit: null,
    costEdit: null,
    replaceEdit: null,
    statusEdit: null,
    missingBarcode: null,
    strictInventoryCountable: null,
    editSessionKey: null,
    controls: null,
    cancelConfirm: null,
    cancelAllowed: null,
    reverseConfirm: null,
    reverseReason: null,
    reverseAllowed: null,
    reversalInProgress: null,
    cancelUserName: null,
    timeCanceled: null,
    statusName: null,
    processingTargetStep: null,
    isMobile: false,
  };

  purchaseTypes: any = [];

  purchaseTypeOptions = [];

  availableCategories = null;

  activeItem;

  // Item-detail info (step-configured infoFields) for the selected item —
  // fetched on demand from the Account and cached per accountId.
  itemInfoModal = false;
  activeItemInfo: any = null;
  activeItemInfoExtraFields: any[] = [];
  private activeItemInfoAccountId = null;
  private itemInfoCache: any = {};
  private infoFieldsCache: any = {};
  private itemViewPermissionCache: any = {};

  // Physical-keypad entry (scanners like the Honeywell EDA61K): keystrokes and
  // scans both arrive as key events, told apart by timing — scan bursts have
  // sub-threshold gaps between characters and end with Enter.
  private keypadBurstMs = 75;
  private lastKeyTime = 0;
  private scanBurst = false;
  private pendingManualKeys: string[] = [];
  private manualKeyTimeout = null;
  // A scan of a different line is resolving (server lookup / line add):
  // keypad keys typed meanwhile are held for the line the scan opens.
  private scanResolving = false;
  private pendingManualConfirm = false;
  // Desktop header refresh request in flight (spinner); see refreshUpdates.
  refreshing = false;

  descrepancyOnly = false;
  // List filters (Transfer Controls). Phones open a counting step showing only
  // the lines this user has counted, so a counter sees their own progress and
  // where they left off; desktop and other steps show everything.
  // Items filter: 'all' (counted + uncounted), 'counted' only, 'uncounted' only.
  countedFilter: 'all' | 'counted' | 'uncounted' = 'all';
  countedByMeOnly = false;

  // Back-compat view of the Items filter (true unless counted-only).
  get showUncounted(): boolean {
    return this.countedFilter !== 'counted';
  }
  filterCounts = { visible: 0, total: 0 };
  private filterDefaultsAppliedFor: string = null;

  validKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z'];
  lastKey;
  keyboardBuffer: String;
  suppressEnterKeyupUntil = 0;
  barcode: String;
  keyboardIsShifted: Boolean;

  resizeTimeout;
  lastWindowWidth = window.innerWidth;

  permissionsLoaded: Subscription;

  autoSyncTimeout;
  autoSyncDelay;
  private initialized = false;
  autoSyncDelayDefault = 5; // 5 seconds
  autoSyncDelayMultiple = 1.2; // 20% increase
  autoSyncDelayMax = 1800; // 30 minutes

  // ---- Local Inventory Cache state ----
  private cacheSubs: Subscription[] = [];
  cacheStatus: any = null;
  queueStatus: any = null;
  syncSheetOpen = false;
  staleAlertOpen = false;
  staleAlertText = '';
  failedOps: any[] = [];
  // Failed changes that belong to other transfers stored on this device
  // (yesterday's count, say): the dot counts them, so the sheet shows them
  // too — one group per transfer, named, with the reason it refused them.
  otherFailedGroups: FailedGroup[] = [];
  // Set once the server says the open transfer is gone — deleted, completed
  // or canceled elsewhere, or no such id: scanning stops, the banner says why.
  unavailable: { status: string; detail: any; at: Date } | null = null;
  private lastUnavailableNoticeAt = 0;
  // Products with a queued change not yet acknowledged: their shares stay local.
  private pendingAccountIds = new Set<string>();

  public version: string = packageJson.version;

  @ViewChild(CdkVirtualScrollViewport) viewPort: CdkVirtualScrollViewport;

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    // Height-only resizes are the Android soft keyboard opening/closing;
    // reloading then replaces the purchase object and dismisses any open modal.
    if (window.innerWidth == this.lastWindowWidth) {
      return;
    }
    this.lastWindowWidth = window.innerWidth;
    // Re-fit the item columns immediately; the debounced reload below
    // re-syncs again once the purchase re-renders.
    this.syncItemsHorizontal();
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(() => {
      // A reload replaces the purchase object, which would dismiss the open
      // Bulk Add wizard — skip it while the wizard is up.
      if (this.purchaseId && !this.purchase?.bulkAdd) {
        this.loadPurchase();
      }
    }, 1000);
  }


  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (event.target.toString() != "[object HTMLBodyElement]") {
      return;
    }

    const now = Date.now();
    const gap = now - this.lastKeyTime;
    this.lastKeyTime = now;

    // A scan burst that never received its Enter suffix goes stale quickly.
    if (this.scanBurst && gap > 500) {
      this.scanBurst = false;
    }

    const key = event.key;

    // The find modal's Enter blurs its input mid-keystroke, so the keyup lands
    // here on the body — swallow it rather than treat it as a keypad confirm.
    if (key === 'Enter' && Date.now() < this.suppressEnterKeyupUntil) {
      this.suppressEnterKeyupUntil = 0;
      return;
    }

    if (this.validKeys.indexOf(key.toLowerCase()) !== -1) {
      if (!this.keypadEditingActive() && !(this.scanResolving && this.purchase.isMobile)) {
        this.keyboardBuffer += key;
        return;
      }

      // An item is selected on a keypad device: hand-typed digits edit its
      // target field, while scanner output must stay barcode input. A digit is
      // held for one burst window; a follow-up key inside that window proves a
      // scan and diverts everything to the barcode buffer untouched.
      if (this.scanBurst) {
        this.keyboardBuffer += key;
        return;
      }

      if (gap < this.keypadBurstMs && (this.pendingManualKeys.length > 0 || this.keyboardBuffer.length > 0)) {
        this.startScanBurst();
        this.keyboardBuffer += key;
        return;
      }

      if (/^[0-9]$/.test(key)) {
        this.pendingManualKeys.push(key);
        if (this.manualKeyTimeout) {
          clearTimeout(this.manualKeyTimeout);
        }
        this.manualKeyTimeout = setTimeout(() => this.flushManualKeys(), this.keypadBurstMs + 15);
      } else {
        // Letters can't be a quantity — barcode input as before.
        this.keyboardBuffer += key;
      }
      return;
    }

    if (key == "Enter") {
      if (this.keyboardBuffer.length > 0) {
        this.barcodeEntered(this.keyboardBuffer);
        this.keyboardBuffer = "";
        this.keyboardIsShifted = false;
        this.scanBurst = false;
      } else if (this.scanResolving && this.purchase.isMobile) {
        // Confirm typed while a scan resolves: applies to the scanned line
        // once it opens (finishScanKeys), never to the line being left.
        this.pendingManualConfirm = true;
      } else if (this.keypadEditingActive()) {
        // Keypad confirm: apply any digits still in the burst-detection window,
        // then save — same as tapping Save Change.
        this.flushManualKeys();
        this.saveItem();
      }
      return;
    }

    if (key == "Backspace" && !this.scanBurst && this.keyboardBuffer.length === 0) {
      if (this.scanResolving && this.purchase.isMobile) {
        this.pendingManualKeys.push('d');
      } else if (this.keypadEditingActive()) {
        this.flushManualKeys();
        this.screenBtnPress('d');
      }
    }
  }

  // Physical-keypad editing applies on phone-width devices while an item is
  // selected (touch-only devices simply never produce key events).
  keypadEditingActive(): boolean {
    return this.purchase.isMobile && !!this.activeItem && this.purchase.containsEditableFields;
  }

  private startScanBurst() {
    this.scanBurst = true;
    if (this.manualKeyTimeout) {
      clearTimeout(this.manualKeyTimeout);
      this.manualKeyTimeout = null;
    }
    if (this.pendingManualKeys.length > 0) {
      this.keyboardBuffer += this.pendingManualKeys.join('');
      this.pendingManualKeys = [];
    }
  }

  private flushManualKeys() {
    if (this.manualKeyTimeout) {
      clearTimeout(this.manualKeyTimeout);
      this.manualKeyTimeout = null;
    }
    if (this.pendingManualKeys.length === 0) {
      return;
    }
    if (this.scanResolving) {
      // Held until the scan settles (finishScanKeys flushes or drops them).
      return;
    }
    const keys = this.pendingManualKeys;
    this.pendingManualKeys = [];
    if (!this.activeItem) {
      return;
    }
    for (const digit of keys) {
      this.screenBtnPress(digit === 'd' ? 'd' : parseInt(digit, 10));
    }
  }

  // After a scan of a different line settles: keys (and an Enter) typed while
  // it resolved go to the line the scan opened; if the scan opened nothing
  // ("Item not found", chooser dismissed) they are dropped rather than
  // applied to the line that was open before the scan. Runs one macrotask
  // later so processBarcode's own deferred setActiveItem has happened.
  private finishScanKeys(activeBefore) {
    setTimeout(() => {
      this.scanResolving = false;
      const confirm = this.pendingManualConfirm;
      this.pendingManualConfirm = false;
      const opened = !!this.activeItem && this.activeItem !== activeBefore;
      if (!opened) {
        this.pendingManualKeys = [];
        return;
      }
      this.flushManualKeys();
      if (confirm && this.activeItem) {
        this.saveItem();
      }
    }, 0);
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ioService: IoService,
    private locationService: LocationService,
    private tagPrintService: TagPrintService,
    private authorizationService: AuthorizationService,
    private reportService: ReportService,
    private headerService: HeaderService,
    private supportService: SupportService,
    private settingsService: SettingsService,
    private deviceSettingsService: DeviceSettingsService,
    private clientSettingsService: ClientSettingsService,
    public connectivityService: ConnectivityService,
    public inventoryCacheService: InventoryCacheService,
    public transferQueueService: TransferQueueService,
    private changeDetector: ChangeDetectorRef,
    private authenticationService: AuthenticationService,
  ) { }

  // The browser-print rows (print-only CSS) are rendered only between
  // beforeprint and afterprint. beforeprint fires synchronously before the
  // print snapshot, so the rows are built with a synchronous detectChanges.
  printing = false;

  @HostListener('window:pagehide')
  onPageHide() {
    this.flushDeferredSaves();
  }

  @HostListener('window:beforeprint')
  onBeforePrint() {
    this.printing = true;
    try {
      this.changeDetector.detectChanges();
    } catch (e) {
      console.log('beforeprint render error: ', e);
    }
  }

  @HostListener('window:afterprint')
  onAfterPrint() {
    this.printing = false;
  }

  ngOnInit(): void {
    // Make sure permissions are loaded
    this.permissionsLoaded = this.authorizationService.permissionsLoadedObservable().subscribe(loaded => {
      this.checkPrerequisites();
    });

    // Make sure locations are loaded
    this.locationsSet = this.locationService.locationsSetObservable().subscribe(availableLocations => {
      this.checkPrerequisites();
    });

    this.checkPrerequisites();

    // Subscribe to support active state
    this.supportService.supportActive$.subscribe((active) => {
      this.supportActive = active;
      console.log('support active state changed:', this.supportActive);
    });

    this.supportActive = this.supportService.isSupportActive();

    this.settingsService.settingsActive$.subscribe((active) => {
      this.settingsActive = active;
    });
    this.settingsActive = this.settingsService.isSettingsActive();

    // Local Inventory Cache: queued changes come back applied (merge them),
    // and a queue that has not pushed for 15 minutes raises the stale alert.
    this.cacheSubs.push(this.transferQueueService.appliedObservable().subscribe(batch => this.onQueueApplied(batch)));
    this.cacheSubs.push(this.transferQueueService.staleAlertObservable().subscribe(alert => this.showStaleAlert(alert)));
    this.cacheSubs.push(this.transferQueueService.statusObservable().subscribe(status => {
      const failedBefore = this.queueStatus ? this.queueStatus.failed : null;
      this.queueStatus = status;
      if (status.pending === 0 && this.staleAlertOpen) {
        this.staleAlertOpen = false;
      }
      if (failedBefore !== status.failed) {
        this.loadFailedOps();
      }
    }));
    this.cacheSubs.push(this.inventoryCacheService.statusObservable().subscribe(status => {
      this.cacheStatus = status;
      this.onCacheModeChange();
    }));
  }

  // The device cache became usable while this transfer was open (the catalog
  // finished loading). Lines merged through the server path meanwhile carry
  // the server's qty but no per-device shares, and the first cache-mode save
  // of one of them would send this device's share as the whole line total
  // ("50 + 1" became "50 + 51" when another scanner's 50 arrived during the
  // load). Refresh the lines from the server: resyncItems derives every
  // line's shares and keeps the open line's unsaved edit.
  private cacheModeWas: boolean = null;
  private cacheReadyResync: Promise<void> = null;
  private onCacheModeChange() {
    const now = this.cacheMode();
    const was = this.cacheModeWas;
    this.cacheModeWas = now;
    if (!now || was !== false || this.cacheReadyResync) {
      return;
    }
    if (!this.purchase || !this.purchase._id || this.purchase.loading || !Array.isArray(this.purchase.items) || this.purchase.items.length === 0 || this.isLocked()) {
      return;   // not open yet (loadPurchase derives shares itself) or not editable
    }
    console.log('device cache ready with the transfer open: refreshing lines for per-device shares');
    this.cacheReadyResync = this.resyncItems().catch(e => console.log('cache-ready resync failed: ', e)).then(() => { this.cacheReadyResync = null; });
  }

  checkPrerequisites() {
    if (
      this.authorizationService.permissionsAreLoaded() &&
      this.locationService.getLocations() != null
    ) {
      this.init();
    }
  }

  // Offline scanning guard: scans while disconnected are rejected loudly
  // (counted on the overlay) instead of failing silently; reconnecting
  // reloads the open transfer so the device shows server truth.
  blockedScanCount = 0;
  private connectivitySub: any = null;
  private lastOnlineState = true;

  private watchConnectivity() {
    if (this.connectivitySub) { return; }
    this.connectivitySub = this.connectivityService.onlineObservable().subscribe((online) => {
      const was = this.lastOnlineState;
      this.lastOnlineState = online;
      if (!online && was) {
        // Fresh disconnect — the overlay counts blocked scans from zero.
        this.blockedScanCount = 0;
      }
      if (online && !was && this.purchase && this.purchase._id) {
        this.blockedScanCount = 0;
        if (this.cacheMode()) {
          // Reconnected with the cache on: the queue drains and the applied
          // lines merge in place; the poll catches everything else.
          this.transferQueueService.scheduleReplay(0);
          this.autoSyncDelay = this.autoSyncDelayDefault;
          this.loadPurchaseUpdates(null);
        } else {
          // Reconnected — show server truth (anything scanned offline was NOT
          // recorded; the overlay already said so).
          this.loadPurchase();
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.removalFlushTimer) {
      clearTimeout(this.removalFlushTimer);
      this.removalFlushTimer = null;
      this.flushLineRemovals();
    }
    // Leaving the screen: push any count still waiting to be saved.
    this.flushDeferredSaves();
    this.headerService.clearBackLink();
    for (const sub of this.cacheSubs) {
      sub.unsubscribe();
    }
    this.cacheSubs = [];
    if (this.connectivitySub) {
      this.connectivitySub.unsubscribe();
      this.connectivitySub = null;
    }
    if (this.permissionsLoaded) {
      this.permissionsLoaded.unsubscribe();
    }
      
    if (this.locationsSet) {
      this.locationsSet.unsubscribe();
    }

    if (this.autoSyncTimeout) {
      clearTimeout(this.autoSyncTimeout);
    }

    if (this.manualKeyTimeout) {
      clearTimeout(this.manualKeyTimeout);
    }
  }

  init = async () => {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    try {
      this.purchaseTypes = await this.ioService.post('/transfer/getPurchaseTypes');
      if (this.inventoryCacheService.isEnabled()) {
        this.transferQueueService.saveMeta('purchaseTypes', this.purchaseTypes);
      }
    } catch (e) {
      // Offline with the cache on: the last known types open the cached transfer.
      const cached = this.inventoryCacheService.isEnabled() ? await this.transferQueueService.loadMeta('purchaseTypes') : null;
      if (!cached) {
        throw e;
      }
      this.purchaseTypes = cached;
    }
    this.purchaseId$ = this.route.paramMap.pipe(switchMap(params => of(params.get('purchaseId'))));
    this.purchaseId$.subscribe(purchaseId => {
      console.log('purchaseId', purchaseId);
      if (purchaseId) {
        this.purchaseId = purchaseId;
        this.loadPurchase();
      } else {
        this.setPurchaseTypeOptions();

        // If this is not an existing purchase, set the type to the first option
        if (!purchaseId) {
          this.purchase.type = this.purchaseTypeOptions[0].key;
        }

        this.purchaseTypeChange(this.purchase.type);
        this.purchase.loading = false;
      }
    });

    this.purchaseTypeKey$ = this.route.paramMap.pipe(switchMap(params => of(params.get('purchaseTypeKey'))));
    this.purchaseTypeKey$.subscribe(purchaseTypeKey => {
      if (purchaseTypeKey) {
        console.log('purchaseTypeKey', purchaseTypeKey);

        // Check if this is actually a purchaseId (not a known purchase type key)
        const isKnownType = this.purchaseTypes.some(pt => pt.key === purchaseTypeKey);
        if (!isKnownType && !this.purchaseId) {
          this.purchaseId = purchaseTypeKey;
          this.loadPurchase();
          return;
        }

        this.purchaseTypeKey = purchaseTypeKey;
        if (!this.purchaseId) {
          this.purchase.type = this.purchaseTypeKey;
          this.purchaseTypeChange(this.purchase.type);
          this.purchase.loading = false;
        }
      }
    });

  }

  // PurchaseType.customFields: transfer-level text fields (e.g. the vendor's
  // Invoice Number on Vendor to Store) stored on the purchase under their key.
  customFields(): any[] {
    const fields = this.purchase && this.purchase.typeObject ? this.purchase.typeObject.customFields : null;
    return Array.isArray(fields) ? fields.filter(field => field && field.key) : [];
  }

  // Required custom fields still blank on the given value holder (the purchase
  // on create, the settingsEdit copy in the settings dialog). The server
  // enforces the same rule on create and on any update that blanks one.
  missingRequiredCustomFields(holder: any): any[] {
    return this.customFields().filter(field => field.required === true && this.isBlank(holder ? holder[field.key] : null));
  }

  snackMissingCustomFields(missing: any[]): void {
    AppComponent.showSnack(missing.map(field => field.name || field.key).join(', ') + (missing.length === 1 ? ' is' : ' are') + ' required.', 'OK', 5000);
  }

  newPurchase = async () => {
    console.log('newPurchase: ');

    const blockers = this.buildBlockers();
    if (blockers.length > 0) {
      AppComponent.showSnack(blockers.join(', ') + (blockers.length === 1 ? ' is' : ' are') + ' required.', 'OK', 5000);
      return;
    }

    // On a Make Order, and on any target-only type (adjustments, physical
    // counts), the location and the customer are the same — never send a stale
    // source id left over from a previous screen.
    if (this.purchase.type == 'make-order' || !this.purchase.typeObject?.sourceType) {
      this.purchase.locationId = this.purchase.customerId;
      this.purchase.locationName = this.purchase.customerName;
    }

    let purchaseUpdateResponse: any = await this.ioService.post('/transfer/updateTransfer', {
      purchase: this.purchase
    });

    this.router.navigate(['transfers', purchaseUpdateResponse.purchaseId]);
  }

  setPurchaseTypeOptions = async () => {
    this.purchaseTypeOptions = [];

    for (let purchaseType of this.purchaseTypes) {
      // Unlisted types (e.g. adjustment flows created exclusively from their
      // reports) never appear in the type dropdown — deep links still work.
      if (purchaseType.unlisted === true) {
        continue;
      }
      // Check for edit permission (stepped types gate creation on the FIRST step)
      const createAction = this.createActionKey(purchaseType);
      if (this.authorizationService.checkPermission('purchase.'+purchaseType.key+'.'+createAction, 'any') || this.authorizationService.checkPermission('purchase.'+purchaseType.key+'.'+createAction, 'general')) {
        this.purchaseTypeOptions.push(purchaseType);
      }
    }

    return;
  }

  // Item identifier shown in item cells / print / search results: the SKU by
  // default, or the Item # (first barcode) when the client runs
  // settings.itemNumberPrimary (per client feedback: "Item # everywhere").
  itemNumberPrimary(): boolean {
    return this.clientSettingsService.isItemNumberPrimary();
  }

  itemIdentifier(item: any): any {
    if (this.itemNumberPrimary()) {
      // Vendor products: the server resolves the linked retail item's Item #
      // as itemNumber (search rows and transfer lines); the vendor's own
      // barcode is the UPC, which is only the fallback.
      if (item.itemNumber) {
        return item.itemNumber;
      }
      return Array.isArray(item.barcode)
        ? (item.barcode.length > 0 ? item.barcode[0] : null)
        : item.barcode;
    }
    return item.sku;
  }

  identifierLabel(): string {
    return this.itemNumberPrimary() ? 'Item #' : 'SKU';
  }

  // Search-result cell values pass through here so the sku column can honor
  // the Item #-primary setting without touching per-type searchResultFields.
  searchResultValue(searchResult: any, path: string): any {
    if (path === 'sku' && this.itemNumberPrimary()) {
      return this.itemIdentifier(searchResult);
    }
    return searchResult[path];
  }

  // Item-field columns for a given status. Stepped types read the current step's
  // fields (normalized so name/type work like the legacy label/pipe). Legacy
  // PurchaseType.fields only defines the interactive statuses (open/pending/…);
  // transient or terminal statuses like "processing" have no entry, so fall back
  // to the pending layout and never return a non-iterable. Results are memoized —
  // templates iterate this every change-detection pass and need stable identity.
  private fieldsCache: any = {};

  getStatusFields(typeObject: any, status: string): any[] {
    const cacheKey = (typeObject ? typeObject.key : '') + ':' + status;
    if (this.fieldsCache[cacheKey]) {
      return this.fieldsCache[cacheKey];
    }

    let rawFields;
    if (this.hasSteps(typeObject)) {
      let stepIndex = typeObject.steps.findIndex(step => step.key === status);
      if (stepIndex === -1 && status === 'processing' && this.purchase && this.purchase.processingTargetStep) {
        stepIndex = typeObject.steps.findIndex(step => step.key === this.purchase.processingTargetStep);
      }
      rawFields = stepIndex !== -1 ? (typeObject.steps[stepIndex].fields || []) : [];
    } else {
      const fields = (typeObject && typeObject.fields) || {};
      rawFields = fields[status] || fields['pending'] || fields['open'] || [];
    }

    this.fieldsCache[cacheKey] = rawFields.map(field =>
      (field.label !== undefined && field.pipe !== undefined)
        ? field
        : { ...field, label: field.label !== undefined ? field.label : field.name, pipe: field.pipe !== undefined ? field.pipe : field.type });

    return this.fieldsCache[cacheKey];
  }

  // ─── Unit columns (vendor unit ⇄ retail unit) ───────────────────
  // A step field with `unitOf: '<baseKey>'` is a retail-unit view of a
  // vendor-unit field: value = base × line caseQty (retail units per vendor
  // unit, stamped on vendor product lines). Editing it converts back into the
  // base field, which stays the stored quantity — completion, costing and
  // reports keep working in vendor units. Under PurchaseType.wholeVendorUnits
  // an entry that does not make whole vendor units rounds up (away from
  // zero) when the edit is committed, and the line notes the rounding.
  unitFields(typeObject: any = this.purchase.typeObject, status: string = this.purchase.status): any[] {
    return this.getStatusFields(typeObject, status).filter(field => !!field.unitOf);
  }

  unitFieldConfig(key: string): any {
    if (!key || !this.purchase || !this.purchase.typeObject) {
      return null;
    }
    return this.unitFields().find(field => field.key === key) || null;
  }

  isUnitField(key: string): boolean {
    return !!this.unitFieldConfig(key);
  }

  lineCaseQty(item: any): number {
    const caseQty = Number(item && item.caseQty);
    return caseQty > 0 ? caseQty : 1;
  }

  // Convert a unit column's entered value into its base field. `commit` applies
  // the whole-vendor-unit rounding (only when the edit is being saved, so
  // typing is never rewritten mid-keystroke).
  applyUnitEntry(item: any, key: string, commit = false) {
    const field = this.unitFieldConfig(key);
    if (!field || !item) {
      return;
    }
    const caseQty = this.lineCaseQty(item);
    const units = Number(item[key]);
    if (isNaN(units)) {
      return;
    }
    let cases = units / caseQty;
    if (commit) {
      // The rounding note stays while the (already rounded) value is merely
      // re-committed; any different entry replaces or clears it.
      const keepNote = item.unitRounding && Math.abs(units - item.unitRounding.units) < 0.01;
      if (!keepNote) {
        delete item.unitRounding;
      }
      if (this.purchase.typeObject && this.purchase.typeObject.wholeVendorUnits === true && caseQty > 1) {
        const whole = Math.sign(cases) * Math.ceil(Math.abs(cases) - 1e-9);
        if (whole !== cases) {
          cases = whole;
          item.unitRounding = { entered: units, units: this.numberRound(whole * caseQty, 4) };
        }
      }
    }
    item[field.unitOf] = this.numberRound(cases, 4);
  }

  // Whether this device should render the on-screen numberpad when an item is
  // selected at phone width. Client Configuration provides the default
  // (settings.showNumberpad); a device-level override from the Settings panel
  // wins — scanners with built-in keypads (e.g. Honeywell EDA61K) turn it off
  // and get the item-info panel in its place.
  showNumberpad(): boolean {
    return this.deviceSettingsService.showNumberpad(this.clientSettingsService.isShowNumberpadDefault());
  }

  // Item-detail fields for the info panel/modal: the current step's infoFields,
  // falling back to a type-level infoFields list (which also serves legacy
  // types). Normalized and memoized like getStatusFields.
  getInfoFields(typeObject: any = this.purchase.typeObject, status: string = this.purchase.status): any[] {
    if (!typeObject) {
      return [];
    }
    const cacheKey = (typeObject.key || '') + ':' + status;
    if (this.infoFieldsCache[cacheKey]) {
      return this.infoFieldsCache[cacheKey];
    }

    let rawFields = null;
    if (this.hasSteps(typeObject)) {
      let stepIndex = typeObject.steps.findIndex(step => step.key === status);
      if (stepIndex === -1 && status === 'processing' && this.purchase && this.purchase.processingTargetStep) {
        stepIndex = typeObject.steps.findIndex(step => step.key === this.purchase.processingTargetStep);
      }
      if (stepIndex !== -1) {
        rawFields = typeObject.steps[stepIndex].infoFields;
      }
    }
    if (!rawFields) {
      rawFields = typeObject.infoFields || [];
    }

    this.infoFieldsCache[cacheKey] = rawFields.map(field =>
      (field.label !== undefined && field.pipe !== undefined)
        ? field
        : { ...field, label: field.label !== undefined ? field.label : field.name, pipe: field.pipe !== undefined ? field.pipe : field.type });

    return this.infoFieldsCache[cacheKey];
  }

  // Desktop-only: the SKU/Item cell links to the View named by the
  // PurchaseType (itemViewKey) when the user holds view.<key>.view anywhere.
  // Permission verdicts are cached per view key, but only once the
  // permission map has actually loaded — a false from an unloaded map must
  // not stick.
  canOpenItemView(item: any): boolean {
    const viewKey = this.purchase?.typeObject?.itemViewKey;
    if (!viewKey || this.purchase.isMobile || !item?.accountId) {
      return false;
    }
    if (this.itemViewPermissionCache[viewKey] === undefined) {
      if (!this.authorizationService.permissionsAreLoaded()) {
        return false;
      }
      this.itemViewPermissionCache[viewKey] =
        this.authorizationService.checkPermission('view.' + viewKey + '.view', 'any', false);
    }
    return this.itemViewPermissionCache[viewKey];
  }

  openItemView(item: any, event: Event): void {
    if (!this.canOpenItemView(item)) {
      return;
    }
    event.stopPropagation();
    this.router.navigate(
      ['details', this.purchase.typeObject.itemViewKey, item.accountId],
      { relativeTo: this.route, queryParamsHandling: 'preserve' },
    );
  }

  // Purchase items only carry transactional fields, so descriptive Account
  // fields are fetched on demand and cached for the life of the loaded purchase.
  loadItemInfo(item) {
    if (!this.itemInfoCache[item.accountId]) {
      this.itemInfoCache[item.accountId] = this.ioService.post('/transfer/getTransferItemInfo', {
        purchaseId: this.purchase._id,
        accountId: item.accountId,
      }).then((response: any) => ({
        info: (response && response.info) || {},
        // Fields the server adds beyond the step config (e.g. a vendor
        // product line's "Retail Units per Vendor Unit").
        extraFields: (response && Array.isArray(response.extraFields)) ? response.extraFields : [],
      })).catch(() => ({ info: {}, extraFields: [] }));
    }
    return this.itemInfoCache[item.accountId];
  }

  // Item-details rows: server-added fields first (deduped by key), then the
  // step's configured infoFields.
  displayInfoFields(): any[] {
    const configured = this.getInfoFields();
    const extra = (this.activeItemInfoExtraFields || [])
      .filter(field => field && field.key && !configured.some(f => f.key === field.key))
      .map(field => ({ ...field, label: field.name, pipe: field.type }));
    return extra.length ? extra.concat(configured) : configured;
  }

  refreshActiveItemInfo() {
    const item = this.activeItem;
    if (!item || this.getInfoFields().length === 0) {
      this.activeItemInfo = null;
      this.activeItemInfoExtraFields = [];
      this.activeItemInfoAccountId = null;
      return;
    }
    if (this.activeItemInfoAccountId === item.accountId && this.activeItemInfo) {
      return;
    }
    this.activeItemInfoAccountId = item.accountId;
    this.activeItemInfo = null;
    this.activeItemInfoExtraFields = [];
    this.loadItemInfo(item).then(result => {
      if (this.activeItem && this.activeItem.accountId === item.accountId) {
        this.activeItemInfo = result.info;
        this.activeItemInfoExtraFields = result.extraFields;
      }
    });
  }

  // Display value for an item-details row; array values (e.g. a retail
  // item's vendor list) read as a comma-separated list.
  itemInfoValue(field): any {
    const value = this.activeItemInfo ? this.activeItemInfo[field.key] : undefined;
    return Array.isArray(value) ? value.join(', ') : value;
  }

  hasItemInfoValue(field): boolean {
    return this.activeItemInfo
      && this.activeItemInfo[field.key] !== undefined
      && this.activeItemInfo[field.key] !== null
      && this.activeItemInfo[field.key] !== '';
  }

  openItemInfo() {
    this.itemInfoModal = true;
    this.refreshActiveItemInfo();
  }

  closeItemInfo() {
    this.itemInfoModal = false;
  }

  // ==== Dynamic Transfer Steps ====================================================
  // A stepped PurchaseType defines steps: [{ key, name, color, authContext, fields,
  // allowAddRemove, allowRegress, requireCount, requireReconcile, confirmProceed,
  // proceedLabel, inventory }]. purchase.status is the current step key. Legacy
  // types keep every historical gate untouched — these helpers return false/null
  // for them and the new UI elements simply don't render.

  hasSteps(typeObject: any = this.purchase.typeObject): boolean {
    return !!typeObject && Array.isArray(typeObject.steps) && typeObject.steps.length > 0;
  }

  currentStepInfo(): any {
    const typeObject = this.purchase.typeObject;
    if (!this.hasSteps(typeObject)) {
      return null;
    }
    const index = typeObject.steps.findIndex(step => step.key === this.purchase.status);
    if (index === -1) {
      return null; // processing / deleted / canceled
    }
    return { step: typeObject.steps[index], index, steps: typeObject.steps };
  }

  nextStep(): any {
    const current = this.currentStepInfo();
    return current && current.index < current.steps.length - 1 ? current.steps[current.index + 1] : null;
  }

  prevStep(): any {
    const current = this.currentStepInfo();
    return current && current.index > 0 ? current.steps[current.index - 1] : null;
  }

  isTerminalStep(): boolean {
    const current = this.currentStepInfo();
    return !!current && current.index === current.steps.length - 1;
  }

  isFirstStep(): boolean {
    if (!this.hasSteps()) {
      return this.purchase.status == 'open';
    }
    const current = this.currentStepInfo();
    return !!current && current.index === 0;
  }

  // Line items may be added/removed in this state (drives search-to-add vs
  // search-within-transfer, scan-add vs scan-find, and the remove controls).
  canAddRemove(): boolean {
    if (!this.hasSteps()) {
      return this.purchase.status == 'open';
    }
    const current = this.currentStepInfo();
    return !!current && current.step.allowAddRemove === true;
  }

  // Steps that accept UNEXPECTED items: physically present but never on the
  // transfer (extra case on the truck). They land with Expected 0 and ride
  // the discrepancy machinery. Only meaningful when normal adds are off.
  canUnexpectedAdd(): boolean {
    if (!this.hasSteps() || this.canAddRemove()) {
      return false;
    }
    const current = this.currentStepInfo();
    return !!current && current.step.allowUnexpectedAdd === true;
  }

  // Read-only end state: terminal step reached (or legacy complete).
  isLocked(): boolean {
    // Reversed (or mid-reversal): finished for good, whatever the type.
    if (this.purchase.status === 'reversed' || this.purchase.status === 'reversing') {
      return true;
    }
    if (!this.hasSteps()) {
      return this.purchase.status == 'complete';
    }
    return this.purchase.status == 'complete' || this.isTerminalStep();
  }

  // The purchase is in an editable working state for the current user.
  canEdit(): boolean {
    if (!this.hasSteps()) {
      return (this.purchase.status == 'open' || this.purchase.status == 'pending') && this.purchase.authorization.update;
    }
    return !!this.currentStepInfo() && !this.isTerminalStep() && this.purchase.authorization.update;
  }

  stepPermissionContext(purchase: any, step: any): any {
    const authContext = step.authContext || purchase.typeObject.authContext;
    if (!authContext || authContext === 'general') {
      return 'general';
    }
    return purchase[authContext];
  }

  checkStepPermission(purchase: any, step: any, actionOverride: string = null): boolean {
    const action = actionOverride || step.permissionAction || step.key;
    return this.authorizationService.checkPermission(
      'purchase.' + purchase.type + '.' + action,
      this.stepPermissionContext(purchase, step));
  }

  canProceed(): boolean {
    const current = this.currentStepInfo();
    if (!current || !this.nextStep()) {
      return false;
    }
    return this.checkStepPermission(this.purchase, current.step, current.step.proceedPermissionAction);
  }

  // Mirror of the legacy mobile gate on Complete (requirePendingCount): on a
  // phone, a requireCount step hides Proceed until every line is counted, so
  // the worker sees the state instead of a server rejection after the fact.
  proceedBlockedByCount(): boolean {
    const current = this.currentStepInfo();
    return !!current
      && current.step.requireCount === true
      && this.purchase.isMobile
      && this.purchase.uncounted > 0;
  }

  canRegress(): boolean {
    const current = this.currentStepInfo();
    if (!current || !this.prevStep() || current.step.allowRegress === false) {
      return false;
    }
    return this.checkStepPermission(this.purchase, current.step);
  }

  proceedLabel(): string {
    const current = this.currentStepInfo();
    const next = this.nextStep();
    if (!next) {
      return null;
    }
    return current.step.proceedLabel || next.name;
  }

  regressLabel(): string {
    const current = this.currentStepInfo();
    const prev = this.prevStep();
    if (!prev) {
      return null;
    }
    return current.step.regressLabel || ('Back to ' + prev.name);
  }

  // Text-typed step fields (a required Note, etc.): edited as free text in
  // the row / phone panel, never the numeric input target.
  isTextField(field: any): boolean {
    return !!field && (field.type === 'string' || field.pipe === 'string');
  }

  // Text step fields shown in the phone's note editor: an editable one always,
  // a read-only one only when it is meant for the phone (displayMobile).
  textFields(): any[] {
    return this.getStatusFields(this.purchase.typeObject, this.purchase.status)
      .filter(field => this.isTextField(field) && (field.editable || field.displayMobile));
  }

  textFieldEditable(field: any): boolean {
    return !!field && field.editable === true && !this.isLocked() && this.canEdit();
  }

  textFieldsEditable(): boolean {
    return !!this.activeItem && this.textFields().some(field => this.textFieldEditable(field));
  }

  isBlank(value: any): boolean {
    return value === undefined || value === null || String(value).trim() === '';
  }

  // Step fields flagged `required` must be filled on every line before the
  // step can proceed (the server enforces the same rule). Returns one entry
  // per field with the number of lines still missing it.
  missingRequiredFields(): { field: any, count: number }[] {
    const current = this.currentStepInfo();
    if (!current || !Array.isArray(current.step.fields)) {
      return [];
    }
    const missing = [];
    for (const field of current.step.fields) {
      if (!field || field.required !== true || !field.key) {
        continue;
      }
      const count = (this.purchase.items || []).filter(item => this.isBlank(item[field.key])).length;
      if (count > 0) {
        missing.push({ field, count });
      }
    }
    return missing;
  }

  // Lines whose product was deleted after they were added (flagged by the
  // server on load). Completing would fail inside the movement run, so the
  // modal names them up front with the fix: remove the line or replace it.
  deletedLines(): any[] {
    return (this.purchase && this.purchase.items || []).filter(item => item && item.accountDeleted);
  }

  showDeletedLinesError(detail?: any[]) {
    const lines = Array.isArray(detail) && detail.length > 0
      ? detail.map(entry => ({ index: entry.index, name: entry.name, sku: entry.sku }))
      : this.deletedLines().map(item => ({ index: item.index, name: item.name, sku: item.sku }));
    this.purchase.errors = [
      { msg: (lines.length === 1 ? '1 line refers' : lines.length + ' lines refer') + ' to a product that was deleted after it was added. '
          + 'Remove the line, or use Replace where a replacement product is offered, then try again.' },
      ...lines.map(line => ({ msg: 'Line ' + (line.index + 1) + ': ' + line.name + (line.sku ? ' (' + line.sku + ')' : '') })),
    ];
  }

  // Replace a deleted line with the product the server suggested: the old line
  // comes off and the replacement goes on with a re-stated vendor quantity
  // (pack sizes may differ), through the normal remove/add paths.
  startReplaceItem(item) {
    if (!item || !item.replacement || !this.canAddRemove()) {
      return;
    }
    const oldCase = Number(item.caseQty) > 0 ? Number(item.caseQty) : 1;
    const newCase = Number(item.replacement.caseQty) > 0 ? Number(item.replacement.caseQty) : 1;
    const suggested = Math.max(1, Math.round(((Number(item.qty) || 0) * oldCase) / newCase));
    this.purchase.replaceEdit = { item, replacement: item.replacement, qty: suggested, oldCase, newCase };
  }

  cancelReplaceItem() {
    this.purchase.replaceEdit = null;
  }

  async confirmReplaceItem() {
    const edit = this.purchase.replaceEdit;
    if (!edit) {
      return;
    }
    const qty = Number(edit.qty);
    if (!(qty > 0)) {
      AppComponent.showSnack('Enter the quantity to order of the replacement product.', 'OK', 4000);
      return;
    }
    this.purchase.replaceEdit = null;
    try {
      await this.removePurchaseItem(edit.item.index);
      const replacement = edit.replacement;
      await this.addPurchaseItem({
        _id: replacement.accountId,
        name: replacement.name,
        sku: replacement.sku,
        barcode: replacement.barcode,
        price: replacement.price,
        unitDescription: replacement.unitDescription,
        subtype: 'vendor-product',
        caseQty: replacement.caseQty,
        qty,
      }, true, false, qty, 'tap');
      AppComponent.showSnack('Replaced with ' + replacement.name + ' × ' + qty + '.', 'OK', 4000);
      await this.loadPurchase();
    } catch (e) {
      AppComponent.showSnack((e && e.error && e.error.msg) || 'Could not replace the line.', 'OK', 5000);
      await this.loadPurchase();
    }
  }

  proceedStep() {
    const current = this.currentStepInfo();
    const next = this.nextStep();
    if (!current || !next) {
      return;
    }
    if (this.deletedLines().length > 0) {
      this.showDeletedLinesError();
      return;
    }
    const missing = this.missingRequiredFields();
    if (missing.length > 0) {
      AppComponent.showSnack(missing.map(entry => entry.count + ' item' + (entry.count === 1 ? '' : 's') + ' missing ' + (entry.field.name || entry.field.label || entry.field.key)).join('; ') + ' — fill in every line before proceeding.', 'OK', 6000);
      return;
    }
    if (current.step.confirmProceed) {
      this.purchase.statusEdit = {
        status: next.key,
        msg: current.step.confirmProceed,
      };
    } else {
      this.updateTransferStatus(next.key);
    }
  }

  regressStep() {
    const prev = this.prevStep();
    if (!prev || !this.canRegress()) {
      return;
    }
    // Backward counterpart of confirmProceed: a step whose regress rules throw
    // work away (Picking overwrites picked quantities, Receiving overwrites
    // received counts) can require a confirmation before going back.
    const current = this.currentStepInfo();
    if (current?.step?.confirmRegress) {
      this.purchase.statusEdit = {
        status: prev.key,
        msg: current.step.confirmRegress,
      };
      return;
    }
    this.updateTransferStatus(prev.key);
  }

  currentStepColor(): string {
    const current = this.currentStepInfo();
    if (current && current.step.color) {
      return this.subtleStepBackground(current.step.color);
    }
    if (this.hasSteps() && this.purchase.status == 'processing') {
      return this.subtleStepBackground('#555555');
    }
    return null;
  }

  // Render the configured step color as a translucent tint rather than a solid
  // fill so the theme's text colors stay readable on the header.
  subtleStepBackground(color: string): string {
    const hex = String(color).match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (!hex) {
      return color;
    }
    let value = hex[1];
    if (value.length === 3) {
      value = value.split('').map(character => character + character).join('');
    }
    const red = parseInt(value.substring(0, 2), 16);
    const green = parseInt(value.substring(2, 4), 16);
    const blue = parseInt(value.substring(4, 6), 16);
    return 'rgba(' + red + ', ' + green + ', ' + blue + ', 0.22)';
  }

  // Permission action that gates creating a transfer of this type: the first
  // step's key for stepped types, the historical update action otherwise.
  // A PurchaseType may declare an explicit create gate (createAuthContext /
  // createPermissionAction) separating "who initiates" from "who works the
  // first step" — e.g. warehouse-retail: stores order (customerId side),
  // warehouse staff build. Falls back to the first step's gate.
  createActionKey(typeObject: any): string {
    if (this.hasSteps(typeObject)) {
      return typeObject.createPermissionAction
        || typeObject.steps[0].permissionAction || typeObject.steps[0].key;
    }
    return 'update';
  }

  createAuthContext(typeObject: any): string {
    if (this.hasSteps(typeObject)) {
      return typeObject.createAuthContext
        || typeObject.steps[0].authContext || typeObject.authContext;
    }
    return typeObject.authContext;
  }

  showTransferName(): boolean {
    return this.clientSettingsService.isTransferNamesEnabled();
  }

  loadPurchase = async (purchaseResponse: any = null) => {
    console.log('loadPurchase: ', this.purchaseId);
    await this.flushDeferredSaves();
    this.pollStopped = false;
    this.unavailable = null;

    if (this.autoSyncTimeout) {
      clearTimeout(this.autoSyncTimeout);
    }

    this.autoSyncDelay = this.autoSyncDelayDefault;

    this.activeItem = null;

    let offlineCopy = false;
    let unavailableError: { status: string; detail: any } | null = null;
    if (purchaseResponse == null) {
      try {
        purchaseResponse = await this.ioService.post('/transfer/getTransfer', {
          purchaseId: this.purchaseId
        });
      } catch (e) {
        if (this.isUnavailableError(e)) {
          // The server answered: the transfer is gone (deleted, completed or
          // canceled elsewhere, or no such id). Show what this device last saw
          // of it, read-only with the banner — not as a live copy, which would
          // let scanning carry on into a transfer that cannot take it.
          unavailableError = this.unavailableFromError(e);
          const seen = await this.transferQueueService.loadPurchase(this.purchaseId)
            || await this.transferQueueService.loadPurchaseHead(this.purchaseId);
          if (!seen) {
            AppComponent.showSnack(this.unavailableText(unavailableError.status, unavailableError.detail), 'OK', 6000);
            throw e;
          }
          purchaseResponse = seen;
          if (!Array.isArray(purchaseResponse.items)) {
            purchaseResponse.items = [];
          }
        } else {
          // Offline with the cache on: open the copy saved on the last load so
          // scanning and counting continue against it; the queue carries the
          // changes and the next load replaces the copy.
          const cached = this.inventoryCacheService.isEnabled() ? await this.transferQueueService.loadPurchase(this.purchaseId) : null;
          if (!cached) {
            throw e;
          }
          purchaseResponse = cached;
          offlineCopy = true;
        }
      }
    }
    if (!offlineCopy && this.inventoryCacheService.isEnabled()) {
      this.transferQueueService.savePurchase(purchaseResponse);
    }
    purchaseResponse.offlineCopy = offlineCopy;

    this.availableCategories = null;
    this.fieldsCache = {};
    this.infoFieldsCache = {};
    this.itemInfoCache = {};
    this.itemInfoModal = false;
    this.activeItemInfo = null;
    this.activeItemInfoAccountId = null;
    purchaseResponse.searchText = null;
    purchaseResponse.searchResults = null;
    purchaseResponse.activeCategoryId = null;

    for (let typeOb of this.purchaseTypes) {
      if (typeOb.key == purchaseResponse.type) {

        let containsEditableFields = false;
        for (let field of this.getStatusFields(typeOb, purchaseResponse.status)) {
          if (field.editable) {
            containsEditableFields = true;
          }
        }

        purchaseResponse.containsEditableFields = containsEditableFields;

        purchaseResponse.typeObject = typeOb;
      }
    }

    if (purchaseResponse.effectiveDate) {
      console.log('existing effectiveDate found: ', purchaseResponse.effectiveDate);
      purchaseResponse.effectiveDate = {
        startDate: new Date(purchaseResponse.effectiveDate),
        endDate: new Date(purchaseResponse.effectiveDate)
      };
    }

    // purchaseResponse.canExport = this.authorizationService.checkPermission('purchase.export', purchaseResponse.customerId);

    if (this.hasSteps(purchaseResponse.typeObject)) {
      // Stepped types: view passes at either involved location (or generally);
      // "update" maps to the CURRENT step's permission so every existing
      // authorization.update gate means "may work the step this transfer is in".
      const stepIndex = purchaseResponse.typeObject.steps.findIndex(step => step.key === purchaseResponse.status);
      const currentStep = stepIndex !== -1 ? purchaseResponse.typeObject.steps[stepIndex] : null;
      purchaseResponse.authorization = {
        view: this.authorizationService.checkPermission('purchase.'+purchaseResponse.type+'.view', purchaseResponse.locationId)
          || this.authorizationService.checkPermission('purchase.'+purchaseResponse.type+'.view', purchaseResponse.customerId)
          || this.authorizationService.checkPermission('purchase.'+purchaseResponse.type+'.view', 'general'),
        update: currentStep ? this.checkStepPermission(purchaseResponse, currentStep) : false,
        complete: false,
        reopen: false,
      };
    } else if (purchaseResponse.typeObject.authContext === 'general') {
      purchaseResponse.authorization = {
        view: this.authorizationService.checkPermission('purchase.'+purchaseResponse.type+'.view', 'general'),
        update: this.authorizationService.checkPermission('purchase.'+purchaseResponse.type+'.update', 'general'),
        complete: this.authorizationService.checkPermission('purchase.'+purchaseResponse.type+'.complete', 'general'),
        reopen: this.authorizationService.checkPermission('purchase.'+purchaseResponse.type+'.reopen', 'general'),
      };
    } else {

      purchaseResponse.authorization = {};
      for (let authorizationKey of [
        'view',
        'update',
        'complete',
      ]) {
        purchaseResponse.authorization[authorizationKey] = this.authorizationService.checkPermission('purchase.'+purchaseResponse.type+'.'+authorizationKey, purchaseResponse[purchaseResponse.typeObject.authContext]);
      }

    }

    // Resume a transfer stuck in "processing" — gated by the dedicated
    // purchase.<type>.retry permission, scoped to the type's auth context like the
    // other per-type actions (Developers still pass via checkPermission's override).
    const retryContext = purchaseResponse.typeObject.authContext === 'general'
      ? 'general'
      : purchaseResponse[purchaseResponse.typeObject.authContext];
    purchaseResponse.authorization.retry =
      this.authorizationService.checkPermission('purchase.' + purchaseResponse.type + '.retry', retryContext);
    purchaseResponse.authorization.upload = this.authorizationService.checkPermission('Upload', 'general');
console.log('AUTHORIZATION: ', purchaseResponse.authorization);
    // Vendor-sourced transfers (POs): a user who may edit Vendor Items may change a
    // line's cost, which reprices the vendor product. Whether the transfer is in
    // an editable state is checked at click time (canEditItemCost) — the old
    // gate on authorization.complete + status open/pending never passed on
    // stepped types, where "complete" is always false and statuses are step keys.
    // Applies to both directions of a vendor transfer: receiving (sourceType
    // vendor) and Return to Vendor (targetType vendor) carry vendor-product lines.
    if (this.isVendorLinkedType(purchaseResponse.typeObject)) {
      purchaseResponse.authorization.editPrice = this.authorizationService.checkPermission('Vendor.manage', 'general');
    }

    let itemIndex = 0;
    let barcodeIndexes = {};
    let skuIndexes = {};
    for (let item of purchaseResponse.items) {
      item.index = itemIndex;
      item.serverQty = item.qty;

      if (!item.count) {
        item.count = 0;
      }

      this.indexItemCodes(item, itemIndex, barcodeIndexes, skuIndexes);
      this.initShares(item, purchaseResponse);

      itemIndex++;
    }

    purchaseResponse.barcodeIndexes = barcodeIndexes;
    purchaseResponse.skuIndexes = skuIndexes;

    // Select which field is selected by default
    purchaseResponse.inputTargetField = "qty";
    for (let field of this.getStatusFields(purchaseResponse.typeObject, purchaseResponse.status)) {
      if (field.default) {
        purchaseResponse.inputTargetField = field.key;
      }
    }
    

    if (window.innerWidth < 700) {
      purchaseResponse.isMobile = true;
    }

    // Reflect the server-side retry lock so a refresh keeps Retry hidden and the
    // "Retrying..." indicator shown while a retry is actively running.
    purchaseResponse.retrying = !!purchaseResponse.retryInProgress;

    console.log('barcodeIndexes:', barcodeIndexes);
    this.keyboardBuffer = "";
    this.purchase = purchaseResponse;
    this.applyDefaultListFilters();
    this.updateListReportLink();
    // Phones: open with the far-right column (the step's input column, e.g.
    // Order Qty) fully in view rather than cut off behind the right edge.
    this.scrollItemsToEndPending = window.innerWidth < 700;
    this.scrollItemsToEndTries = 0;
    this.refreshDisplayItems();

    this.updateCalculations();

    await this.loadExportOptions();

    this.watchConnectivity();

    if (unavailableError) {
      await this.markUnavailable(unavailableError.status, unavailableError.detail);
      return;
    }

    if (this.purchase.status === 'processing' || (this.purchase.status === 'reversing' && this.purchase.reversalInProgress)) {
      // A large movement (or reversal) is posting in the background: poll the
      // HEAD only (status + processingProgress) and reload the full purchase
      // once the status changes. Reloading the whole purchase every 4 s cost
      // the API 2–3 s of CPU per poll on a 20k-line count and starved the
      // movement.
      this.pollProcessing();
      return;
    }

    if (!this.isLocked()) {
      await this.loadCategories();
      this.loadPurchaseUpdates(null);
    }

    return;
  }

  // While the server posts a step's movement (or a reversal) in the
  // background: a light head poll every 4 s for progress; the full purchase
  // reloads when the status changes, or when a reversal's heartbeat stops.
  private pollProcessing() {
    if (this.autoSyncTimeout) {
      clearTimeout(this.autoSyncTimeout);
    }
    this.autoSyncTimeout = setTimeout(async () => {
      if (!this.purchase || (this.purchase.status !== 'processing' && this.purchase.status !== 'reversing')) {
        return;
      }
      const polledStatus = this.purchase.status;
      try {
        const head: any = await this.ioService.post('/transfer/getTransferUpdates', {
          purchaseId: this.purchase._id,
          editSessionKey: this.purchase.editSessionKey,
          lastRetrieved: new Date().toISOString(),   // head only — no line payload
          omitIndex: null,
          includeIndexes: [],
        });
        if (head && head.status && (head.status !== polledStatus
            || (polledStatus === 'reversing' && !head.reversalInProgress))) {
          await this.loadPurchase();
          return;
        }
        if (head && head.processingProgress) {
          (this.purchase as any).processingProgress = head.processingProgress;
        }
      } catch (e) {
        console.log('processing poll failed: ', e);
      }
      this.pollProcessing();
    }, 4000);
  }

  loadCategories = async () => {
    let categoriesFindInput = {
      type: 'inventory'
    };

    // Categories are looked up at a store/warehouse the user is granted at.
    // The target is normally that location, but on a vendor-target transfer
    // (Return to Vendor) the target is the vendor, where store users hold no
    // grants and the lookup came back "Permission denied" as a toast — use the
    // source location there.
    const vendorTarget = this.purchase.typeObject && this.purchase.typeObject.targetType === 'vendor';
    const categoryLocationId = vendorTarget
      ? this.purchase.locationId
      : (this.purchase.customerId || this.purchase.locationId);
    if (categoryLocationId) {
      categoriesFindInput['locationId'] = categoryLocationId;
    } else {
      categoriesFindInput['anyLocation'] = true;
    }

    let availableCategories:any = await this.ioService.post('/group/getGroups', categoriesFindInput);

    if (availableCategories) {
      availableCategories.unshift({
        _id: null,
        name: 'All Categories',
        subtype: ''
      });
  
      this.availableCategories = availableCategories;

      this.purchase.selectedCategory = this.availableCategories[0];
    }

    return;
  }

  loadExportOptions = async () => {
    console.log('loadExportOptions: ');
    // let exportOptions: any = await this.reportService.getReports(['document'], this.purchase.type);

    let exportOptions: any = await this.ioService.post('/report/getLocationReports', {
      locationId: this.purchase.customerId,
      contextType: this.purchase.type,
      unlisted: true
    });


    console.log('exportOptions: ', exportOptions);

    // The header shows at most ONE download and ONE print icon; each opens a
    // chooser when several options exist. Print options are flattened across
    // every context report (a report may carry several templates) and named
    // after the Template doc. Newer servers attach the names (`templates`);
    // against older ones they are looked up here, and as a last resort the
    // key is shown so two templates never collapse into one label.
    const missingKeys = [];
    for (const exportOption of (exportOptions || [])) {
      if (!Array.isArray(exportOption.templates) && exportOption.templateKeys && exportOption.templateKeys.length) {
        missingKeys.push(...exportOption.templateKeys);
      }
    }
    let lookedUp: any[] = [];
    if (missingKeys.length) {
      try {
        lookedUp = (await this.ioService.post('/template/getPrintTemplates', {
          printTemplateKeys: Array.from(new Set(missingKeys)),
        }) as any[]) || [];
      } catch (err) {
        console.log('getPrintTemplates lookup failed: ', err);
      }
    }

    const printOptions = [];
    for (const exportOption of (exportOptions || [])) {
      const templates = Array.isArray(exportOption.templates) ? exportOption.templates : lookedUp;
      for (const templateKey of (exportOption.templateKeys || [])) {
        const template = templates.find(t => t.key === templateKey);
        printOptions.push({
          reportKey: exportOption.key,
          reportName: exportOption.name,
          templateKey,
          name: (template && template.name) || (exportOption.name + ' (' + templateKey + ')'),
        });
      }
    }

    // A template name repeated across reports gets its report name prefixed
    // (counted before renaming so every duplicate is prefixed, not just the first).
    const nameCounts = {};
    for (const option of printOptions) {
      nameCounts[option.name] = (nameCounts[option.name] || 0) + 1;
    }
    for (const option of printOptions) {
      if (nameCounts[option.name] > 1) {
        option.name = option.reportName + ': ' + option.name;
      }
    }

    this.purchase.exportOptions = exportOptions;
    this.purchase.printOptions = printOptions;
  }


  async purchaseTypeChange(purchaseType) {
    this.purchase.type = purchaseType;

    this.purchase.locationId = null;
    this.purchase.customerId = null;
    this.purchase.locationName = null;
    this.purchase.customerName = null;

    for (const typeOb of this.purchaseTypes) {
      if (typeOb.key === this.purchase.type) {
        this.purchase.typeObject = typeOb;
      }
    }

    await this.updateLocationOptions();
    await this.updateCustomerOptions();
  }

  async updateLocationOptions() {
    if (this.purchase.typeObject.sourceType) {
      let locationOptions = await this.locationService.getLocationsByType(this.purchase.typeObject.sourceType);
      let allowedLocationOptions = [];
      const sourceActionKey = this.createActionKey(this.purchase.typeObject);
      // Stepped types scope creation by ONE side (the first step's authContext) —
      // only filter this dropdown when it's the scoped side.
      const sourceFilterApplies = !this.hasSteps(this.purchase.typeObject)
        || this.createAuthContext(this.purchase.typeObject) === 'locationId';
      for (let locationOption of locationOptions) {
        if (!sourceFilterApplies || this.authorizationService.checkPermission('purchase.'+this.purchase.type+'.'+sourceActionKey, locationOption._id) || this.purchase.typeObject.sourceType == 'vendor') {
          allowedLocationOptions.push(locationOption);
        }
      }
  
      this.purchase.locationOptionsAll = allowedLocationOptions;
      this.applyServeRestrictions();
    } else {
      this.purchase.locationOptions = [];
      this.purchase.locationOptionsAll = [];
      this.purchase.locationId = null;
    }

    // Exactly one choice is no choice: take it. With several, leave the
    // dropdown on "Select…" so the user has to pick (a silently preselected
    // first store was how transfers landed at the wrong location).
    if (!this.purchase.locationId && this.purchase.locationOptions.length === 1) {
      this.purchaseLocationChange(this.purchase.locationOptions[0]._id);
    }
  }

  // Source locations (vendors today; possibly warehouses later) may declare
  // servesLocationId — the target locations they deliver to (absent = all).
  // Filter the source dropdown by the chosen target, and reselect if the
  // current source no longer qualifies. Re-runs whenever the target changes.
  applyServeRestrictions() {
    const allOptions = this.purchase.locationOptionsAll || [];
    const targetId = this.purchase.customerId;
    const options = !targetId ? allOptions : allOptions.filter(option =>
      !Array.isArray(option.servesLocationId)
      || option.servesLocationId.some(id => String(id) === String(targetId)));
    this.purchase.locationOptions = options;

    if (this.purchase.locationId && !options.some(option => option._id === this.purchase.locationId)) {
      // The chosen source no longer serves this target: back to "Select…"
      // (never a silent switch to another source) unless only one remains.
      this.purchaseLocationChange(options.length === 1 ? options[0]._id : null);
    }
  }

  async updateCustomerOptions() {
    if (this.purchase.typeObject.targetType) {
      let customerOptions = await this.locationService.getLocationsByType(this.purchase.typeObject.targetType);
      let allowedCustomerOptions = [];
      const targetActionKey = this.createActionKey(this.purchase.typeObject);
      const targetFilterApplies = !this.hasSteps(this.purchase.typeObject)
        || this.createAuthContext(this.purchase.typeObject) === 'customerId';
      for (let customerOption of customerOptions) {
        if (!targetFilterApplies || this.authorizationService.checkPermission('purchase.'+this.purchase.type+'.'+targetActionKey, customerOption._id) || this.purchase.typeObject.targetType == 'vendor') {
          allowedCustomerOptions.push(customerOption);
        }
      }

      this.purchase.customerOptions = allowedCustomerOptions;
    } else {
      this.purchase.customerOptions = [];
      this.purchase.customerId = null;
    }

    // See updateLocationOptions: only a lone option is taken automatically.
    if (!this.purchase.customerId && this.purchase.customerOptions.length === 1) {
      this.purchaseCustomerChange(this.purchase.customerOptions[0]._id);
    }
  }

  purchaseLocationChange(locationId) {
    this.purchase.locationId = locationId || null;
    const location = locationId ? this.locationService.getLocation(locationId) : null;
    this.purchase.locationName = location ? location.name : null;
  }

  purchaseCustomerChange(locationId) {
    this.purchase.customerId = locationId || null;
    const location = locationId ? this.locationService.getLocation(locationId) : null;
    this.purchase.customerName = location ? location.name : null;
    this.applyServeRestrictions();
  }

  sameSourceAndTarget(): boolean {
    const typeObject = this.purchase.typeObject;
    return !!(typeObject && typeObject.sourceType && typeObject.targetType
      && this.purchase.locationId && this.purchase.customerId
      && String(this.purchase.locationId) === String(this.purchase.customerId));
  }

  // Everything that must be in place before a transfer can be built: the
  // source and target selections (when the type has them), required custom
  // fields, and distinct locations. Drives the Build button and its snack.
  buildBlockers(): string[] {
    const typeObject = this.purchase.typeObject;
    if (!typeObject) {
      return ['Transfer type'];
    }
    const blockers = this.missingRequiredCustomFields(this.purchase).map(field => field.name || field.key);
    if (typeObject.sourceType && !this.purchase.locationId) {
      blockers.push(typeObject.sourceLabel || 'Source location');
    }
    if (typeObject.targetType && !this.purchase.customerId) {
      blockers.push(typeObject.targetLabel || 'Target location');
    }
    if (this.sameSourceAndTarget()) {
      blockers.push('Different source and target locations');
    }
    return blockers;
  }

  purchaseSubtypeChange(subtype) {
    this.purchase.subtype = subtype;
  }


  itemQtyChange(item, fieldKey, changedItem = item) {
    if (this.isUnitField(fieldKey)) {
      this.applyUnitEntry(item, fieldKey, false);
    }
    this.updateLineCalculations(changedItem);
  }

  removePurchaseItem = async (itemIndex) => {
    this.activeItem = null;
    const item = this.purchase.items[itemIndex];
    this.cancelDeferredSave(item);
    item.removing = true;

    this.purchase.items.splice(this.purchase.items.indexOf(item), 1);
    this.purchase.items = [...this.purchase.items];

    this.updateBarcodeIndexes();

    this.updateCalculations();

    if (this.cacheMode()) {
      await this.enqueueLineOp(item, 'remove', {});
      // Line indexes shifted: refresh the offline copy (worker, background).
      this.transferQueueService.savePurchase(this.purchase);
      return;
    }

    // Send Update to Server — batched with any other removals made in the
    // same moment (see queueLineRemoval).
    this.queueLineRemoval(item, itemIndex);
  }

  // ---- batched line removals (server mode) ---------------------------------
  // Removing a line rewrites the whole transfer document on the server (a
  // 17k-line warehouse order is ~6 MB) and replicates the rewrite, so rapid
  // clicks must never become concurrent per-line updates that queue behind
  // one another for minutes (production, 2026-10-09). Removals are collected
  // for a moment and sent as ONE removeTransferItems request; clicks made
  // while that request is in flight collect for the next one. An API without
  // the batch endpoint (404) gets the old one-request-per-line call, in
  // sequence.
  static readonly REMOVAL_BATCH_MS = 150;
  private pendingRemovals: Array<{ item: any; itemIndex: number; purchase: any }> = [];
  private removalFlushTimer: any = null;
  private removalFlushPromise: Promise<void> = null;
  private batchRemoveSupported = true;

  private queueLineRemoval(item, itemIndex: number) {
    this.pendingRemovals.push({ item, itemIndex, purchase: this.purchase });
    if (!this.removalFlushTimer && !this.removalFlushPromise) {
      this.removalFlushTimer = setTimeout(() => {
        this.removalFlushTimer = null;
        this.flushLineRemovals();
      }, TransferComponent.REMOVAL_BATCH_MS);
    }
  }

  private flushLineRemovals() {
    if (this.removalFlushPromise || this.pendingRemovals.length === 0) {
      return;
    }
    // One request per transfer: a removal queued just before navigating to
    // another transfer still goes to the transfer it was made on.
    const purchase = this.pendingRemovals[0].purchase;
    const batch = this.pendingRemovals.filter(entry => entry.purchase === purchase);
    this.pendingRemovals = this.pendingRemovals.filter(entry => entry.purchase !== purchase);
    this.removalFlushPromise = this.sendLineRemovals(purchase, batch)
      .catch(() => undefined)
      .then(() => {
        this.removalFlushPromise = null;
        if (this.pendingRemovals.length > 0) {
          this.flushLineRemovals();
        }
      });
  }

  private async sendLineRemovals(purchase, batch: Array<{ item: any; itemIndex: number }>) {
    const ids = batch.map(entry => String(entry.item.accountId));
    // After each line's own add / update, if one is still in flight.
    await Promise.all(ids.map(id => this.awaitLineWrites(id)));
    const base = {
      purchaseId: purchase._id,
      type: purchase.type,
      locationId: purchase.locationId,
      customerId: purchase.customerId,
      costTotal: purchase.costTotal,
      editSessionKey: purchase.editSessionKey,
    };
    let write: Promise<any>;
    if (this.batchRemoveSupported) {
      write = this.ioService.post('/transfer/removeTransferItems', { ...base, accountIds: ids }).catch(err => {
        if (err && err.status === 404) {
          this.batchRemoveSupported = false;
          return this.removeLinesOneByOne(base, batch);
        }
        throw err;
      });
    } else {
      write = this.removeLinesOneByOne(base, batch);
    }
    for (const id of ids) {
      this.trackLineWrite(id, write);
    }
    try {
      const purchaseUpdate: any = await write;
      if (purchaseUpdate && purchaseUpdate.timeUpdated && this.purchase === purchase) {
        this.purchase.timeUpdated = new Date(purchaseUpdate.timeUpdated);
      }
    } catch (err) {
      console.log('line removal failed: ', err);
      AppComponent.showSnack(err?.error?.msg || 'Unable to remove the item. Reloading the transfer.', 'OK', 6000);
      if (this.purchase === purchase) {
        this.loadPurchase();
      }
    }
  }

  // Older API without the batch endpoint: one request per line, in sequence.
  private async removeLinesOneByOne(base, batch: Array<{ item: any; itemIndex: number }>) {
    let last: any = null;
    for (const entry of batch) {
      last = await this.ioService.post('/transfer/removeTransferItem', { ...base, itemIndex: entry.itemIndex, item: entry.item });
    }
    return last;
  }

  // ─── Header sorting (display-only) ─────────────────────────
  // The stored items array is never reordered — item.index stays each item's
  // canonical identity for saves and the polling sync. displayItems is a sorted
  // VIEW over the same objects, rebuilt on membership changes (load/add/remove)
  // and header clicks — deliberately NOT on value edits, so rows never jump
  // under an active scanner. Other users' sorts are invisible to this one.
  displayItems: any[] = [];
  sortKey: string = null;
  sortDirection = 1;

  sortBy(key: string) {
    if (this.sortKey === key) {
      if (this.sortDirection === 1) {
        this.sortDirection = -1;
      } else {
        // Third click clears back to the transfer's own order.
        this.sortKey = null;
        this.sortDirection = 1;
      }
    } else {
      this.sortKey = key;
      this.sortDirection = 1;
    }
    this.refreshDisplayItems();
  }

  // Phone widths scroll the item columns horizontally; the viewport must be
  // as wide as the header row (width: max-content) because the virtual
  // scroll's absolutely-positioned content can't push the width itself.
  syncItemsHorizontal() {
    const wrapper = document.querySelector('.items-scroll') as HTMLElement;
    if (!wrapper) {
      return;
    }
    const header = wrapper.querySelector('.items-header') as HTMLElement;
    const viewport = wrapper.querySelector('.inventory-table') as HTMLElement;
    if (!header || !viewport) {
      return;
    }
    // All widths: the viewport must match the header so narrow windows scroll
    // headers and rows together instead of the viewport panning rows alone.
    // Two-pass: the header's own width depends on the scrollbar spacer, whose
    // width we measure from the viewport after sizing it.
    viewport.style.width = header.offsetWidth + 'px';
    const scrollbarWidth = viewport.offsetWidth - viewport.clientWidth;
    wrapper.style.setProperty('--vscrollbar', scrollbarWidth + 'px');
    viewport.style.width = header.offsetWidth + 'px';
    if (window.innerWidth >= 700) {
      // No pinned column on desktop — clear any leftover phone-scroll state.
      wrapper.style.setProperty('--hscroll', '0px');
    } else if (this.scrollItemsToEndPending) {
      this.scrollItemsToEnd(wrapper);
    }
  }

  // Header "back" button to the report that lists this transfer's type (e.g.
  // Warehouse to Store). Resolved through the user's permission-filtered
  // report list, so it only appears when there is somewhere to go.
  private async updateListReportLink() {
    const typeKey = this.purchase?.type;
    let report = null;
    try {
      report = await this.reportService.findReportForTransferType(typeKey, this.purchase?.typeObject?.listReportKey);
    } catch (e) {
      console.log('list report lookup failed: ', e);
    }
    if (!report || this.purchase?.type !== typeKey) {
      this.headerService.clearBackLink();
      return;
    }
    this.headerService.setBackLink({
      label: report.name,
      scope: '/transfers/',
      action: () => this.reportService.navigateToReport(report.key),
    });
  }

  // Phone default scroll position after a (re)load: the rightmost column fully
  // visible; the pinned identity cell follows via --hscroll exactly as it does
  // for a finger scroll. Widths can still be settling on the first tick after
  // a load, so an unscrollable wrapper is re-checked a few times.
  private scrollItemsToEndPending = false;
  private scrollItemsToEndTries = 0;

  private scrollItemsToEnd(wrapper: HTMLElement) {
    const maxScroll = wrapper.scrollWidth - wrapper.clientWidth;
    if (maxScroll > 0) {
      this.scrollItemsToEndPending = false;
      wrapper.scrollLeft = maxScroll;
      wrapper.style.setProperty('--hscroll', wrapper.scrollLeft + 'px');
    } else if (this.scrollItemsToEndTries++ < 5) {
      setTimeout(() => this.syncItemsHorizontal(), 100);
    } else {
      // Nothing to scroll (all columns fit) — stop waiting.
      this.scrollItemsToEndPending = false;
    }
  }

  itemsScrolled(event: any) {
    event.target.style.setProperty('--hscroll', event.target.scrollLeft + 'px');
  }

  refreshDisplayItems() {
    // Defer: header/viewport widths settle after this render pass.
    setTimeout(() => this.syncItemsHorizontal());
    const all = this.purchase?.items || [];
    // Filtered here (not only per row): the virtual scroll sizes rows by
    // count, so hidden rows inside it would leave gaps.
    const items = this.filtersActive() ? all.filter(item => this.itemVisible(item)) : all;
    this.filterCounts = { visible: items.length, total: all.length };
    if (!this.sortKey) {
      this.displayItems = items.slice();
      return;
    }
    const key = this.sortKey;
    const dir = this.sortDirection;
    // A just-counted line holds a Date until the save reply brings the
    // server's ISO string; compare both as ISO so Time Counted sorts true.
    const sortValue = (value) => value instanceof Date ? value.toISOString() : value;
    this.displayItems = items.slice().sort((a, b) => {
      const av = sortValue(a[key]);
      const bv = sortValue(b[key]);
      const aEmpty = av === null || av === undefined || av === '';
      const bEmpty = bv === null || bv === undefined || bv === '';
      if (aEmpty && bEmpty) { return a.index - b.index; }
      if (aEmpty) { return 1; }  // empty values sink in either direction
      if (bEmpty) { return -1; }
      const cmp = (typeof av === 'number' && typeof bv === 'number')
        ? av - bv
        : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      return (cmp * dir) || (a.index - b.index);
    });
  }

  // Barcode/SKU lookup maps: code -> every line index carrying it. A code can
  // sit on more than one line (a single and a six-pack sharing a UPC), which
  // the scan path resolves through the chooser instead of picking one blindly.
  private indexItemCodes(item, itemIndex: number, barcodeIndexes, skuIndexes) {
    if (item.barcode) {
      const barcodes = Array.isArray(item.barcode) ? item.barcode : [item.barcode];
      for (const barcode of barcodes) {
        this.addCodeIndex(barcodeIndexes, barcode, itemIndex);
      }
    }
    if (item.sku) {
      this.addCodeIndex(skuIndexes, item.sku, itemIndex);
    }
    // Vendor lines are scanned by the linked retail item's Item # (the server
    // resolves new scans that way); index it so a re-scan finds the line too.
    if (item.itemNumber) {
      this.addCodeIndex(barcodeIndexes, item.itemNumber, itemIndex);
    }
    // Break Case lines are the case, but staff may scan the single: index the
    // single's codes so that scan selects the case line instead of re-adding.
    if (Array.isArray(item.caseBreakSingleBarcodes)) {
      for (const barcode of item.caseBreakSingleBarcodes) {
        this.addCodeIndex(barcodeIndexes, barcode, itemIndex);
      }
    }
    if (item.caseBreakSingleSku) {
      this.addCodeIndex(skuIndexes, item.caseBreakSingleSku, itemIndex);
    }
  }

  private addCodeIndex(map, code, itemIndex: number) {
    const key = String(code);
    if (!map[key]) {
      map[key] = [];
    }
    if (map[key].indexOf(itemIndex) === -1) {
      map[key].push(itemIndex);
    }
  }

  updateBarcodeIndexes() {
    let itemIndex = 0;
    let barcodeIndexes = {};
    let skuIndexes = {};
    for (let item of this.purchase.items) {
      item.index = itemIndex;

      this.indexItemCodes(item, itemIndex, barcodeIndexes, skuIndexes);

      itemIndex++;
    }
    this.purchase.barcodeIndexes = barcodeIndexes;
    this.purchase.skuIndexes = skuIndexes;
    this.refreshDisplayItems();
    return;
  }


  fieldClick(field, item) {
    // console.log('fieldClick: ', field);
    switch (field.key) {
      case 'cost':
        this.startEditItemCost(field, item);
        break;
    }
  }


  // Cost is editable on a vendor-sourced transfer while it is in a working
  // state for this user (legacy open/pending, or any non-terminal step they may
  // work) and they hold Vendor.manage.
  canEditItemCost(): boolean {
    return !!this.purchase.authorization?.editPrice
      && this.isVendorLinkedType(this.purchase.typeObject)
      && this.canEdit();
  }

  // Vendor products are the lines on both directions of a vendor transfer
  isVendorLinkedType(typeObject: any): boolean {
    return !!typeObject && (typeObject.sourceType === 'vendor' || typeObject.targetType === 'vendor');
  }

  startEditItemCost(field, item) {
    if (this.canEditItemCost()) {
      // Unit costs are kept to 4 decimal places (fractional cents).
      this.purchase.costEdit = {
        item,
        cost: (item.cost/100).toFixed(4),
        fieldLabel: field.label
      };
    }
  }

  cancelCostEdit() {
    this.purchase.costEdit = null;
  }

  saveCostEdit = async () => {
    // Update Vendor Product
    const cost = this.numberRound(Number(String(this.purchase.costEdit.cost).replace(/[^0-9.\-]/g, '')), 4);
    if (!isFinite(cost) || cost <= 0) {
      AppComponent.showSnack('Enter a cost greater than zero (up to 4 decimal places).', 'OK', 4000);
      return;
    }
    let vendorProductPriceUpdate: any = await this.ioService.post('/inventory/updateVendorProductPrice', {
      accountId: this.purchase.costEdit.item.accountId,
      price: cost,
    });

    // Refresh line item cost
    this.purchase.costEdit.item.cost = vendorProductPriceUpdate.vendorProductPrice;
    this.saveItem(false);
    this.cancelCostEdit();

    await this.updateCalculations();
  }

  numberRound = (value, decimals) => {
    return Number(Math.round(Number(value+'e'+decimals))+'e-'+decimals);
  };

  selectSearchResult = async (searchResult: any) => {
    if (searchResult.inPurchase) {
      // Find-within-transfer mode: jump to the existing line instead of adding.
      // Selecting is locating, never counting — on mobile, setActiveItem on the
      // already-active item would tap-add 1, so skip it in that case. Blur the
      // search box so wedge scans return to the barcode handler.
      const itemIndex = this.purchase.items.findIndex(item => item.accountId == searchResult.accountId);
      if (itemIndex !== -1 && !(this.activeItem && this.activeItem.index === itemIndex)) {
        this.setActiveItem(itemIndex);
        if (window.innerWidth >= 700) {
          this.scrollToIndexRobust(itemIndex);
        }
      }
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      this.purchase.findModal = false;
      this.clearSearch();
      return;
    }

    // Find-modal catalog result (findInventoryMode): the item is not on the
    // transfer. Adds are only legal here as UNEXPECTED lines, and the add is
    // irreversible on a locked step — confirm first, mirroring the scan
    // flow's "Receive Unexpected" prompt. The add records 1 unit (same as a
    // scan) and the new line becomes active for +/− corrections.
    if (this.purchase.findModal) {
      if (this.canAddRemove()) {
        // Handheld search-to-add (Transfer Controls → Search Items to Add): add
        // the line and make it active for the qty controls. A result already on
        // the transfer is selected rather than added again.
        const existingIndex = this.purchase.items.findIndex(item => item.accountId == searchResult._id);
        this.purchase.findModal = false;
        this.clearSearch();
        if (existingIndex !== -1) {
          this.setActiveItem(existingIndex);
          return;
        }
        const addedItem = await this.addPurchaseItem(searchResult, true, false, 0, 'tap');
        if (addedItem) {
          this.setActiveItem(addedItem.index);
        }
        return;
      }

      if (!this.canUnexpectedAdd()) {
        return;
      }
      const confirmRef = AppComponent.showMessage(
        'Item Not On Transfer',
        `Receive ${searchResult.name} as an UNEXPECTED item? It will be added with Expected 0 and a count of 1.`,
        ['Receive Unexpected', 'Cancel'],
        false,
      );
      const confirmResult: DialogOption = await confirmRef.afterClosed().toPromise();
      if (!confirmResult || confirmResult.value !== 'Receive Unexpected') {
        return;
      }
      const newItem = await this.addPurchaseItem(searchResult, true, false, 0, 'tap');
      this.purchase.findModal = false;
      this.purchase.findInventoryMode = false;
      this.clearSearch();
      if (newItem) {
        this.setActiveItem(newItem.index);
      }
      return;
    }

    this.addPurchaseItem(searchResult, true, false, 0, 'tap');
    // this.purchase.searchResults = null;
  }

  addPurchaseItem = async (item, updateCalculations=true, defaultZeros=false, addQty=0, entryMethod: 'tap' | 'scan' | 'import' | 'bulk' = 'tap') => {

// console.log('addPurchaseItem: ', item);
    // Break Case types: whichever side was scanned (case or single), the line
    // is the CASE, with its linked single carried along for display; the
    // server resolves the link again on save and refuses unlinked items.
    if (this.purchase.typeObject?.breakCase === true && !item.caseBreakQty) {
      let resolved: any;
      try {
        resolved = await this.ioService.post('/transfer/resolveCaseBreak', { accountId: item._id || item.accountId });
      } catch (e) {
        AppComponent.showSnack((e && e.error && e.error.msg) || 'This item cannot be broken into singles.', 'OK', 5000);
        return null;
      }
      const existingIndex = this.purchase.items.findIndex(line => String(line.accountId) === String(resolved.account._id));
      if (existingIndex !== -1) {
        this.setActiveItem(existingIndex);
        AppComponent.showSnack(resolved.account.name + ' is already on this transfer.', 'OK', 3000);
        return null;
      }
      item = {
        ...resolved.account,
        caseBreakSingleId: resolved.single._id,
        caseBreakSingleName: resolved.single.name,
        caseBreakSingleSku: resolved.single.sku,
        caseBreakSingleBarcodes: resolved.single.barcode,
        caseBreakQty: resolved.unitsPerCase,
      };
    }
    let sourceQty = 0;
    let targetQty = 0;
    let adjustedQty = 0;
    let qty = 0;
    let childAccountId;
    let childQty;
    

    if (addQty) {
      qty = addQty;
    } else if (entryMethod === 'scan' && this.purchase.typeObject.addUponScan) {
      // addUponScan types count the scan itself as one unit (applied through
      // the step's input target field right after the add), so the step
      // default must not also be applied — it would land the line at 2.
    } else {

      for (let field of this.getStatusFields(this.purchase.typeObject, this.purchase.status)) {
        if (field.default) {
          // Step configs store the default as a number or the legacy `true`
          // (= 1). `true` used to flow into numberRound and come out NaN, so
          // the line saved with qty 0 instead of the configured default.
          const parsedDefault = field.default === true ? 1 : parseFloat(field.default);
          const defaultValue = isNaN(parsedDefault) ? 0 : parsedDefault;
          if (field.key === 'qty') {
            qty = defaultValue;
          } else if (field.key === 'qtyRemove') {
            // "Qty to Remove" (adjustment types: breakage, tasters, theft,
            // donations) is the derived view of -qty, so its default is stored
            // negated. Previously only a `qty` column's default was honored, so
            // manually added lines on those types started at 0.
            qty = defaultValue * -1;
          }
        }
      }

    }



    if (item.balance && item.balance[this.purchase.locationId]) {
      sourceQty = item.balance[this.purchase.locationId];
    }
    
    if (item.balance && item.balance[this.purchase.customerId]) {
      targetQty = item.balance[this.purchase.customerId];
    }
    // Count types flagged defaultRemoveAll (Physical Inventory): every line
    // starts at Counted 0, i.e. the change is the inverse of on hand — the
    // same start the server gives Add All. Before this, only Add All started
    // at 0; a searched/tapped line started at Counted = on hand. A scan on an
    // addUponScan type still counts its one unit on top (applied through the
    // input target field right after the add), so a scanned line lands at 1.
    if (this.purchase.typeObject?.defaultRemoveAll && this.purchase.type !== 'adjustment-retail' && !addQty) {
      qty = targetQty * -1;
    }

    if (this.purchase.type == 'adjustment-retail' && !this.purchase.strictInventoryCountable) {

      if (item.descendantAccountIds && item.descendantAccountIds.length > 0 && (item.descendantAccountIds[0] != item._id || item.descendantAccountIds.length > 1)) {
        targetQty = 0;
        childAccountId = item.options[0].options[0].accountId;
        childQty = item.options[0].options[0].qty;
      }

      if (defaultZeros) {
        qty = targetQty*-1;
      } else {
        adjustedQty = targetQty;
      }
      
    }

    if (item.subtype == 'vendor-product') {
      item.cost = item.price;
    } else {
      if (this.purchase.type == 'vendor-retail' || this.purchase.type == 'retail-vendor' || !item.cost) {
        if (item.calculatedCost && item.calculatedCost.default) {
          item.cost = item.calculatedCost.default;
        }
      }
    }

    let newItem:any = {
      accountId: item._id,
      name: item.name,
      categoryName: item.categoryName,
      subcategoryName: item.subcategoryName,
      barcode: item.barcode,
      itemNumber: item.itemNumber,
      remoteId: item.remoteId,
      unitOfMeasure: item.unitOfMeasure,
      unitDescription: item.unitDescription,
      attr: item.attr,
      cost: item.cost,
      price: item.price,
      sourceQty: this.numberRound(sourceQty, 4),
      targetQty: this.numberRound(targetQty, 4),
      adjustedQty: this.numberRound(adjustedQty, 4),
      qty: this.numberRound(qty, 4),
      serverQty: this.numberRound(qty, 4),
      sku: item.sku,
      entryMethod,
      // uiCost: (item.cost / 100).toFixed(2),
      index: this.purchase.items.length,
    };

    if (item.stockLocation && item.stockLocation[this.purchase.locationId]) {
      newItem.stockLocation = item.stockLocation[this.purchase.locationId];
    }

    // Break Case lines carry their resolved single (the server stamps the
    // same fields on save) so the columns and scan index work immediately.
    if (item.caseBreakQty) {
      newItem.caseBreakSingleId = item.caseBreakSingleId;
      newItem.caseBreakSingleName = item.caseBreakSingleName;
      newItem.caseBreakSingleSku = item.caseBreakSingleSku;
      newItem.caseBreakSingleBarcodes = item.caseBreakSingleBarcodes;
      newItem.caseBreakQty = item.caseBreakQty;
    }

    // Vendor product lines carry their case size (retail units per vendor
    // unit) so unit columns can convert; the server stamps it too.
    if (item.subtype === 'vendor-product') {
      const caseQty = Number(item.caseQty !== undefined ? item.caseQty : item.options?.[0]?.options?.[0]?.qty);
      newItem.caseQty = caseQty > 0 ? caseQty : 1;
    }

    // Order increment (case/pack size) rides on the item so qty edits can warn
    // when they break it; persisted server-side with the other descriptive fields.
    if (item.orderIncrement > 1) {
      newItem.orderIncrement = item.orderIncrement;
    }

    // Adding on a step where normal adds are off = an UNEXPECTED item
    // (allowUnexpectedAdd step). Expected 0 so the discrepancy math and the
    // reconcile gate surface it; the server forces the same fields. The scan
    // itself counts as one physical unit received.
    if (this.canUnexpectedAdd()) {
      newItem.expectedQty = 0;
      newItem.requestedQty = 0;
      newItem.unexpected = true;
      if (!newItem.qty) {
        newItem.qty = 1;
        newItem.serverQty = 1;
      }
    }

    if (childAccountId) {
      newItem.childAccountId = childAccountId;
      newItem.childQty = childQty;
    }

    this.purchase.items = [...this.purchase.items, newItem];


    if (updateCalculations) {

      this.indexItemCodes(newItem, newItem.index, this.purchase.barcodeIndexes, this.purchase.skuIndexes);

      this.refreshDisplayItems();
  
      if (this.purchase.searchResults && this.purchase.searchResults.length > 0) {
        this.purchase.searchResults.splice(this.purchase.searchResults.indexOf(item), 1);
      }

      if (this.cacheMode()) {
        // Local Inventory Cache: the line is queued (add + this device's
        // share) and shows up on the server when the queue drains. No on-hand
        // yet when the item came from the cache — the server fills it.
        this.initShares(newItem, this.purchase);
        newItem.myShare = this.inputValueOf(newItem);
        newItem.otherShares = 0;
        if (item.fromCache) {
          newItem.pendingOnHand = true;
        }
        this.updateCalculations();
        await this.enqueueLineOp(newItem, 'add', { set: newItem.myShare, entryMethod });
        return newItem;
      }

      this.purchase.savingUpdates = true;

      // Send Update to Server
      const addWrite = this.ioService.post('/transfer/updateTransferItem', {
        purchaseId: this.purchase._id,
        type: this.purchase.type,
        locationId: this.purchase.locationId,
        customerId: this.purchase.customerId,
        itemIndex: newItem.index,
        item: newItem,
        new: true,
        editSessionKey: this.purchase.editSessionKey,
      });
      this.trackLineWrite(newItem.accountId, addWrite);
      const purchaseUpdate: any = await addWrite;

      this.purchase.timeUpdated = new Date(purchaseUpdate.timeUpdated);

      this.purchase.savingUpdates = false;

      this.updateCalculations();
    }
    // this.save();
    return newItem;
  }

  // Every bulk item-add path (import, replenish, add-by-filter) lives in the
  // Bulk Add wizard (BulkAddComponent); the wizard posts to the server-side
  // endpoints and the transfer reloads on close.
  bulkAddAvailable() {
    return !!(this.purchase.typeObject?.allowAddAll
      || this.purchase.inventoryReplenishment
      || this.purchase.authorization?.upload);
  }

  bulkAddLaunch() {
    this.purchase.bulkAdd = true;
  }

  bulkAddClosed(result) {
    this.purchase.bulkAdd = null;
    if (result?.reload) {
      // A large bulk add reloads a large transfer: keep the loading overlay
      // up until the new lines are on screen, otherwise the old (often empty)
      // list sits there for seconds and looks like nothing happened.
      this.purchase.loading = true;
      this.loadPurchase().catch(() => {
        this.purchase.loading = false;
      });
    } else if (!this.isLocked()) {
      // No full reload — sync any updates that arrived while the wizard was
      // open (update polling pauses while it is up).
      this.loadPurchaseUpdates(null);
    }
    if (result?.errors?.length) {
      this.purchase.errors = result.errors;
    }
  }

  clearSearch() {
    this.purchase.searchText = null;
    this.searchInventory();
  }

  // Handheld full-screen item search (Transfer Controls: Search Items to Add /
  // Find Item on Transfer).
  openFindModal() {
    this.purchase.searchText = null;
    this.purchase.searchResults = null;
    this.purchase.findInventoryMode = false;
    this.purchase.findModal = true;
    setTimeout(() => {
      const input = document.getElementById('find-item-input') as HTMLInputElement;
      if (input) {
        input.focus();
      }
    }, 50);
  }

  closeFindModal() {
    this.purchase.findModal = false;
    this.purchase.findInventoryMode = false;
    this.clearSearch();
  }

  // "No Results" escape hatch on allowUnexpectedAdd steps: widen the find
  // modal's search from the transfer's lines to the full eligible catalog so
  // a physically-present item that was never on the transfer can be received
  // as UNEXPECTED. Not offered on other locked steps — adding there is
  // forbidden by design and the button would be a false promise.
  searchOffTransfer() {
    this.purchase.findInventoryMode = true;
    // Run right away and leave the keyboard down so the widened results show.
    this.runFindModalSearch(document.getElementById('find-item-input') as HTMLInputElement);
  }

  // Handheld find modal: the soft keyboard covers the results, so searching
  // as the user types (with Enter taking the first hit) meant selecting an
  // item they never saw. In the modal, typing only clears stale results;
  // Enter runs the search and blurs the input so the keyboard drops and the
  // results are visible — nothing is auto-selected, the user taps a row. The
  // desktop inline search is untouched (search-as-you-type, Enter picks the
  // first result). The modal only opens on handhelds (isMobile buttons).
  searchOnEnter(): boolean {
    return !!this.purchase && !!this.purchase.findModal;
  }

  findModalTextChanged() {
    if (this.purchase.searchTimeout) {
      clearTimeout(this.purchase.searchTimeout);
    }
    this.purchase.searchResults = null;
    this.purchase.searching = false;
  }

  runFindModalSearch(input?: HTMLInputElement) {
    if (input && document.activeElement === input) {
      // Once blurred, this keystroke's keyup reaches the window scanner/keypad
      // handler — flag it so it is ignored there (see keyEvent).
      this.suppressEnterKeyupUntil = Date.now() + 1000;
      input.blur();
    }
    this.searchInventory(true);
  }

  searchInventory = async (immediate = false) => {
    this.purchase.searchResults = null;

    if (this.purchase.searchTimeout) {
      clearTimeout(this.purchase.searchTimeout);
    }

    if (this.purchase.searchText != '' && this.purchase.searchText != null) {
      this.purchase.searching = true;

      // Steps without allowAddRemove use the search to FIND lines already on the
      // transfer rather than to add new inventory (unless the user explicitly
      // widened the search to the catalog — findInventoryMode).
      if (this.hasSteps() && !this.canAddRemove() && !this.purchase.findInventoryMode) {
        const searchLower = String(this.purchase.searchText).toLowerCase();
        this.purchase.searchResults = this.purchase.items.filter(item => {
          const barcodes = Array.isArray(item.barcode) ? item.barcode : [item.barcode];
          return (item.name && String(item.name).toLowerCase().indexOf(searchLower) !== -1)
            || (item.sku && String(item.sku).toLowerCase().indexOf(searchLower) !== -1)
            || (item.itemNumber && String(item.itemNumber).toLowerCase().indexOf(searchLower) !== -1)
            || barcodes.some(barcode => barcode && String(barcode).toLowerCase().indexOf(searchLower) !== -1);
        }).slice(0, 10).map(item => ({
          inPurchase: true,
          accountId: item.accountId,
          sku: item.sku,
          // itemIdentifier() reads the barcode when Item # is primary — the
          // sku column renders blank without these.
          barcode: item.barcode,
          itemNumber: item.itemNumber,
          name: item.name,
          unitOfMeasure: item.unitOfMeasure,
          unitDescription: item.unitDescription,
          attr: item.attr,
          subtype: item.categoryName,
        }));
        this.purchase.searching = false;
        return;
      }

      this.purchase.searchTimeout = setTimeout(async () => {
        let existingInventoryIds = this.purchase.items.map(item => {
          return item.accountId;
        });

        let inventory = await this.ioService.post('/transfer/getInventoryAccounts', {
          purchaseId: this.purchase._id,
          search: this.purchase.searchText,
          limit: 10,
          // omit: existingInventoryIds
        });

        this.purchase.searchResults = inventory;
        this.purchase.searching = false;
      }, immediate ? 0 : 750);


    } else {
      this.purchase.searching = false;
    }
  }

  searchKeyDown(event: any) {
    if (event.key === "Enter" && this.searchOnEnter()) {
      event.preventDefault();
      this.runFindModalSearch(event.target);
      return;
    }
    if (event.key === "Enter" && this.purchase && this.purchase.searchResults && this.purchase.searchResults.length > 0) {
      this.selectSearchResult(this.purchase.searchResults[0]);
      this.searchInventory();
    }
    if (event.key === "Escape" && this.purchase && this.purchase.findModal) {
      this.closeFindModal();
    }
  }

  // Search result columns come from the PurchaseType (searchResultFields —
  // strings or {path, class} objects); types without the config keep the
  // original column set.
  private defaultSearchResultFields = [
    { path: 'sku', class: 'sku' },
    { path: 'name', class: 'name' },
    { path: 'unitOfMeasure', class: 'uom' },
    { path: 'unitDescription', class: 'unit-description' },
    { path: 'subtype', class: 'subtype' },
  ];

  // Memoized like getStatusFields: the template iterates this every change-
  // detection pass, and a fresh array of fresh objects makes ngFor destroy and
  // recreate the result rows' children on every pass. If that happens between
  // a click's mousedown and mouseup (any timer can trigger a CD pass), the
  // browser suppresses the click entirely — search results intermittently
  // ignored clicks until this was cached.
  private searchResultFieldsCache: any[] = null;
  private searchResultFieldsCacheKey: string = null;

  searchResultFields() {
    const cacheKey = this.purchase?.typeObject?.key || '';
    if (this.searchResultFieldsCache && this.searchResultFieldsCacheKey === cacheKey) {
      return this.searchResultFieldsCache;
    }

    const configured = this.purchase?.typeObject?.searchResultFields;
    let fields;
    if (!Array.isArray(configured) || configured.length === 0) {
      fields = this.defaultSearchResultFields;
    } else {
      // String entries inherit the default column's class for that path (e.g.
      // unitOfMeasure → .uom) so the standard styling and narrow-window hiding
      // keep applying; unknown paths fall back to the path itself.
      fields = configured.map(field => {
        if (typeof field !== 'string') {
          return { path: field.path, class: field.class || field.path };
        }
        const defaultField = this.defaultSearchResultFields.find(d => d.path === field);
        return { path: field, class: defaultField ? defaultField.class : field };
      });
    }

    this.searchResultFieldsCache = fields;
    this.searchResultFieldsCacheKey = cacheKey;
    return fields;
  }

  removeAll = async () => {
    const itemCount = this.purchase.items.length;
    const confirmRef = AppComponent.showMessage(
      'Remove All Items',
      `Are you sure you want to remove all ${itemCount} item${itemCount === 1 ? '' : 's'} from this transfer?`,
      ['Remove All', 'Cancel'],
      false,
    );
    const confirmResult: DialogOption = await confirmRef.afterClosed().toPromise();
    if (!confirmResult || confirmResult.value !== 'Remove All') {
      return;
    }

    this.purchase.loading = true;
    this.purchase.items = [];
    let purchaseUpdateResponse: any = await this.ioService.post('/transfer/updateTransfer', {
      purchase: this.purchase
    });
    this.updateBarcodeIndexes();
    this.purchase.loading = false;
  }

  markAllCounted = async () => {
    this.purchase.loading = true;
    for (let purchaseItem of this.purchase.items) {
      purchaseItem.counted = true;
    }
    await this.updateCalculations();
    this.purchase.loading = false;
  }

  markAllReceived = async () => {
    this.purchase.loading = true;
    for (let purchaseItem of this.purchase.items) {
      purchaseItem.qty = purchaseItem.expectedQty;
    }
    
    let purchaseUpdateResponse: any = await this.ioService.post('/transfer/updateTransfer', {
      purchase: this.purchase
    });

    this.loadPurchase();
  }

  // Running totals behind updateCalculations. itemsRef pins the array they
  // were computed from: a replaced array (reload, resync, bulk add) forces a
  // full pass before any incremental update.
  private calcTotals: { itemsRef: any[]; costTotal: number; qtyTotal: number; unitTotal: number; caseQtyLines: number; uncounted: number } = null;

  private calcContext() {
    const typeObject = this.purchase.typeObject || {};
    return {
      orderTransfer: !!(typeObject.sourceType && typeObject.targetType),
      defaultCostFromPrice: typeObject.defaultCostFromPrice === true,
      allowNegativeQty: typeObject.allowNegativeQty,
      invertNegativeQty: typeObject.invertNegativeQty === true,
      unitFields: this.unitFields(),
    };
  }

  // Derives one line's display fields and returns what it contributes to the
  // transfer totals (stored on the line as _calc for incremental updates).
  private calculateLine(purchaseItem, ctx) {
    if (purchaseItem.price && !purchaseItem.cost && ctx.defaultCostFromPrice) {
      purchaseItem.cost = purchaseItem.price;
    }

    if (!purchaseItem.qty) {
      purchaseItem.qty = 0;
    } else {
      if (ctx.allowNegativeQty === false && purchaseItem.qty < 0) {
        console.log('negative qty not allowed');
        if (ctx.invertNegativeQty) {
          purchaseItem.qty *= -1;
        } else {
          purchaseItem.qty = 0;
        }
      }
    }

    // The handheld "previous + new" split lives inside this line math only
    // when it was opened on the counted quantity (targetQtyCalculated then
    // holds just the new part); a split on a direct field (Transfer Qty,
    // Qty to Remove, vendor units) keeps the whole total in the field, so
    // subtracting it here put the Source/Target tiles off by the previous part.
    let previousCount = 0;
    if (purchaseItem.previousCount && (purchaseItem.previousCountField || 'targetQtyCalculated') === 'targetQtyCalculated') {
      previousCount = purchaseItem.previousCount;
    }

    purchaseItem.sourceQtyCalculated = purchaseItem.sourceQty - purchaseItem.qty - previousCount;
    purchaseItem.targetQtyCalculated = purchaseItem.targetQty + purchaseItem.qty - previousCount;
    purchaseItem.lineCost = purchaseItem.qty * purchaseItem.cost;
    purchaseItem.differenceQty = purchaseItem.qty - purchaseItem.expectedQty;

    // Removal-flow display: "Qty to Remove" shows/edits the positive
    // magnitude of a negative stored qty (breakage, tasters, theft,
    // donations). Derived — never persisted (server derivedFieldKeys).
    purchaseItem.qtyRemove = purchaseItem.qty * -1;

    // Display-only Item # — the server's resolved value when present (vendor
    // lines carry the linked retail item's Item #), else the first barcode
    // (barcode may be an array). Lets step configs show an "Item #" column
    // without touching the composite SKU/Item cell.
    if (!purchaseItem.itemNumber) {
      purchaseItem.itemNumber = Array.isArray(purchaseItem.barcode)
        ? (purchaseItem.barcode.length > 0 ? purchaseItem.barcode[0] : null)
        : purchaseItem.barcode;
    }

    // Order increment (case/pack size) awareness: warn — never block — when
    // an ordered qty is not a whole multiple of the item's increment.
    // Only on types that move stock from a source to a target (orders and
    // transfers): on adjustments and counts the qty column is a discrepancy,
    // not an order, so the warning was noise there.
    purchaseItem.incrementWarning = ctx.orderTransfer
      && purchaseItem.orderIncrement > 1
      && purchaseItem.qty > 0
      && purchaseItem.qty % purchaseItem.orderIncrement !== 0;

    // Break Case: singles that will be added = cases × singles per case
    // (display only; the server recomputes it when the transfer completes).
    if (purchaseItem.caseBreakQty) {
      purchaseItem.caseBreakUnits = this.numberRound((purchaseItem.qty || 0) * purchaseItem.caseBreakQty, 2);
    }

    // Unit columns mirror their base field × case size. An entered value is
    // kept when it already round-trips (4-decimal tolerance) so typing is
    // not rewritten; anything else (load, case edits, rounding) re-derives.
    const hasCaseQty = purchaseItem.caseQty !== undefined;
    const units = hasCaseQty ? purchaseItem.qty * this.lineCaseQty(purchaseItem) : 0;
    for (const unitField of ctx.unitFields) {
      const derived = this.numberRound((purchaseItem[unitField.unitOf] || 0) * this.lineCaseQty(purchaseItem), 4);
      const current = Number(purchaseItem[unitField.key]);
      if (isNaN(current) || Math.abs(derived - current) > 0.01) {
        purchaseItem[unitField.key] = derived;
      }
    }

    const contribution = {
      cost: purchaseItem.lineCost || 0,
      qty: purchaseItem.qty || 0,
      units,
      caseQtyLine: hasCaseQty ? 1 : 0,
      uncounted: purchaseItem.counted ? 0 : 1,
    };
    purchaseItem._calc = contribution;
    return contribution;
  }

  private applyCalcTotals() {
    const totals = this.calcTotals;
    this.purchase.costTotal = totals.costTotal;
    this.purchase.qtyTotal = totals.qtyTotal;
    this.purchase.unitTotal = totals.caseQtyLines > 0 ? this.numberRound(totals.unitTotal, 4) : null;
    this.purchase.uncounted = totals.uncounted;
  }

  updateCalculations = async () => {
    console.log('updateCalculations: ');
    const ctx = this.calcContext();
    const totals = { itemsRef: this.purchase.items, costTotal: 0, qtyTotal: 0, unitTotal: 0, caseQtyLines: 0, uncounted: 0 };
    for (const purchaseItem of this.purchase.items) {
      const c = this.calculateLine(purchaseItem, ctx);
      totals.costTotal += c.cost;
      totals.qtyTotal += c.qty;
      totals.unitTotal += c.units;
      totals.caseQtyLines += c.caseQtyLine;
      totals.uncounted += c.uncounted;
    }
    this.calcTotals = totals;
    this.applyCalcTotals();
    console.log('uncounted: ', this.purchase.uncounted);
  }

  // One line changed (a scan, a typed count): re-derive that line and move
  // the totals by its difference instead of walking every line — on a
  // 20k-line count the full pass cost a handheld ~100 ms per scan.
  updateLineCalculations(item) {
    const totals = this.calcTotals;
    if (!item || !item._calc || !totals || totals.itemsRef !== this.purchase.items || this.purchase.items.indexOf(item) === -1) {
      this.updateCalculations();
      return;
    }
    const before = item._calc;
    const after = this.calculateLine(item, this.calcContext());
    totals.costTotal += after.cost - before.cost;
    totals.qtyTotal += after.qty - before.qty;
    totals.unitTotal += after.units - before.units;
    totals.caseQtyLines += after.caseQtyLine - before.caseQtyLine;
    totals.uncounted += after.uncounted - before.uncounted;
    this.applyCalcTotals();
  }

  startEditStatus(status) {
    let msg = "Are you sure?"
    switch (status) {
      case 'pending':
        msg = "Are you sure you are done building this transfer?";
        break;
    }

    this.purchase.statusEdit = {
      status,
      msg
    };
  }

  cancelEditStatus() {
    this.purchase.statusEdit = null;
  }

  // Click-sorting for the discrepancy table inside the status-change modal —
  // same asc → desc → off cycle as the item grid.
  statusDetailSortKey: string = null;
  statusDetailSortDirection = 1;

  sortStatusDetail(key: string) {
    if (!this.purchase.statusEdit || !this.purchase.statusEdit.detailRows) {
      return;
    }
    if (this.statusDetailSortKey === key) {
      if (this.statusDetailSortDirection === 1) {
        this.statusDetailSortDirection = -1;
      } else {
        this.statusDetailSortKey = null;
        this.statusDetailSortDirection = 1;
      }
    } else {
      this.statusDetailSortKey = key;
      this.statusDetailSortDirection = 1;
    }

    const sortKey = this.statusDetailSortKey;
    const direction = this.statusDetailSortDirection;
    this.purchase.statusEdit.detailRows = [...this.purchase.statusEdit.detailRows].sort((a, b) => {
      if (!sortKey) {
        return 0;
      }
      const aVal = a[sortKey] == null ? '' : a[sortKey];
      const bVal = b[sortKey] == null ? '' : b[sortKey];
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * direction;
      }
      return String(aVal).localeCompare(String(bVal), undefined, { numeric: true }) * direction;
    });
  }

  // --- Cancel Transfer (stepped types; gated server-side by
  // purchase.<type>.cancel and surfaced through purchase.cancelAllowed) ---

  startCancelTransfer() {
    this.purchase.controls = false;
    this.purchase.cancelConfirm = true;
  }

  dismissCancelTransfer() {
    this.purchase.cancelConfirm = false;
  }

  cancelTransfer = async () => {
    this.purchase.cancelConfirm = false;
    this.purchase.loading = true;
    try {
      await this.ioService.post('/transfer/cancelTransfer', {
        purchaseId: this.purchase._id,
      });
      AppComponent.showSnack('Transfer canceled.', 'OK', 4000);
    } catch (e) {
      console.log('Error canceling transfer: ', e);
      this.purchase.errors = [
        { msg: e?.error?.msg || 'Unable to cancel the transfer.' },
      ];
    }
    await this.loadPurchase();
    this.purchase.loading = false;
  }

  // --- Reverse Transfer (any completed transfer; gated server-side by
  // purchase.<type>.reverse and surfaced through purchase.reverseAllowed).
  // A reason is required and travels with the request; a resume of an
  // interrupted reversal reuses the reason already on the transfer. ---

  startReverseTransfer() {
    this.purchase.controls = false;
    this.purchase.reverseReason = '';
    this.purchase.reverseConfirm = true;
  }

  dismissReverseTransfer() {
    this.purchase.reverseConfirm = false;
  }

  reverseReasonValid(): boolean {
    return !!(this.purchase.reverseReason && String(this.purchase.reverseReason).trim());
  }

  reverseTransfer = async () => {
    const resuming = this.purchase.status === 'reversing';
    if (!resuming && !this.reverseReasonValid()) {
      return;
    }
    this.purchase.reverseConfirm = false;
    this.purchase.loading = true;
    let errors: any[] = null;
    try {
      const response: any = await this.ioService.post('/transfer/reverseTransfer', {
        purchaseId: this.purchase._id,
        reason: resuming ? undefined : String(this.purchase.reverseReason).trim(),
      });
      AppComponent.showSnack(response && response.processing ? response.msg : 'Transfer reversed.', 'OK', 4000);
    } catch (e) {
      console.log('Error reversing transfer: ', e);
      errors = [
        { msg: e?.error?.msg || 'Unable to reverse the transfer.' },
        ...((e?.error?.errors || []).slice(0, 10)),
      ];
    }
    await this.loadPurchase();
    this.purchase.loading = false;
    if (errors) {
      // After the reload so the error dialog survives the fresh purchase.
      this.purchase.errors = errors;
    }
  }

  toggleDiscrepancyView() {
    this.descrepancyOnly = !this.descrepancyOnly;
    this.refreshDisplayItems();
  }

  toggleShowUncounted() {
    this.setShowUncounted(!this.showUncounted);
  }

  setCountedFilter(mode: 'all' | 'counted' | 'uncounted') {
    this.countedFilter = mode;
    this.refreshDisplayItems();
  }

  // "Counted by" has nothing to act on when only uncounted lines are listed.
  countedByApplies(): boolean {
    return this.countedFilter !== 'uncounted';
  }

  toggleCountedByMe() {
    this.setCountedByMe(!this.countedByMeOnly);
  }

  setShowUncounted(value: boolean) {
    this.setCountedFilter(value ? 'all' : 'counted');
  }

  setCountedByMe(value: boolean) {
    this.countedByMeOnly = value;
    this.refreshDisplayItems();
  }

  setDiscrepancyOnly(value: boolean) {
    this.descrepancyOnly = value;
    this.refreshDisplayItems();
  }

  // A step where lines get counted (physical counts and other requireCount steps).
  isCountStep(): boolean {
    return this.hasSteps() && !!(this.currentStepInfo()?.step?.requireCount || this.purchase.typeObject?.countAsOf);
  }

  // Defaults once per transfer: phone + counting step → counted-by-me only.
  private applyDefaultListFilters() {
    const id = String(this.purchase && this.purchase._id);
    if (this.filterDefaultsAppliedFor === id) {
      return;
    }
    this.filterDefaultsAppliedFor = id;
    const focused = !!this.purchase.isMobile && this.isCountStep();
    this.countedFilter = focused ? 'counted' : 'all';
    this.countedByMeOnly = focused;
    this.descrepancyOnly = false;
  }

  filtersActive(): boolean {
    return this.countedFilter !== 'all' || (this.countedByMeOnly && this.countedByApplies()) || this.descrepancyOnly;
  }

  // One-line description of the active filters for the bar above the list.
  filterSummary(): string {
    const parts = [];
    if (this.countedFilter === 'counted') { parts.push('counted'); }
    if (this.countedFilter === 'uncounted') { parts.push('uncounted'); }
    if (this.countedByMeOnly && this.countedByApplies()) { parts.push('by me'); }
    if (this.descrepancyOnly) { parts.push('discrepancies'); }
    return parts.length ? 'Showing ' + parts.join(' · ') : 'Showing all items';
  }

  // Message for an empty filtered list.
  filterEmptyMessage(): string {
    if (this.countedFilter === 'uncounted') {
      return 'Everything has been counted.';
    }
    if (this.countedFilter === 'counted' && this.countedByMeOnly) {
      return 'Nothing counted by you yet — scan an item to start.';
    }
    return 'No items match the current filters.';
  }

  // Did this user count the line? Lines this device counted are flagged
  // locally as they are saved; lines from the server carry the last user who
  // saved them and, with the Local Inventory Cache, each device's share.
  isCountedByMe(item): boolean {
    if (!item) {
      return false;
    }
    if (item.countedByMe === true) {
      return true;
    }
    const me = this.authenticationService.getAuthenticatedId();
    const deviceId = this.deviceSettingsService.getDeviceId();
    const contributions = item.contributions || null;
    if (contributions) {
      if (contributions[deviceId]) {
        return true;
      }
      if (me && Object.keys(contributions).some(key => contributions[key] && String(contributions[key].userId) === String(me))) {
        return true;
      }
    }
    return !!me && !!item.updatedUserId && String(item.updatedUserId) === String(me);
  }

  private markCountedByMe(item) {
    if (!item) {
      return;
    }
    item.countedByMe = true;
    if (this.isCountStep()) {
      item.counted = true;
      item.countedTime = new Date();
    }
  }

  // The filter rules alone (no exceptions) — used for the list and its counts.
  filterMatches(item): boolean {
    if (!item) {
      return false;
    }
    if (this.countedFilter === 'counted' && !item.counted) {
      return false;
    }
    if (this.countedFilter === 'uncounted' && item.counted) {
      return false;
    }
    if (this.countedByMeOnly && this.countedByApplies() && !this.isCountedByMe(item)) {
      return false;
    }
    if (this.descrepancyOnly && (item.expectedQty || 0) == (item.qty || 0)) {
      return false;
    }
    return true;
  }

  // Row visibility for the "Show Discrepancies Only" filter. Works on any
  // status — stepped transfers carry expectedQty from their step ops, legacy
  // transfers from the pending flow (previously the filter was dead outside
  // status 'pending' on stepped types).
  itemVisible(item): boolean {
    // The selected line and a line with an unsaved scan always stay visible.
    return this.filterMatches(item) || item === this.activeItem || (!!item && item.pendingSave === true);
  }

  updateTransferStatus = async (status, acknowledgeDiscrepancies = false) => {
    await this.flushDeferredSaves();
    this.purchase.loading = true;

    setTimeout(async () => {
      try {
        let purchaseStatusResponse: any = await this.ioService.post('/transfer/updateTransferStatus', {
          purchaseId: this.purchase._id,
          status,
          returnPurchase: true,
          defaultPendingQty: this.purchase.typeObject.defaultPendingQty,
          acknowledgeDiscrepancies,
        });

        await this.loadPurchase(purchaseStatusResponse.purchase);
      } catch(e) {
        console.log('Error updating purchase status: ', e);
        await this.loadPurchase();
        if (e?.error?.deletedLines) {
          this.showDeletedLinesError(e.error.detail);
          this.purchase.loading = false;
          return;
        }

        const detailItems = e?.error?.detail && Array.isArray(e.error.detail)
          ? e.error.detail.map((detailItem) => detailItem.name + ': ' + detailItem.qty + ' of ' + detailItem.expectedQty)
          : [];

        if (e?.error?.discrepancies) {
          // Reconcile steps take one transfer-level acknowledgment: show the
          // discrepancy table and re-send with the acknowledgment on Yes.
          // The server detail is authoritative for qty/expectedQty; display
          // columns (Item #, size, attribute) come from the loaded items.
          const detailRows = (e.error.detail || []).map((detailItem) => {
            const matchedItem = (this.purchase.items || []).find(
              (candidate) => String(candidate.accountId) === String(detailItem.accountId)) || {};
            const qty = Number(detailItem.qty || 0);
            const expectedQty = Number(detailItem.expectedQty || 0);
            return {
              itemNumber: Array.isArray(matchedItem.barcode)
                ? (matchedItem.barcode.length > 0 ? matchedItem.barcode[0] : null)
                : matchedItem.barcode,
              name: detailItem.name,
              unitDescription: matchedItem.unitDescription,
              attr: matchedItem.attr,
              expectedQty,
              qty,
              differenceQty: qty - expectedQty,
            };
          });
          this.statusDetailSortKey = null;
          this.statusDetailSortDirection = 1;
          this.purchase.statusEdit = {
            status,
            msg: e.error.msg + ' Proceed anyway?',
            detailRows,
            acknowledgeDiscrepancies: true,
          };
        } else {
          // Surface the server's message (e.g. permission denials) in the in-app
          // error modal — a raw alert() blocks the page and stringifies the
          // error object uselessly.
          this.purchase.errors = [
            { msg: e?.error?.msg || 'Unable to update the transfer status.' },
            ...detailItems.map((line) => ({ msg: line })),
          ];
        }

        this.purchase.loading = false;
      }
    }, 1000);

    return;
  }

  save = async () => {
    console.log('save: ', this.purchase);

    let purchaseUpdateResponse: any = await this.ioService.post('/transfer/updateTransfer', {
      purchase: this.purchase
    });

    if (this.purchase.status != 'deleted') {
      this.loadPurchase();
    }
  }

  delete = async () => {
    this.purchase.status = 'deleted';
    await this.save();
    // History-back can land right back on the deleted transfer's URL (deep
    // link, fresh tab, or the create flow's history entry), which then fails
    // to load with "No matching purchase found." — go to the dashboard.
    this.router.navigate(['/']);
  }

  // send = async () => {
  //   if (this.purchase.items.length > 0) {
  //     this.purchase.status = 'pending';
  //     this.save();
  //   } else {
  //     this.purchase.errors = ['Transfer must contain at least 1 item.'];
  //   }
  // }

  // revoke = async () => {
  //   this.purchase.status = 'open';
  //   this.save();
  // }

  complete = async () => {
    if (this.deletedLines().length > 0) {
      this.showDeletedLinesError();
      return;
    }
    console.log('complete: ', this.purchase);

    if (!this.purchase.itemSaving) {

      this.purchase.saving = true;
      this.purchase.loading = true;

      try {
        let purchaseCompleteResponse: any = await this.ioService.post('/transfer/completeTransfer', {
          purchaseId: this.purchase._id
        });
        this.loadPurchase();
      } catch(purchaseCompleteError) {
        console.log('purchaseCompleteError: ', purchaseCompleteError.error);

        if (purchaseCompleteError.error && purchaseCompleteError.error.msg && purchaseCompleteError.error.detail) {
          await this.loadPurchase();

          let errorMsg = 'Vendor item with bad configuration: '+purchaseCompleteError.error.detail.name+'. Transfer not completed.';

          this.purchase.errors = [
            {
              msg: errorMsg
            }
          ];


          console.log('purchaseWithErrors: ', this.purchase);
        }
      }
    }
  }


  retry = async () => {
    console.log('retry: ', this.purchase);

    // Guard against double-initiation in this session: once a retry is in flight the
    // button is hidden, but bail defensively in case it's invoked again.
    if (this.purchase.retrying) {
      return;
    }

    // Hide the Retry button and show the "retrying" indicator immediately. A refresh
    // is covered separately by the server-side lock surfaced via retryInProgress.
    this.purchase.retrying = true;
    this.purchase.saving = true;

    try {
      let purchaseCompleteResponse: any = await this.ioService.post('/transfer/completeTransfer', {
        purchaseId: this.purchase._id,
        retry: true,
      });
      await this.loadPurchase();
    } catch(purchaseCompleteError) {
      console.log('purchaseCompleteError: ', purchaseCompleteError.error);

      // Reload so the view reflects the server's authoritative state (the lock is
      // released on a known failure, so Retry becomes available again if applicable).
      await this.loadPurchase();

      if (purchaseCompleteError.error && purchaseCompleteError.error.deletedLines) {
        this.showDeletedLinesError(purchaseCompleteError.error.detail);
      } else if (purchaseCompleteError.error && purchaseCompleteError.error.msg) {
        const detail = purchaseCompleteError.error.detail;
        const errorMsg = detail && !Array.isArray(detail) && detail.name
          ? 'Vendor item with bad configuration: ' + detail.name + '. Transfer not completed.'
          : purchaseCompleteError.error.msg;

        this.purchase.errors = [
          {
            msg: errorMsg
          }
        ];
      }
    }
  }





  // itemCostChange(item) {
  //   item.cost = item.uiCost*100;
  //   this.updateCalculations();
  // }




  downloadPurchase = async (reportKey) => {

    let fileExtension = '.csv';
    try {
      let templateConfig:any = await this.ioService.post('/template/getTemplate', {
        key: reportKey
      });
  
      if (templateConfig && templateConfig.fileExtension) {
        fileExtension = templateConfig.fileExtension;
      }
    } catch(e) {

    }

    const fileName = this.exportFileName(fileExtension);

    this.ioService.download('/report/loadRecords', {
      key: reportKey,
      utcOffset: new Date().getTimezoneOffset(),
      clientConfig: {
        download: true,
        clientFilters: [
          {
            type: 'id',
            path: '_id',
            value: [
              this.purchase._id
            ]
          }
        ]
      }
    }, fileName);

  }

  // Header download icon. The built-in CSV of the on-screen grid is always
  // offered (every transfer type, no server round trip); any export reports
  // configured for the type are listed after it. One option downloads
  // directly, several ask which.
  static readonly CSV_EXPORT_KEY = '__transfer-csv__';

  download() {
    const exportOptions = this.purchase.exportOptions || [];
    if (exportOptions.length === 0) {
      this.exportCsv();
      return;
    }

    const dialogConfig = new DialogConfig();
    dialogConfig.title = 'Download';
    dialogConfig.message = 'Choose what to download:';
    dialogConfig.stackOptions = true;
    dialogConfig.options = [new DialogOption('CSV (this transfer)', TransferComponent.CSV_EXPORT_KEY, DialogButtonStyle.Normal)]
      .concat(exportOptions.map(
        (exportOption) => new DialogOption(exportOption.name, exportOption.key, DialogButtonStyle.Normal)
      ));

    AppComponent.showDialog(dialogConfig).afterClosed().subscribe((result) => {
      if (result && result.value === TransferComponent.CSV_EXPORT_KEY) {
        this.exportCsv();
      } else if (result && result.value) {
        this.downloadPurchase(result.value);
      }
    });
  }

  // ─── CSV export of the on-screen grid ───────────────────────────
  // Rows are the lines currently displayed (current sort, "Show Discrepancies
  // Only" respected); columns are the transfer header details, the parts of
  // the Item cell (Item #, SKU, Name, Unit of Measure, Size, Attributes and
  // the UNEXPECTED tag when any line carries it), then every step column of
  // the current status — the full desktop set even when exported from a
  // handheld. Values are spreadsheet-friendly: currency as 36.00, numbers
  // without separators, dates as the screen shows them.
  exportFileName(extension: string): string {
    let fileName = this.purchase.key + extension;
    if (this.purchase.customerName) {
      fileName = this.purchase.customerName + '-' + fileName;
    }
    if (this.purchase.locationName) {
      fileName = this.purchase.locationName + '-' + fileName;
    }
    return fileName.replace(/[\\/:*?"<>|]+/g, '_');
  }

  csvCellValue(value: any, pipe: string): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    switch (pipe) {
      case 'currency':
      case 'currencyDetail':
      case 'currencyInverse': {
        const cents = Number(value);
        const decimals = Math.abs(cents - Math.round(cents)) > 1e-9 ? 4 : 2;
        return (cents / 100 * (pipe === 'currencyInverse' ? -1 : 1)).toFixed(decimals);
      }
      case 'currencyDollars':
        return Number(value).toFixed(2);
      case 'number':
      case 'numberDetail':
      case 'numberInverse':
        return String(Number(value) * (pipe === 'numberInverse' ? -1 : 1));
      case 'dateTime':
      case 'date':
      case 'dateTimeShort':
        return new Date(value).toLocaleString();
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'string':
        return this.rfPipeText(String(value));
      default:
        return typeof value === 'object' ? JSON.stringify(value) : String(value);
    }
  }

  // Mirrors the rf 'string' pipe: entity-decoded plain text.
  private rfPipeText(value: string): string {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = value;
    return tempElement.innerText;
  }

  buildTransferCsv(): { fileName: string, headers: string[], rows: string[][], csv: string } {
    const purchase: any = this.purchase;
    const typeObject = purchase.typeObject || {};
    const headers: string[] = [];
    const leading: string[] = [];

    const addLeading = (label: string, value: any) => { headers.push(label); leading.push(value === null || value === undefined ? '' : String(value)); };
    addLeading('Transfer Key', purchase.key);
    addLeading('Transfer Type', typeObject.name || purchase.type);
    if (typeObject.sourceType) {
      addLeading(typeObject.sourceLabel || 'Source', purchase.locationName);
    }
    if (typeObject.targetType) {
      addLeading(typeObject.targetLabel || 'Target', purchase.customerName);
    }
    for (const field of this.customFields()) {
      addLeading(field.name || field.key, purchase[field.key]);
    }
    const stepInfo = this.currentStepInfo();
    const statusName = (stepInfo && stepInfo.step.name) || purchase.statusName || String(purchase.status || '');
    addLeading('Status', statusName.charAt(0).toUpperCase() + statusName.slice(1));
    // The date column mirrors what the transfer header shows for the type.
    if (purchase.type == 'adjustment-retail' || typeObject.countAsOf) {
      addLeading('Inventory Count Date/Time', this.csvCellValue(purchase.countDateTime || purchase.timeAdded, 'dateTime'));
    } else if (purchase.type == 'vendor-retail' && purchase.effectiveDate && purchase.effectiveDate.startDate) {
      addLeading('Transfer Date', this.csvCellValue(purchase.effectiveDate.startDate, 'dateTime'));
    } else {
      addLeading('Created', this.csvCellValue(purchase.timeAdded, 'dateTime'));
    }

    const items = (this.displayItems || []).filter(item => item && this.itemVisible(item));
    const anyUnexpected = items.some(item => item.unexpected);
    const itemHeaders = ['Item #', 'SKU', 'Name', 'Unit of Measure', 'Size', 'Attributes'].concat(anyUnexpected ? ['Unexpected'] : []);
    headers.push(...itemHeaders);

    const fields = this.getStatusFields(typeObject, purchase.status);
    for (const field of fields) {
      headers.push(field.label || field.name || field.key);
    }

    const rows = items.map(item => {
      const barcode = Array.isArray(item.barcode) ? item.barcode[0] : item.barcode;
      const row = leading.slice();
      row.push(
        item.itemNumber || barcode || '',
        item.sku || '',
        item.name || '',
        item.unitOfMeasure || '',
        item.unitDescription || '',
        item.attr || '',
      );
      if (anyUnexpected) {
        row.push(item.unexpected ? 'Yes' : '');
      }
      for (const field of fields) {
        row.push(this.csvCellValue(item[field.key], field.pipe));
      }
      return row.map(cell => cell === null || cell === undefined ? '' : String(cell));
    });

    const escape = (cell: string) => /[",\r\n]/.test(cell) ? '"' + cell.replace(/"/g, '""') + '"' : cell;
    const csv = [headers].concat(rows).map(row => row.map(escape).join(',')).join('\r\n') + '\r\n';
    return { fileName: this.exportFileName('.csv'), headers, rows, csv };
  }

  exportCsv() {
    if (!this.purchase || !this.purchase._id) {
      return;
    }
    const built = this.buildTransferCsv();
    // UTF-8 BOM so Excel opens accented names correctly.
    const blob = new Blob(['\ufeff' + built.csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = built.fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    AppComponent.showSnack('Downloaded ' + built.rows.length + ' line' + (built.rows.length === 1 ? '' : 's') + ' to ' + built.fileName, 'OK', 4000);
  }

  // Header print icon: one template prints directly, several ask which
  // (mirrors the report view's print chooser).
  print() {
    const printOptions = this.purchase.printOptions || [];
    if (printOptions.length === 0) {
      return;
    }
    if (printOptions.length === 1) {
      this.printReport(printOptions[0].reportKey, printOptions[0].templateKey);
      return;
    }

    const dialogConfig = new DialogConfig();
    dialogConfig.title = 'Print';
    dialogConfig.dismissOnBackdrop = true;
    dialogConfig.stackOptions = true;
    dialogConfig.options = printOptions.map(
      (option, index) => new DialogOption(option.name, index, DialogButtonStyle.Normal)
    );

    AppComponent.showDialog(dialogConfig).afterClosed().subscribe((result) => {
      const option = result && result.value != null ? printOptions[result.value] : null;
      if (option) {
        // Opened here, at the last user interaction: the dialog button click
        // carries its own activation, so the popup blocker allows it, and an
        // earlier popup would have stolen focus from the dialog.
        this.printReport(option.reportKey, option.templateKey);
      }
    });
  }

  // Renders this transfer through a context report's print template into an
  // already-open window (the render can outlast the popup blocker's grace
  // period, so the window is opened before the request).
  // Prints this transfer through a context report's print template. Roll labels
  // go silently to the store's tag printer when an ISE desktop app is online
  // there (TagPrintService); otherwise the popup + print dialog as before.
  printReport(reportKey, templateKey) {
    // Where the tags are being printed: price templates show that location's
    // price (a Price Tags transfer has no location of its own).
    let locationId = null;
    try {
      locationId = this.locationService.getActiveLocationId() || null;
    } catch (e) {
    }
    this.tagPrintService.print('report/loadRecords', {
      key: reportKey,
      templateKey: templateKey,
      locationId,
      utcOffset: new Date().getTimezoneOffset(),
      clientConfig: {
        download: true,
        clientFilters: [
          {
            type: 'id',
            path: '_id',
            value: [
              this.purchase._id
            ]
          }
        ]
      }
    }, { description: (this.purchase.key ? 'Transfer ' + this.purchase.key : 'Transfer') }).catch((err) => {
      console.log('Print render error: ', err);
      AppComponent.showSnack(err?.error?.msg || 'Unable to generate print output.', 'Dismiss', 5000);
    });
  }

  // Resolve a scanned code to an item on the transfer. Some tenants keep the
  // scannable UPC in `sku` with `barcode` holding an internal item number, and
  // scanner configuration can disagree with the catalog about the UPC/EAN
  // leading zero. Strict precedence ladder — each rung consulted only when
  // everything above it missed: exact barcode -> exact sku -> zero-variants of
  // barcode (stripped, then prepended) -> zero-variants of sku.
  // Every line the code resolves to (same ladder; the first rung with any hit
  // wins). More than one = ambiguous scan.
  findBarcodeIndexes(barcode): number[] {
    const code = String(barcode);
    const variants = [];
    if (code.length > 1 && code.startsWith('0')) {
      variants.push(code.substring(1));
    }
    variants.push('0' + code);
    // Retail Pro-derived catalogs store a 12-digit UPC-A as its middle ten
    // digits plus a check digit recomputed as if the number-system digit were
    // zero; a 6/8-leading UPC only matches through this recomputed form.
    if (/^[1-9]\d{11}$/.test(code)) {
      const middle = code.substring(1, 11);
      let checkSum = 0;
      for (let i = 0; i < 10; i++) {
        checkSum += Number(middle[i]) * (i % 2 === 0 ? 1 : 3);
      }
      variants.push(middle + String((10 - (checkSum % 10)) % 10));
    }

    const rungs = [
      [this.purchase.barcodeIndexes[code]],
      [this.purchase.skuIndexes[code]],
      variants.map(variant => this.purchase.barcodeIndexes[variant]),
      variants.map(variant => this.purchase.skuIndexes[variant]),
    ];

    for (const rung of rungs) {
      const hits: number[] = [];
      for (const list of rung) {
        for (const index of (list || [])) {
          if (hits.indexOf(index) === -1) {
            hits.push(index);
          }
        }
      }
      if (hits.length > 0) {
        return hits;
      }
    }
    return [];
  }

  // Mobile numberpad edits live only in memory until Save Change. When the
  // user scans or taps a DIFFERENT item while an edit is pending, tenants with
  // settings.autoSaveOnScan persist the edited line first — identical to
  // pressing Save Change (counted flag included on counting steps). Must run
  // BEFORE any purchase-updates sync, which could otherwise clobber the
  // unsaved local values. Other clients (setting absent) keep today's
  // abandon-on-switch behavior. saveItem clears `edited`, so double calls
  // along the scan path no-op.
  autoSavePendingEdit = async () => {
    if (!this.clientSettingsService.isAutoSaveOnScanEnabled()) { return; }
    if (window.innerWidth >= 700) { return; }
    if (!this.activeItem || !this.activeItem.edited) { return; }
    await this.saveItem();
  }

  barcodeEntered = async (barcode, statusOverride = false) => {
    // Offline: refuse the scan out loud — recording it locally would be
    // silently lost, which is exactly the failure mode this guards against.
    if (!this.connectivityService.isOnline() && !this.cacheMode()) {
      this.blockedScanCount++;
      return;
    }

    // A scan while the "which item?" chooser is up has nowhere to go.
    if (this.scanChooserOpen) {
      AppComponent.showSnack('Choose an item first', 'OK', 2000);
      return;
    }

    // Weight/price-embedded scale labels (client settings.barcodeParsers,
    // decoded the way the ISE register does): the item code inside the label
    // drives the lookup and the decoded weight (or price ÷ store price) is
    // the quantity this scan adds.
    const decoded = parseEncodedBarcode(barcode, this.clientSettingsService.getBarcodeParsers());
    const code = decoded ? decoded.itemCode : barcode;

    // A re-scan of the already-active item resolves entirely locally —
    // skip the overlay, the pre-sync and the catalog lookup, even when the
    // code is shared with another item. Decoded labels always resolve
    // through the catalog: the quantity math needs the account's prices.
    if (!decoded && this.activeItem && this.findBarcodeIndexes(code).indexOf(this.activeItem.index) !== -1) {
      await this.processBarcode(code, statusOverride, false, this.activeItem.index);
      return;
    }

    // Scanning a different item: push the previous line's deferred count (in
    // the background — the save is a round trip and the new scan must not
    // wait on it), and save an unsaved edit first (config-gated).
    this.flushDeferredSaves();
    await this.autoSavePendingEdit();

    // Keypad keys typed from here until the scanned line opens are held for
    // that line (server mode resolves behind the overlay for a round trip).
    const activeBefore = this.activeItem;
    this.scanResolving = true;

    if (this.cacheMode()) {
      // The catalog is on the device: resolve and add without an overlay.
      try {
        await this.processBarcode(code, statusOverride, this.connectivityService.isOnline(), undefined, decoded);
      } finally {
        this.finishScanKeys(activeBefore);
      }
      return;
    }

    // Resolving a scan hits the server; overlay so the user knows the scan registered.
    LoadingComponent.showLoading();
    try {
      await this.processBarcode(code, statusOverride, true, undefined, decoded);
    } finally {
      LoadingComponent.hideLoading();
      this.finishScanKeys(activeBefore);
    }
  }

  // Quantity a decoded scale label adds for this account/line. `units` is the
  // label's retail-unit quantity (weight, or price ÷ the target store's
  // price). On vendor-sourced transfers every line is a vendor product, so
  // `qty` is that in cases (rounded up when the type wants whole cases);
  // elsewhere qty = units. Null when a price label has no store price to
  // divide by.
  decodedScanQty(decoded: DecodedBarcode, account: any, line: any): { qty: number, units: number, vendor: boolean } | null {
    // Vendor-product lines (receiving from or returning to a vendor) count in cases
    const vendor = this.purchase.typeObject?.sourceType === 'vendor' || this.purchase.typeObject?.targetType === 'vendor';
    let unitPrice: number = null;
    if (vendor) {
      // A vendor product's own price is its case cost — only the linked
      // retail item's store price (stamped by the server) converts a label.
      unitPrice = (account && typeof account.retailPrice === 'number') ? account.retailPrice : null;
    } else if (account && typeof account.locationPrice === 'number') {
      unitPrice = account.locationPrice;
    } else if (line && typeof line.price === 'number') {
      unitPrice = line.price;
    } else if (account && typeof account.price === 'number') {
      unitPrice = account.price;
    }
    const units = decodedQuantity(decoded, unitPrice);
    if (units == null) {
      return null;
    }
    if (vendor) {
      const caseQty = this.lineCaseQty(line && line.caseQty != null ? line : (account || {}));
      let cases = units / caseQty;
      if (this.purchase.typeObject.wholeVendorUnits === true && caseQty > 1) {
        cases = Math.sign(cases) * Math.ceil(Math.abs(cases) - 1e-9);
      }
      return { qty: this.numberRound(cases, 4), units, vendor };
    }
    return { qty: this.numberRound(units, 3), units, vendor };
  }

  decodedScanMessage(result: { units: number, vendor: boolean }, item: any): string {
    const uom = result.vendor ? 'retail units' : (item && item.unitOfMeasure ? item.unitOfMeasure : '');
    return 'Added ' + result.units + (uom ? ' ' + uom : '') + ' to ' + (item && item.name ? item.name : 'item');
  }

  // The unit (retail-unit) column paired with the step's input target field:
  // the target itself when it is a unit column, else the column whose unitOf
  // is the target. Null when the step has no unit column for it.
  scanUnitField(): any {
    const target = this.purchase.inputTargetField;
    if (!target) {
      return null;
    }
    return this.unitFieldConfig(target) || this.unitFields().find(field => field.unitOf === target) || null;
  }

  // Apply a decoded label to a line already on the transfer. On vendor
  // transfers the retail units are added through the unit column so the new
  // TOTAL converts to cases exactly like a typed entry (including the
  // whole-vendor-unit rounding and its note); otherwise the quantity is
  // added to the step's input target field. Replaces the type's addUponScan /
  // rescanAddQty increment for this scan.
  applyDecodedToLine(item: any, decoded: DecodedBarcode, account: any, wasAlreadyActive: boolean) {
    if (!(this.canEdit() && this.scanTargetFieldEditable())) {
      return;
    }
    const result = this.decodedScanQty(decoded, account, item);
    if (!result) {
      AppComponent.showSnack('No store price to convert the label price for ' + item.name, 'OK', 4000);
      return;
    }
    if (!wasAlreadyActive) {
      this.recordScanUndo(item);
    }
    const unitField = result.vendor ? this.scanUnitField() : null;
    if (unitField) {
      // A column derived from stored cases (4 decimals) carries float dust
      // (2.0833 × 24 = 49.9992): snap a derived value so the total reads as
      // entered; a value the user typed this session is kept as is.
      const derived = this.numberRound((item[unitField.unitOf] || 0) * this.lineCaseQty(item), 4);
      const current = Number(item[unitField.key]);
      const base = (isNaN(current) || Math.abs(current - derived) < 1e-9) ? this.numberRound(derived, 2) : current;
      item[unitField.key] = this.numberRound(base + result.units, 4);
      this.applyUnitEntry(item, unitField.key, true);
      this.updateCalculations();
    } else {
      this.changePurchaseItemValue(item, this.isUnitField(this.purchase.inputTargetField) ? result.units : result.qty);
    }
    this.confirmChange(item);
    AppComponent.showSnack(this.decodedScanMessage(result, item), 'OK', 2500);
  }

  // PurchaseType.rescanAddQty: how much a scan of a line ALREADY on the
  // transfer adds to the step's input field (blank/0 = just select the line,
  // the legacy behavior). Distinct from addUponScan, which also counts the
  // first scan and so double-counts on steps whose qty column has a default.
  rescanAddQty(): number {
    const raw = this.purchase?.typeObject?.rescanAddQty;
    if (raw === true) {
      return 1;
    }
    const qty = parseFloat(raw);
    return isNaN(qty) || qty <= 0 ? 0 : qty;
  }

  // The scan increment writes through the input target field; only do so when
  // the current step exposes that field as editable (a scan on a read-only
  // step must only locate the line).
  // Mobile panel edit controls (numberpad, +/−/Reset, Save, tap-to-add) act
  // only when the current step exposes the input target field as editable;
  // on a read-only step the panel is informational.
  activeFieldEditable(): boolean {
    return !!this.activeItem && !this.isLocked() && this.scanTargetFieldEditable();
  }

  scanTargetFieldEditable(): boolean {
    const fieldKey = this.purchase?.inputTargetField;
    if (!fieldKey) {
      return false;
    }
    return this.getStatusFields(this.purchase.typeObject, this.purchase.status)
      .some(field => field.key === fieldKey && field.editable);
  }

  // One scanned code can belong to several items — a single and a six-pack
  // often share a UPC. Resolution order: the active line if it carries the
  // code; otherwise gather every match (lines on the transfer + catalog items
  // not yet on it) and, when there is more than one, ask. Only the chosen
  // item is selected/added — never all of them.
  processBarcode = async (barcode, statusOverride = false, syncFirst = true, resolvedIndex?: number, decoded: DecodedBarcode = null) => {

    // A transfer the server has closed takes no more scans; say so instead of
    // queuing a change that can never apply (the pre-sync below would only
    // ask the server again and get the same answer).
    if (this.unavailable) {
      this.notifyUnavailable();
      return;
    }

    const canAdd = this.canAddRemove() || statusOverride;
    let barcodeIndex: number = resolvedIndex;
    let catalogItem = null;
    // The catalog account behind an existing line, when a decoded label needs
    // its prices / case size (the server includes on-transfer accounts on request).
    let lineAccount = null;

    if (barcodeIndex === undefined) {
      // Sync remote changes and look the code up in the catalog together; the
      // lookup only runs on steps that can add lines (always for a decoded
      // label, whose quantity math needs the account). The server omits
      // accounts already on this transfer unless includeExisting is set.
      // With the catalog on the device the sync runs in the background: the
      // server reconciles per-device counts, so the scan need not wait for it.
      const preSync = syncFirst ? this.loadPurchaseUpdates(null) : Promise.resolve(null);
      const [, catalogItems] = await Promise.all([
        this.cacheMode() ? Promise.resolve(null) : preSync,
        (canAdd || decoded)
          ? (this.cacheMode()
            ? this.cachedCatalogLookupWithFallback(barcode, !!decoded)
            : (async () => {
              // A just-removed (or just-added) line must have landed before
              // the server decides what is "already on this transfer".
              await this.awaitLineWrites();
              return this.ioService.post('/transfer/getInventoryAccounts', {
                purchaseId: this.purchase._id,
                barcode,
                includeExisting: !!decoded,
              });
            })())
          : Promise.resolve([]),
      ]);

      const candidates: any[] = this.findBarcodeIndexes(barcode)
        .map(index => ({ index, item: this.purchase.items[index] }));
      for (const account of (Array.isArray(catalogItems) ? catalogItems : [])) {
        // Break Case: a scanned single belongs to the case line already on the
        // transfer (the server only omits accounts by their own id).
        const existingIndex = this.purchase.items.findIndex(item => item.accountId == account._id
          || (this.purchase.typeObject?.breakCase === true && item.caseBreakSingleId && String(item.caseBreakSingleId) === String(account._id)));
        if (existingIndex === -1) {
          if (canAdd) {
            candidates.push({ account, item: account });
          }
        } else {
          const existing = candidates.find(candidate => candidate.index === existingIndex);
          if (existing) {
            existing.account = account;
          } else {
            candidates.push({ index: existingIndex, item: this.purchase.items[existingIndex], account });
          }
        }
      }

      let chosen = null;
      if (this.activeItem && candidates.some(candidate => candidate.index === this.activeItem.index)) {
        chosen = candidates.find(candidate => candidate.index === this.activeItem.index);
      } else if (candidates.length > 1) {
        chosen = await this.chooseScanMatch(barcode, candidates);
        if (!chosen) {
          return;
        }
      } else if (candidates.length === 1) {
        chosen = candidates[0];
      }
      if (chosen) {
        barcodeIndex = chosen.index;
        if (barcodeIndex === undefined) {
          catalogItem = chosen.account || null;
        } else {
          lineAccount = chosen.account || null;
        }
      }
    }

    if (barcodeIndex !== undefined) {
      // This item is found on the active purchase
      console.log('barcodeIndex: '+barcodeIndex);
      setTimeout(() => {

        const wasAlreadyActive = this.activeItem && this.activeItem.index === barcodeIndex;

        // A decoded scale label carries its own quantity: add that and skip
        // the type's re-scan increment for this scan.
        if (decoded) {
          try {
            this.applyDecodedToLine(this.purchase.items[barcodeIndex], decoded, lineAccount, wasAlreadyActive);
          } catch (e) {
            console.log('ERROR: ', e);
          }
          if (!wasAlreadyActive) {
            this.setActiveItem(barcodeIndex);
            if (window.innerWidth >= 700) {
              this.scrollToIndexRobust(barcodeIndex);
            }
          }
          return;
        }

        // Re-scan of a line already on the transfer: addUponScan types count
        // every scan (first scan included, handled below); otherwise the
        // type's rescanAddQty adds that many to the step's input field — the
        // first scan still takes the step's default qty. Both flags set:
        // addUponScan wins so a re-scan is never counted twice.
        const rescanQty = this.purchase.typeObject.addUponScan ? 0 : this.rescanAddQty();

        try {
          if (this.purchase.typeObject.addUponScan) {
            if (this.purchase.typeObject.addUponScan === true) {
              this.purchase.typeObject.addUponScan = 1;
            }
            let item = this.purchase.items[barcodeIndex];
            if (!wasAlreadyActive) {
              this.recordScanUndo(item);
              item.firstCountScan = !item.counted;
              item.scanAdded = this.purchase.typeObject.addUponScan;
            }
            this.changePurchaseItemValue(item, this.purchase.typeObject.addUponScan);
            this.deferLineSave(item);
          } else if (rescanQty && this.canEdit() && this.scanTargetFieldEditable()) {
            const item = this.purchase.items[barcodeIndex];
            if (!wasAlreadyActive) {
              this.recordScanUndo(item);
              // This scan is what counts the line: the panel shows its
              // value plainly instead of "previous + 0". On a line counted
              // earlier the scan's increment is the panel's "new" part.
              item.firstCountScan = !item.counted;
              item.scanAdded = rescanQty;
            }
            this.changePurchaseItemValue(item, rescanQty);
            this.deferLineSave(item);
          }
        } catch(e) {
          console.log('ERROR: ', e);
        }

        // Types with neither flag: a re-scan of the already-selected line on
        // mobile used to add one through setActiveItem's tap-the-active-row
        // side effect. Taps no longer count, so keep that legacy scan
        // behavior explicitly (desktop never had it).
        if (wasAlreadyActive && !this.purchase.typeObject.addUponScan && !rescanQty
          && window.innerWidth < 700 && this.activeFieldEditable() && !this.purchase.searchText) {
          try {
            this.changePurchaseItemValue(this.activeItem, 1);
          } catch (e) {
            console.log('ERROR: ', e);
          }
        }
        if (wasAlreadyActive) {
          return;
        }

        this.setActiveItem(barcodeIndex);

        if (window.innerWidth >= 700) {
          // this.viewPort.scrollToIndex(barcodeIndex);
          this.scrollToIndexRobust(barcodeIndex);
        }

      }, 0);
    } else if (catalogItem) {
      // A decoded label adds the line at the decoded quantity (no first-scan
      // increment); a label whose price cannot be converted adds at the
      // step's default so the item is at least on the transfer.
      let decodedResult = null;
      if (decoded) {
        decodedResult = this.decodedScanQty(decoded, catalogItem, null);
        if (!decodedResult) {
          AppComponent.showSnack('No store price to convert the label price for ' + catalogItem.name, 'OK', 4000);
        }
      }
      const newItem = await this.addPurchaseItem(catalogItem, true, false, decodedResult ? decodedResult.qty : 0, 'scan');
      setTimeout(() => {
        if (!newItem) {
          return;
        }
        barcodeIndex = newItem.index;
        if (decoded) {
          // Show the label's exact retail units in the unit column (the
          // stored cases round-trip within the column's tolerance).
          const unitField = decodedResult && decodedResult.vendor ? this.scanUnitField() : null;
          if (unitField) {
            newItem[unitField.key] = this.numberRound(decodedResult.units, 4);
          }
          this.setActiveItem(barcodeIndex);
          if (decodedResult) {
            AppComponent.showSnack(this.decodedScanMessage(decodedResult, newItem), 'OK', 2500);
          }
          return;
        }
        if (this.purchase.typeObject.addUponScan) {
          this.recordScanUndo(newItem);
        }
        // The scan that adds a line is its first entry: the panel shows the
        // step's default quantity plainly, not as "default + 0".
        newItem.scanCreated = true;
        this.setActiveItem(barcodeIndex);

        // addUponScan types: the FIRST scan counts as one unit too —
        // previously only re-scans incremented, so a fresh scan sat at
        // qty 0. Applied through the step's input target field (same as
        // the re-scan path) so e.g. Qty to Remove gets the right sign;
        // types without addUponScan are unchanged.
        try {
          if (this.purchase.typeObject.addUponScan) {
            if (this.purchase.typeObject.addUponScan === true) {
              this.purchase.typeObject.addUponScan = 1;
            }
            const scannedItem = this.purchase.items[barcodeIndex];
            this.changePurchaseItemValue(scannedItem, this.purchase.typeObject.addUponScan);
            this.deferLineSave(scannedItem);
          }
        } catch (e) {
          console.log('ERROR: ', e);
        }
      }, 10);
    } else if (canAdd) {
      // A blocking alert() would swallow the next scan's keystrokes on
      // handheld scanners — notify without interrupting the scan flow.
      const note = this.cacheMode() && this.lastCacheLookupNote ? ' (' + this.lastCacheLookupNote + ')' : '';
      AppComponent.showSnack('Item not found: ' + barcode + (decoded && decoded.raw !== barcode ? ' (label ' + decoded.raw + ')' : '') + note, 'OK', 5000);
    } else if (!this.isLocked()) {
      this.purchase.missingBarcode = barcode;
    }
  }

  // "Which item?" prompt for an ambiguous scan. Lines already on the transfer
  // are labeled so "select that line" reads differently from "add a new one".
  // The dialog takes focus off the body, so scanner keystrokes while it is up
  // never reach the barcode buffer (and Enter cannot press a button).
  scanChooserOpen = false;

  chooseScanMatch = async (barcode, candidates: any[]) => {
    // The scan overlay would sit on top of the prompt.
    LoadingComponent.hideLoading();

    const config = new DialogConfig();
    config.title = 'Which Item?';
    config.message = `More than one item has barcode ${barcode}.`;
    config.width = '500px';
    config.stackOptions = true;
    config.autoFocus = 'dialog';
    for (const candidate of candidates) {
      config.options.push(new DialogOption(this.scanMatchLabel(candidate), candidate, DialogButtonStyle.Normal));
    }
    config.options.push(new DialogOption('Cancel', null, DialogButtonStyle.Cancel));

    this.scanChooserOpen = true;
    try {
      const result: DialogOption = await AppComponent.showDialog(config).afterClosed().toPromise();
      return result ? result.value : null;
    } finally {
      this.scanChooserOpen = false;
    }
  }

  scanMatchLabel(candidate): string {
    const parts = this.searchResultFields()
      .map(col => this.searchResultValue(candidate.item, col.path))
      .filter(value => value !== undefined && value !== null && String(value).trim() !== '')
      .map(value => String(value));
    const label = parts.length > 0 ? parts.join(' · ') : (candidate.item.name || 'Item');
    return candidate.index !== undefined ? `${label} (on transfer)` : label;
  }

  fieldTab = () => {
    // console.log('FIELD TAB');
    // console.log('activeItemIndex: ', this.activeItem.index);

    this.confirmChange(this.activeItem);

    // Advance through the SORTED view, not the stored array order.
    const displayPosition = this.displayItems.indexOf(this.activeItem);
    const nextItem = displayPosition !== -1 ? this.displayItems[displayPosition + 1] : null;
    if (nextItem) {
      this.setActiveItem(nextItem.index);
    }
    
    setTimeout(() => {
      const targetInput = document.getElementById('item-'+this.activeItem.index+'-'+this.purchase.inputTargetField) as HTMLInputElement;
      targetInput.focus();
      targetInput.select();
    }, 0);

  }

  saveButton = () => {
    if (this.activeItem) {
     this.confirmChange(this.activeItem);
     this.activeItem = null;
    }
  }

  // Opening a line in the phone panel.
  // The first keypad digit REPLACES the value on screen (later digits append):
  // `edited` is what screenBtnPress appends on, and a line left mid-edit
  // (digits typed, then another line scanned without auto-save) still carried
  // it, so "10 + 1" then 5 became "10 + 15".
  // A line counted earlier opens as "previous + new", where previous is the
  // count before the opening scan and new is what that scan added (10 saved,
  // re-scan → "10 + 1"; a tap → "10 + 0"). The scan that first counts a line
  // just shows its value. Applied whether or not another line was active (the
  // switch path used to skip it).
  private leavePreviousCountMode(item) {
    if (!item) {
      return;
    }
    delete item.changeCountMode;
    delete item.addModeSplit;
    if (item.previousCount === undefined) {
      return;
    }
    delete item.previousCount;
    delete item.previousCountField;
    this.updateLineCalculations(item);
  }

  // ---- phone panel "previous + new" entry for a line entered earlier -------
  // Add to Count / Add to Qty: "previous + new" (the default on open; digits
  // edit the new part). Change Count / Change Qty: the whole total (digits
  // replace it, C zeroes it). Tapping the "10 +" prefix switches to the whole
  // total; the link switches back, restoring the original split when the
  // total is unchanged, otherwise "total + 0".
  //
  // Works on whichever editable quantity the handheld is pointed at
  // (purchase.inputTargetField) on every stepped type, so the same gesture
  // serves counts, removals, requests, picks, receipts and vendor units:
  //   - the counted quantity (targetQtyCalculated) carries the split inside
  //     the line math (calculateLine subtracts previousCount), so the field
  //     itself holds the new part;
  //   - every other field keeps its stored total and panelValue() subtracts
  //     the previous part for display. Typed digits still flow through the
  //     delta path (change = new - shown), so the total lands at
  //     previous + new, and saves send totals exactly as before (per-device
  //     shares in Local Inventory Cache mode included).
  // The remaining-at-source column is never split: it is a remainder, not an
  // entry, and no step edits it from the handheld.
  private splitIsCalculated(fieldKey: string): boolean {
    return fieldKey === 'targetQtyCalculated';
  }

  private splitEligibleField(fieldKey: string): boolean {
    return !!fieldKey && fieldKey !== 'sourceQtyCalculated' && fieldKey !== 'differenceQty' && fieldKey !== 'lineCost';
  }

  // What the panel shows for a field: the new part while a split is open on
  // that field, else the field's value.
  panelValue(item, field): number {
    if (!item || !field) {
      return 0;
    }
    const value = Number(item[field.key]) || 0;
    if (item.previousCount !== undefined && field.key === this.purchase.inputTargetField && !this.splitIsCalculated(field.key)) {
      return this.numberRound(value - (Number(item.previousCount) || 0), 4);
    }
    return value;
  }

  // The field's whole quantity (previous + new) regardless of the split.
  private panelTotal(item, fieldKey: string): number {
    const value = Number(item[fieldKey]) || 0;
    if (item.previousCount !== undefined && this.splitIsCalculated(fieldKey)) {
      return this.numberRound(value + (Number(item.previousCount) || 0), 4);
    }
    return value;
  }

  // Wording follows the field: counts on counting steps, quantities elsewhere.
  splitLabels(): { add: string; change: string; hint: string } {
    const counting = this.purchase?.inputTargetField === 'targetQtyCalculated';
    return counting
      ? { add: 'Add to Count', change: 'Change Count', hint: 'Tap to change the whole count' }
      : { add: 'Add to Qty', change: 'Change Qty', hint: 'Tap to change the whole quantity' };
  }

  showSplitControls(field): boolean {
    return !!field && !!this.purchase && field.key === this.purchase.inputTargetField && this.splitEligibleField(field.key);
  }

  // A line has something to add to: a count on counting steps, otherwise any
  // non-zero entry (a Min/Max request, an earlier scan, an imported line).
  private hasPriorEntry(item, fieldKey: string): boolean {
    if (!item) {
      return false;
    }
    if (this.isCountStep() || this.splitIsCalculated(fieldKey)) {
      return item.counted === true;
    }
    return item.counted === true || item.countedByMe === true || (Number(item[fieldKey]) || 0) !== 0;
  }

  inChangeCountMode(item): boolean {
    return !!item && item.changeCountMode === true;
  }

  enterChangeCountMode(event = null) {
    if (event) {
      event.stopPropagation();   // the tile's own tap would add one
    }
    const item = this.activeItem;
    const fieldKey = this.purchase.inputTargetField;
    if (!item || item.previousCount === undefined) {
      return;
    }
    const previous = Number(item.previousCount) || 0;
    const added = this.panelValue(item, { key: fieldKey });
    item.addModeSplit = { previous, added, total: this.numberRound(previous + added, 4) };
    delete item.previousCount;
    delete item.previousCountField;
    delete item.edited;        // the first digit replaces the total
    item.changeCountMode = true;
    this.updateLineCalculations(item);
  }

  enterAddCountMode(event = null) {
    if (event) {
      event.stopPropagation();
    }
    const item = this.activeItem;
    const fieldKey = this.purchase.inputTargetField;
    if (!item || !item.changeCountMode) {
      return;
    }
    const total = this.panelTotal(item, fieldKey);
    const split = item.addModeSplit;
    if (split && split.total === total) {
      item.previousCount = split.previous;
      item.previousCountField = fieldKey;
      if (this.splitIsCalculated(fieldKey)) {
        item[fieldKey] = split.added;
      }
    } else {
      item.previousCount = total;
      item.previousCountField = fieldKey;
      if (this.splitIsCalculated(fieldKey)) {
        item[fieldKey] = 0;
      }
    }
    delete item.addModeSplit;
    delete item.changeCountMode;
    delete item.edited;        // the first digit replaces the new part
  }

  private preparePhonePanel(item, firstCountScan: boolean, scanAdded: number, scanCreated = false) {
    if (!item) {
      return;
    }
    delete item.edited;
    delete item.changeCountMode;
    delete item.addModeSplit;
    const fieldKey = this.purchase.inputTargetField;
    if (!this.splitEligibleField(fieldKey) || !this.scanTargetFieldEditable()) {
      return;
    }
    const previous = this.numberRound(this.panelTotal(item, fieldKey) - scanAdded, 4);
    // Counting steps split only a line counted earlier (firstCountScan is the
    // scan that first counts it). Elsewhere "earlier" means the line already
    // held a quantity before this scan: a Min/Max request, an import, a
    // previous scan.
    const countingStyle = this.isCountStep() || this.splitIsCalculated(fieldKey);
    const hadEntry = countingStyle ? (!firstCountScan && this.hasPriorEntry(item, fieldKey)) : (!scanCreated && previous !== 0);
    if (!hadEntry) {
      return;
    }
    item.previousCount = previous;
    item.previousCountField = fieldKey;
    if (this.splitIsCalculated(fieldKey)) {
      item[fieldKey] = scanAdded;
    }
  }

  setActiveItem = async (itemIndex) => {
    if (this.activeItem && itemIndex !== this.activeItem.index) {
      // Leaving a line pushes its deferred scans; the line being opened keeps
      // its own deferral (its opening scan saves 2 s later like any re-scan,
      // with whatever is typed meanwhile), instead of saving the instant the
      // panel opens.
      this.flushDeferredSaves(this.purchase.items[itemIndex]);
    }
    if (!this.isLocked()) {
      let item = this.purchase.items[itemIndex];

      // A scan's deferred setActiveItem can race a purchase reload (the barcode
      // index map is rebuilt and the looked-up index may be gone) — bail instead
      // of crashing on an undefined item.
      if (!item) {
        console.log('setActiveItem: no item at index', itemIndex);
        return;
      }
      // Set by the scan opening this line (consumed by this open only): whether
      // it is the line's first count, and how much the scan itself added.
      const firstCountScan = item.firstCountScan === true;
      const scanAdded = Number(item.scanAdded) || 0;
      const scanCreated = item.scanCreated === true;
      delete item.firstCountScan;
      delete item.scanAdded;
      delete item.scanCreated;

      if (window.innerWidth < 700) {
        // Mobile: a tap only SELECTS the item (info panel, tiles), whatever
        // the step allows — steps with no editable column (Ready to Pick,
        // Ready for Transit, ...) used to swallow the tap entirely. Taps never
        // change a quantity; only scans do (processBarcode).
        {
          if (this.activeItem) {
            if (itemIndex === this.activeItem.index) {
              // Already selected — nothing to do.
              return;
            } else {
              // An item is already active
              const previousActive = this.activeItem;

              // Tapping away from an unsaved edit: save it first (config-gated).
              await this.autoSavePendingEdit();
              // The line we are leaving must not stay in "previous + new"
              // mode: calculateLine keeps subtracting previousCount, so its
              // row would show only the "new" part and a later open would
              // start from that stale split (cache mode: the deferred flush
              // clears `edited`, so autoSavePendingEdit — which clears it —
              // may not run).
              this.leavePreviousCountMode(previousActive);

              // this.viewPort.scrollToIndex(itemIndex);
              this.scrollToIndexRobust(itemIndex);

              // Load Purchase Updates for this item only
              // The line's latest server copy merges in when it arrives
              // (a round trip must not delay opening the panel).
              this.loadPurchaseUpdates(null, [itemIndex]);

              let activeItem = this.purchase.items[itemIndex];
              this.preparePhonePanel(activeItem, firstCountScan, scanAdded, scanCreated);
              this.activeItem = activeItem;
              this.captureActiveItemBaseline();
              this.refreshActiveItemInfo();
              console.log('activeItem: ', this.activeItem);
            }
      
          } else {

// console.log('Not active item');



            this.scrollToIndexRobust(itemIndex);


            this.preparePhonePanel(item, firstCountScan, scanAdded, scanCreated);

            this.activeItem = item;
            this.captureActiveItemBaseline();
            this.refreshActiveItemInfo();
            console.log('activeItem: ', this.activeItem);
          }
        }

      } else {
        if (this.activeItem == item) {
          // console.log('this is already the active item');
        } else {
          // console.log('this is NOW the active item');
          this.activeItem = item;
          this.captureActiveItemBaseline();
        }
      }
    }

    return;
  }

  scrollToIndexRobust(index) {
    // `index` is the item's CANONICAL index; the viewport renders the (possibly
    // sorted) displayItems view with discrepancy-hidden rows collapsed —
    // translate to the row's visible position before scrolling.
    const rowVisible = (item) => this.itemVisible(item);
    let target = 0;
    let found = false;
    for (const item of this.displayItems) {
      if (item.index === index) {
        found = true;
        break;
      }
      if (rowVisible(item)) {
        target++;
      }
    }
    if (!found) {
      target = index;
    }

    this.viewPort.scrollToIndex(target);

    setTimeout(() => {
      this.viewPort.scrollToIndex(target);
    }, 0);
  }

  qtyChange(newValue) {
    let change = newValue - this.purchase.inputTargetStartValue;

    // console.log('purchase.typeObject: ', this.purchase.typeObject);



    // console.log('startValue: ', this.purchase.inputTargetStartValue);
    // console.log('newValue: ', newValue);
    // console.log('change: ', change);

    this.purchase.inputTargetStartValue = newValue;

    switch (this.purchase.inputTargetField) {
      case 'sourceQtyCalculated':
        this.activeItem.qty -= change;
        break;
      
      case 'targetQtyCalculated':
        this.activeItem.qty += change;
        break;

      case 'qtyRemove':
        this.activeItem.qty -= change;
        break;
      
      case 'expectedQty':

        break;
    }




    this.itemQtyChange(this.activeItem, this.purchase.inputTargetField);
  }

  // Largest quantity a line may carry; anything bigger is a barcode that landed
  // in the count field (the server refuses the same, key quantityTooLarge).
  static readonly MAX_LINE_QUANTITY = 1000000;

  // Reverts a line's editable quantity to the last value the server holds.
  private revertLineQuantity(item) {
    if (!item) { return; }
    if (item.serverQty !== undefined) {
      item.qty = item.serverQty;
    }
    item.edited = false;
    if (this.activeItem === item) {
      this.purchase.inputTargetStartValue = item[this.purchase.inputTargetField];
    }
    this.updateCalculations();
    if (this.activeItem === item) {
      this.purchase.inputTargetStartValue = item[this.purchase.inputTargetField];
    }
  }

  confirmChange = async (item, markCounted = true) => {
    console.log('Save change!');

    // A quantity in the millions is a scanned barcode, not a count: put the
    // line back the way it was and say so instead of saving it.
    const typed = this.activeItem === item ? item[this.purchase.inputTargetField] : item.qty;
    const oversized = [item.qty, typed].find(value => typeof value === 'number' && Math.abs(value) > TransferComponent.MAX_LINE_QUANTITY);
    if (oversized !== undefined) {
      this.revertLineQuantity(item);
      AppComponent.showSnack('"' + Math.round(oversized) + '" looks like a scanned barcode, not a count. The line was not changed.', 'OK', 6000);
      return;
    }

    // Commit a unit-column entry: apply whole-vendor-unit rounding (if the
    // type asks for it) and re-derive before the line is saved.
    if (this.isUnitField(this.purchase.inputTargetField)) {
      this.applyUnitEntry(item, this.purchase.inputTargetField, true);
      this.updateCalculations();
    }

    // Desktop count entry: editing a line on a counting step marks it counted
    // (mobile does this in saveItem) — without this the requireCount gate
    // could never be satisfied from a desktop. Skipped when reverting a scan
    // addition, which restores the line's previous counted flag instead.
    if (markCounted && this.hasSteps() && (this.currentStepInfo()?.step?.requireCount || this.purchase.typeObject?.countAsOf)) {
      item.counted = true;
    }
    if (item.counted) {
      this.markCountedByMe(item);
    }

    if (this.cacheMode()) {
      await this.persistLineViaQueue(item);
      return;
    }

    // A save of a line whose add is still in flight waits for the add.
    await this.awaitLineWrites(String(item.accountId));

    // if (item.qty != item.serverQty) {
      this.purchase.itemSaving = true;
      item.saving = true;

      this.purchase.savingUpdates = true;

      try {
        let countUpdate: any = await this.ioService.post('/transfer/updateTransferItem', {
          purchaseId: this.purchase._id,
          editSessionKey: this.purchase.editSessionKey,
          type: this.purchase.type,
          locationId: this.purchase.locationId,
          customerId: this.purchase.customerId,
          itemIndex: item.index,
          item: item,
          expectedAccountId: item.accountId,
          costTotal: this.purchase.costTotal,
        });
        this.adoptSavedLine(item, countUpdate);

        this.purchase.savingUpdates = false;
    
        this.purchase.timeUpdated = new Date(countUpdate.timeUpdated);

        this.autoSyncDelay = this.autoSyncDelayDefault;
        await this.loadPurchaseUpdates(item.index);

        item.saving = false;
        this.purchase.itemSaving = false;
    
        console.log('change saved');
      // } else {
      //   console.log('no change to save');
      // }
      } catch(e) {
        console.log('error saving update: ', e);
        this.purchase.savingUpdates = false;
        item.saving = false;
        this.purchase.itemSaving = false;
        if (e?.error?.key === 'quantityTooLarge') {
          this.revertLineQuantity(item);
          AppComponent.showSnack(e.error.msg || 'That quantity looks like a scanned barcode. The line was not changed.', 'OK', 6000);
          return;
        }
        if (e?.error?.key === 'itemIndexMismatch') {
          AppComponent.showSnack('This transfer changed while you were editing — the list has been reloaded.', 'OK', 4000);
        }
        this.loadPurchase();
      }

  }

  // ---- Scanner output typed into a focused count field ----
  // A wedge scanner delivers its characters a few milliseconds apart; a person
  // cannot. When two characters land inside that gap, the first one (already
  // in the field) is pulled back out, the rest of the burst is captured
  // instead of typed, and on the scanner's Enter/Tab (or 150 ms of silence)
  // the field is committed as typed and the code goes to barcodeEntered —
  // the way a scan outside the field behaves. Before this, "3" + a scan of
  // 086012501346 saved a count of 3,086,012,501,346.
  private inputScanGapMs = 40;
  private inputScanActive = false;
  private inputScanBuffer = '';
  private inputScanLastTime = 0;
  private inputScanLastChar: string = null;
  private inputScanTimeout: any = null;

  countInputKeydown(event: KeyboardEvent, item, field) {
    // The event's own timestamp: a busy main thread (a 1,500-line count
    // re-rendering per keystroke) must not stretch the measured gap.
    const now = event.timeStamp || performance.now();
    const gap = now - this.inputScanLastTime;
    this.inputScanLastTime = now;
    const key = event.key;
    const input = event.target as HTMLInputElement;

    if (this.inputScanActive) {
      event.preventDefault();
      if (key === 'Enter' || key === 'Tab') {
        this.finishInputScan(input);
      } else if (key.length === 1) {
        this.inputScanBuffer += key;
        this.armInputScanTimeout(input);
      }
      return;
    }

    if (key.length === 1 && this.inputScanLastChar !== null && gap < this.inputScanGapMs) {
      // Second character inside a scanner's gap: a scan is running.
      event.preventDefault();
      this.inputScanActive = true;
      this.inputScanBuffer = this.inputScanLastChar + key;
      // Take the scan's first character back out of the field through the
      // normal input path, so ngModel → qtyChange re-derives the line exactly
      // as a backspace would.
      const value = input.value === null || input.value === undefined ? '' : String(input.value);
      if (value.endsWith(this.inputScanLastChar)) {
        input.value = value.slice(0, -1);
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      this.armInputScanTimeout(input);
      return;
    }

    this.inputScanLastChar = key.length === 1 ? key : null;
  }

  private armInputScanTimeout(input: HTMLInputElement) {
    if (this.inputScanTimeout) {
      clearTimeout(this.inputScanTimeout);
    }
    this.inputScanTimeout = setTimeout(() => this.finishInputScan(input), 150);
  }

  private finishInputScan(input: HTMLInputElement) {
    if (this.inputScanTimeout) {
      clearTimeout(this.inputScanTimeout);
      this.inputScanTimeout = null;
    }
    const code = this.inputScanBuffer;
    this.inputScanActive = false;
    this.inputScanBuffer = '';
    this.inputScanLastChar = null;
    if (input && typeof input.blur === 'function') {
      input.blur();   // commits the typed count through (blur) → confirmChange
    }
    if (code.length > 0) {
      setTimeout(() => this.barcodeEntered(code), 0);
    }
  }

  setInputTargetField(fieldKey, editable = true, event = null) {
    console.log('setInputTargetField: ', fieldKey);
    if (editable) {
      if (fieldKey == this.purchase.inputTargetField && window.innerWidth < 700) {
        // this.activeItem[this.purchase.inputTargetField]++;
       this.changePurchaseItemValue(this.activeItem, 1);
      } else {
        // Switching the field the handheld is pointed at: the "previous + new"
        // split belongs to the field it was opened on (a case scan's "6 + 1"
        // must not become "6 + 36" on the units tile), so collapse it and show
        // the new field's whole total; the next scan opens a fresh split there.
        this.leavePreviousCountMode(this.activeItem);
        this.purchase.inputTargetField = fieldKey;
        this.purchase.inputTargetStartValue = this.activeItem[fieldKey];
      }

      // Select the existing value on focus so typing REPLACES it (entering
      // "13" over a 10 gives 13, not 1310). The start value is captured above,
      // before any keystroke, so delta-based fields still compute correctly.
      const inputTarget: any = event && event.target;
      if (inputTarget && typeof inputTarget.select === 'function') {
        setTimeout(() => inputTarget.select(), 0);
      }
    }
  }

  // Touchscreen stepper on the active field tile: +/- adjust through the
  // input target field (correct sign for derived fields like Qty to Remove);
  // marks the line edited so Save Change / auto-save-on-scan persists it.
  stepActiveValue(delta: number) {
    if (!this.activeItem || !this.activeFieldEditable()) { return; }
    // Physical counts never go below zero: on reconcile/count-gated steps
    // (receiving now starts at 0) a stray − tap must not record a negative
    // quantity. Types without those gates (e.g. corrections) keep full range.
    if (delta < 0) {
      const current = this.currentStepInfo();
      if (current && (current.step.requireReconcile === true || current.step.requireCount === true)
        && (this.activeItem[this.purchase.inputTargetField] || 0) <= 0) {
        return;
      }
    }
    // The split's "new" part never goes negative: taking away more than this
    // panel added is an edit of the whole total, so switch to Change mode
    // first ("1 + -1" was the right total, wrongly split).
    if (delta < 0 && this.activeItem.previousCount !== undefined
      && this.panelValue(this.activeItem, { key: this.purchase.inputTargetField }) + delta < 0) {
      this.enterChangeCountMode();
    }
    this.changePurchaseItemValue(this.activeItem, delta);
    this.activeItem.edited = true;
  }

  // Snapshot of the active item's editable state taken the moment it was
  // selected — Reset restores to this, not to zero. qty is the backbone
  // (derived display fields recompute from it); step-defined direct-edit
  // fields are captured alongside.
  private activeItemBaseline: any = null;

  captureActiveItemBaseline() {
    // The selected line is always listed, whatever the filters.
    this.refreshDisplayItems();
    if (!this.activeItem) {
      this.activeItemBaseline = null;
      this.scanSelectionUndo = null;
      return;
    }
    this.activeItemBaseline = { index: this.activeItem.index, values: this.editableValuesSnapshot(this.activeItem) };

    // Adopt the scan-time snapshot for THIS line (see recordScanUndo);
    // selecting any other line drops it.
    this.scanSelectionUndo = this.pendingScanUndo && this.pendingScanUndo.index === this.activeItem.index
      ? this.pendingScanUndo : null;
    this.pendingScanUndo = null;
  }

  private editableValuesSnapshot(item): any {
    const values: any = { qty: item.qty };
    for (const field of this.getStatusFields(this.purchase.typeObject, this.purchase.status)) {
      if (field.editable && !field.unitOf
        && ['qty', 'sourceQtyCalculated', 'targetQtyCalculated', 'differenceQty', 'lineCost', 'qtyRemove'].indexOf(field.key) === -1) {
        values[field.key] = item[field.key];
      }
    }
    return values;
  }

  // Handheld scan of a line that is NOT selected can add to it (rescanAddQty /
  // addUponScan) and save before the panel opens. Remember the line as it was
  // just before that addition so Discard Change can take the addition back
  // out; the snapshot is held until setActiveItem adopts it for the same line.
  // Reset is untouched (it restores the panel-open state, addition included).
  private pendingScanUndo: any = null;
  private scanSelectionUndo: any = null;

  recordScanUndo(item) {
    if (window.innerWidth >= 700 || !item) {
      return;
    }
    this.pendingScanUndo = {
      index: item.index,
      values: this.editableValuesSnapshot(item),
      counted: item.counted === undefined ? null : item.counted,
      // The scan's save also stamps who counted and when; a discard must
      // take the stamps back with the count or the row keeps showing a
      // "Counted By" on a line that is not counted.
      stamps: TransferComponent.COUNT_STAMP_KEYS.reduce((acc, key) => { acc[key] = item[key]; return acc; }, {}),
    };
  }

  private static readonly COUNT_STAMP_KEYS = ['countedTime', 'countedUserName', 'countedUserId', 'countedByMe'];

  // Discard Change on a scan-selected line: restore the pre-scan values
  // (and counted flag) and persist them, since the scan's addition was saved.
  async undoScanAddition() {
    const undo = this.scanSelectionUndo;
    this.scanSelectionUndo = null;
    if (!undo || !this.activeItem || undo.index !== this.activeItem.index) {
      return;
    }
    for (const key of Object.keys(undo.values)) {
      this.activeItem[key] = undo.values[key];
    }
    // The API clears who-counted-when only on an explicit un-count
    // (counted === false); a null would leave the scan's stamp behind.
    this.activeItem.counted = undo.counted ? true : (this.activeItem.counted ? false : undo.counted);
    for (const key of TransferComponent.COUNT_STAMP_KEYS) {
      const was = undo.stamps ? undo.stamps[key] : undefined;
      if (was === undefined) {
        delete this.activeItem[key];
      } else {
        this.activeItem[key] = was;
      }
    }
    this.updateCalculations();
    await this.confirmChange(this.activeItem, false);
  }

  // Reset the active line to the values it had when it was selected.
  resetActiveValue() {
    if (!this.activeItem || !this.activeFieldEditable()) { return; }
    if (!this.activeItemBaseline || this.activeItemBaseline.index !== this.activeItem.index) { return; }
    for (const key of Object.keys(this.activeItemBaseline.values)) {
      this.activeItem[key] = this.activeItemBaseline.values[key];
    }
    this.updateCalculations();
    this.activeItem.edited = true;
  }

  // Keypad digits apply while a save of this line is in flight too: the
  // reply never overwrites the line's quantity (adoptSavedLine takes stamps
  // and shares only, the follow-up poll omits this line, and cache-mode merges
  // keep the unsaved delta), so digits typed right after a scan used to be
  // dropped for nothing. Save Change (or the next deferred flush) carries them.
  screenBtnPress(value) {
    if (!this.activeFieldEditable()) { return; }
    {

      if (this.activeItem[this.purchase.inputTargetField] == undefined) {
        this.activeItem[this.purchase.inputTargetField] = 0;
      }

      // Digits edit what the panel shows: the new part while a split is open
      // ("previous + new"), else the whole value. The delta below then moves
      // the stored total by the same amount.
      let targetField = this.panelValue(this.activeItem, { key: this.purchase.inputTargetField });
      let targetStartValue = targetField;

      console.log('targetFieldKey: ', this.purchase.inputTargetField);
      console.log('targetFieldValue: ', targetField);

      let stringValue = targetField.toString();


      if (Number.isInteger(value)) {
        if (stringValue == "0") {
          stringValue = "";
        }


        if (this.activeItem.edited) {
          stringValue = stringValue+value;
        } else {
          stringValue = value;
          this.activeItem.edited = true;
        }

        
      } else {
        switch (value) {
          case "d":
            stringValue = stringValue.slice(0, -1);
            break;
  
          case "c":
            stringValue = "0";
            break;
        }
      }

      if (stringValue == "") {
        stringValue = "0";
      }


      let targetNewValue = parseInt(stringValue);

      console.log('targetStartValue: ', targetStartValue);
      console.log('targetNewValue: ', targetNewValue);
      let change = targetNewValue - targetStartValue;
      console.log('change: ', change);

      this.changePurchaseItemValue(this.activeItem, change);
    }
  }

  buildUpdateQueue() {

  }

  changePurchaseItemValue(item, change) {
    console.log('changePurchaseItemValue: ', change);

    const fieldKey = this.purchase.inputTargetField;

    if (this.hasSteps() && !['sourceQtyCalculated', 'targetQtyCalculated', 'differenceQty', 'lineCost', 'qtyRemove'].includes(fieldKey)) {
      // Step-defined columns are real item fields — edit them directly. The
      // derived legacy columns above still back-compute through qty.
      item[fieldKey] = this.numberRound((item[fieldKey] || 0) + change, 4);
    } else {
      switch (fieldKey) {
        case 'sourceQtyCalculated':
          item.qty = this.numberRound(item.qty - change, 4);
          break;

        case 'qty':
          item.qty = this.numberRound(item.qty + change, 4);
          break;

        case 'targetQtyCalculated':
          item.qty = this.numberRound(item.qty + change, 4);
          break;

        case 'qtyRemove':
          item.qty = this.numberRound(item.qty - change, 4);
          break;

        case 'expectedQty':

          break;
      }
    }

    this.itemQtyChange(this.activeItem, fieldKey, item);
  }

  cancel = async (markUncounted = false) => {
    // Discard Change: unsaved scans of this line are dropped with it (the
    // reload below restores the server's count).
    this.cancelDeferredSave(this.activeItem);
    this.purchase.loading = true;

    delete this.activeItem.edited;
    delete this.activeItem.previousCount;
    delete this.activeItem.previousCountField;
    delete this.activeItem.changeCountMode;
    delete this.activeItem.addModeSplit;

    // Handheld: the scan that opened this panel may already have added to the
    // line and saved it — Discard takes that addition back out.
    await this.undoScanAddition();

    // Refresh this item
    await this.loadPurchaseUpdates(null, [this.activeItem.index]);

    try {


      //      await this.saveItem(!markUncounted);
      this.activeItem = null;


      this.purchase.loading = false;
      return;
    } catch(e) {
      console.log('item not found. reload purchase.');
      this.loadPurchase();
    }

  }

  saveItem = async (counted = true) => {
    let activeItem = this.activeItem;
    // Save Change sends the line itself; a deferred scan save would be a duplicate.
    this.cancelDeferredSave(activeItem);

    if (this.isUnitField(this.purchase.inputTargetField)) {
      this.applyUnitEntry(activeItem, this.purchase.inputTargetField, true);
      this.updateCalculations();
    }

    activeItem.saving = true;

    delete this.activeItem.edited;
    // Save Change keeps whatever the opening scan added.
    this.scanSelectionUndo = null;

    if (this.purchase.status == 'pending'
      || (this.hasSteps() && this.currentStepInfo() && this.currentStepInfo().step.requireCount)
      || (this.hasSteps() && this.purchase.typeObject?.countAsOf)) {
      activeItem.counted = counted;
      if (counted) {
        this.markCountedByMe(activeItem);
      }
    }

    if (activeItem.previousCount) {
      delete activeItem.previousCount;
      delete activeItem.previousCountField;
    }
    delete activeItem.changeCountMode;
    delete activeItem.addModeSplit;

    if (this.cacheMode()) {
      await this.persistLineViaQueue(activeItem);
      activeItem.saving = false;
      this.activeItem = null;
      return;
    }

    try {

      this.purchase.savingUpdates = true;

      // Send Update to Server
      let countUpdate: any = await this.ioService.post('/transfer/updateTransferItem', {
        purchaseId: this.purchase._id,
        editSessionKey: this.purchase.editSessionKey,
        type: this.purchase.type,
        locationId: this.purchase.locationId,
        customerId: this.purchase.customerId,
        itemIndex: activeItem.index,
        item: activeItem,
        expectedAccountId: activeItem.accountId,
        costTotal: this.purchase.costTotal,
      });
      this.adoptSavedLine(activeItem, countUpdate);

      this.updateCalculations();


      this.purchase.savingUpdates = false;

      activeItem.saving = false;

      this.loadPurchaseUpdates(null);

      this.activeItem = null;
      return;
    } catch(e) {
      console.log('error saving update: ', e);
      if (e?.error?.key === 'itemIndexMismatch') {
        AppComponent.showSnack('This transfer changed while you were editing — the list has been reloaded.', 'OK', 4000);
      }
      this.loadPurchase();
    }

  }

  // Update polling: paused while the tab is hidden, stopped once the transfer is gone
  pollPausedHidden = false;
  pollStopped = false;

  @HostListener('document:visibilitychange')
  onVisibilityChange() {
    if (document.hidden) {
      // Screen off / app switched: do not sit on an unsaved count.
      this.flushDeferredSaves();
    }
    if (!this.purchase || !this.purchase._id || this.pollStopped) {
      return;
    }
    if (document.hidden) {
      if (this.autoSyncTimeout) {
        clearTimeout(this.autoSyncTimeout);
        this.autoSyncTimeout = null;
      }
      this.pollPausedHidden = true;
    } else if (this.pollPausedHidden) {
      this.pollPausedHidden = false;
      this.autoSyncDelay = this.autoSyncDelayDefault;
      if (!this.isLocked()) {
        this.loadPurchaseUpdates(null);
      }
    }
  }

  loadPurchaseUpdates = async (omitIndex, includeIndexes = []) => {
    if (this.autoSyncTimeout) {
      clearTimeout(this.autoSyncTimeout);
    }

    // The server already said this transfer is gone: nothing to fetch.
    if (this.unavailable) {
      return;
    }

    // The Bulk Add wizard pauses update syncing: applying remote item changes
    // (or the index-mismatch full reload below) replaces purchase state and
    // dismisses the open modal. The timer keeps ticking (without escalating
    // the delay) so syncing resumes as soon as the wizard closes.
    if (this.purchase.bulkAdd) {
      if (!this.isLocked()) {
        this.autoSyncTimeout = setTimeout(() => {
          this.loadPurchaseUpdates(null);
        }, this.autoSyncDelay * 1000);
      }
      return;
    }

    // A hidden tab does not poll; visibilitychange resumes it (see onVisibilityChange).
    if (typeof document !== 'undefined' && document.hidden) {
      this.pollPausedHidden = true;
      return;
    }

    // Offline with the cache on: nothing to fetch; the reconnect resumes polling.
    if (this.cacheMode() && !this.connectivityService.isOnline()) {
      return;
    }

    let purchaseUpdatesResponse;

    if (!this.purchase.savingUpdates) {

      try {
        purchaseUpdatesResponse = await this.ioService.post('/transfer/getTransferUpdates', {
          purchaseId: this.purchase._id,
          editSessionKey: this.purchase.editSessionKey,
          lastRetrieved: this.purchase.lastRetrieved,
          omitIndex,
          includeIndexes,
        });
      } catch (error) {
        // The transfer was deleted elsewhere (or never existed here): stop
        // polling and scanning for good, and say which it was.
        if (this.isUnavailableError(error)) {
          const why = this.unavailableFromError(error);
          this.markUnavailable(why.status, why.detail);
          return;
        }
        // Anything else (timeout, offline): keep the loop alive at the backed-off delay.
        purchaseUpdatesResponse = null;
      }

      // Change-token short-circuit: nothing changed server-side. Only the
      // retrieval stamp moves forward.
      if (purchaseUpdatesResponse && purchaseUpdatesResponse.unchanged) {
        this.purchase.lastRetrieved = purchaseUpdatesResponse.lastRetrieved;
      }

    } else {
      console.log('!!! do not load purchase updates while saving updates');
    }


    if (this.autoSyncTimeout) {
      clearTimeout(this.autoSyncTimeout);
    }

    // Prevent autoSync if transfer is complete
    if (!this.isLocked() && !this.pollStopped) {
      this.autoSyncTimeout = setTimeout(() => {
        console.log('setting autoSyncTimeout: ', this.autoSyncDelay+' seconds');
        this.loadPurchaseUpdates(null);
      }, this.autoSyncDelay*1000);
      this.autoSyncDelay *= this.autoSyncDelayMultiple;
      if (this.autoSyncDelay > this.autoSyncDelayMax) {
        this.autoSyncDelay = this.autoSyncDelayMax;
      }
    }



    // Completing or canceling a non-stepped transfer leaves timeUpdated alone,
    // so an "unchanged" head can still carry a new status.
    if (purchaseUpdatesResponse && purchaseUpdatesResponse.unchanged && !this.purchase.savingUpdates
      && purchaseUpdatesResponse.status && this.purchase.status && purchaseUpdatesResponse.status !== this.purchase.status) {
      console.log('transfer status changed elsewhere (head unchanged). reload purchase.');
      this.loadPurchase();
      return;
    }

    if (purchaseUpdatesResponse && !purchaseUpdatesResponse.unchanged && !this.purchase.savingUpdates) {
      // Another user moved the transfer to a different step: columns and
      // permissions change with it, so this is the one case that still needs
      // the full reload.
      if (purchaseUpdatesResponse.status && this.purchase.status && purchaseUpdatesResponse.status !== this.purchase.status) {
        console.log('transfer status changed elsewhere. reload purchase.');
        this.loadPurchase();
        return;
      }

      if (purchaseUpdatesResponse.updatedItems && purchaseUpdatesResponse.updatedItems.length > 0) {
        if (!this.applyRemoteItemUpdates(purchaseUpdatesResponse)) {
          await this.resyncItems();
        }
      } else if (typeof purchaseUpdatesResponse.itemCount === 'number'
        && purchaseUpdatesResponse.itemCount !== this.purchase.items.length) {
        // No line changed but the count did: someone removed a line (a removal
        // leaves no newer timeUpdated behind). Refresh the lines in place.
        await this.resyncItems();
      } else {
        this.purchase.lastRetrieved = purchaseUpdatesResponse.lastRetrieved;
      }
    }
    return purchaseUpdatesResponse;
  }

  // Desktop header refresh: the same pull the timer makes (which backs off to
  // 30 minutes on a quiet transfer), on demand, and the timer restarts at its
  // shortest delay. Spins while the request is out (`refreshing`, declared
  // with the poll state above) and says what it found.
  async refreshUpdates(event = null) {
    if (event) {
      event.stopPropagation();
    }
    if (this.refreshing || !this.purchase || !this.purchase._id) {
      return;
    }
    this.refreshing = true;
    const started = Date.now();
    let response;
    try {
      this.autoSyncDelay = this.autoSyncDelayDefault;
      response = await this.loadPurchaseUpdates(null);
    } finally {
      // Keep the spin visible for a beat so a fast reply still reads as "checked".
      setTimeout(() => { this.refreshing = false; }, Math.max(0, 600 - (Date.now() - started)));
    }
    if (response === undefined) {
      // Skipped: a save is in flight, the wizard is open, or offline — the timer covers it.
      return;
    }
    if (response === null) {
      AppComponent.showSnack('Could not check for updates — will retry automatically', 'OK', 3000);
      return;
    }
    const lines = response.unchanged ? 0 : ((response.updatedItems && response.updatedItems.length) || 0);
    AppComponent.showSnack(lines > 0 ? 'Updated ' + lines + ' line' + (lines === 1 ? '' : 's') : 'Up to date', 'OK', 2000);
  }

  // Folds other users' line changes into the open transfer WITHOUT replacing
  // the purchase object: edits land on the existing line objects, lines others
  // added are appended at their server index, so the scanner in this hand
  // keeps its active line, keyboard buffer, search and scroll position (the
  // old path reloaded the whole transfer on every remote add). Returns false
  // when the arrays can no longer be reconciled by index (a removal shifted
  // lines, a concurrent add raced ours), in which case resyncItems() refreshes
  // the lines in place instead.
  applyRemoteItemUpdates(response): boolean {
    const items = this.purchase.items;
    const updates = (response.updatedItems || []).slice().sort((a, b) => a.index - b.index);
    const touched: any[] = [];
    let appended = false;
    for (const update of updates) {
      const remote = update.item || {};
      const local = items[update.index];
      if (local && String(local.accountId) === String(remote.accountId)) {
        if (local.pendingSave || local.saving || this.isStaleRemoteLine(local, remote)) {
          // This device's newer count is still waiting to be saved (or is
          // being saved right now), or this reply is older than what the
          // line already shows; the reply that follows the save refreshes it.
          continue;
        }
        touched.push(local);
        if (this.cacheMode()) {
          this.mergeServerLine(local, remote);
          continue;
        }
        local.qty = remote.qty;
        local.expectedQty = remote.expectedQty;
        local.counted = remote.counted;
        // A per-line count basis (countAsOfLine) set by another device.
        if (remote.targetQtyAsOf !== undefined) {
          local.targetQtyAsOf = remote.targetQtyAsOf;
          if (remote.targetQty !== undefined) {
            local.targetQty = remote.targetQty;
          }
        }
        if (local.cost !== null) {
          local.cost = remote.cost;
        }
        if (local.price !== null) {
          local.price = remote.price;
        }
        if (remote.stockLocation !== undefined) {
          local.stockLocation = remote.stockLocation;
        }
        if (remote.countedTime !== undefined) { local.countedTime = remote.countedTime; }
        if (remote.countedUserId !== undefined) { local.countedUserId = remote.countedUserId; }
        if (remote.countedUserName !== undefined) { local.countedUserName = remote.countedUserName; }
        local.timeUpdated = remote.timeUpdated;
        continue;
      }
      if (local || update.index !== items.length) {
        // The slot holds a different line, or there is a gap: indexes shifted.
        return false;
      }
      const prepared = this.prepareRemoteItem(remote, update.index);
      this.initShares(prepared, this.purchase);
      items.push(prepared);
      appended = true;
    }
    if (typeof response.itemCount === 'number' && response.itemCount !== items.length) {
      return false;
    }
    if (appended) {
      this.updateBarcodeIndexes();
    }
    this.checkCountComplete();
    this.purchase.lastRetrieved = response.lastRetrieved;
    this.purchase.timeUpdated = response.timeUpdated ? new Date(response.timeUpdated) : this.purchase.timeUpdated;
    if (appended) {
      this.updateCalculations();
    } else {
      for (const line of touched) {
        this.updateLineCalculations(line);
      }
      // A remote change can move a row under a filter OR a column sort (a
      // scanner's add must surface at the top of a Time Counted sort).
      if (touched.length > 0 && (this.filtersActive() || this.sortKey)) {
        this.refreshDisplayItems();
      }
    }
    if (this.inventoryCacheService.isEnabled() && (touched.length > 0 || appended)) {
      if (appended) {
        this.transferQueueService.savePurchase(this.purchase);
      } else {
        this.transferQueueService.savePurchaseLines(String(this.purchase._id), touched);
      }
    }
    return true;
  }

  // A line as the server stores it, given the same per-line setup loadPurchase
  // applies (index, server-side qty baseline, count).
  private prepareRemoteItem(remote, index: number) {
    const item: any = { ...remote, index };
    item.serverQty = item.qty;
    if (!item.count) {
      item.count = 0;
    }
    return item;
  }

  // Refreshes the lines from the server while keeping everything else about
  // the open transfer: the purchase object, the active line (re-pointed by
  // account, with an unsaved edit carried over), the search box, the keyboard
  // buffer and the scroll position. A step change still goes through loadPurchase.
  async resyncItems() {
    await this.flushDeferredSaves();
    let fresh: any;
    try {
      fresh = await this.ioService.post('/transfer/getTransfer', {
        purchaseId: this.purchaseId
      });
    } catch (e) {
      console.log('resyncItems failed, reloading: ', e);
      this.loadPurchase();
      return;
    }
    if (!fresh || !Array.isArray(fresh.items) || fresh.status !== this.purchase.status || fresh.type !== this.purchase.type) {
      this.loadPurchase(fresh || null);
      return;
    }

    const previousActive = this.activeItem;
    const items = fresh.items;
    items.forEach((item, index) => {
      item.index = index;
      item.serverQty = item.qty;
      if (!item.count) {
        item.count = 0;
      }
      // Per-device shares, exactly as loadPurchase derives them. Without this
      // every refreshed line had no shares at all, and the next cache-mode
      // save of a line other scanners had also counted sent this device's
      // share as the WHOLE line total (value minus "nobody else"): a second
      // scanner's 50 was doubled onto the line ("65 + 1" became "65 + 51").
      // A remove or re-add on any scanner triggers this refresh everywhere.
      this.initShares(item, this.purchase);
    });

    this.purchase.items = items;
    this.purchase.timeUpdated = fresh.timeUpdated ? new Date(fresh.timeUpdated) : this.purchase.timeUpdated;
    this.purchase.lastRetrieved = fresh.lastRetrieved;
    (this.purchase as any).deletedLineCount = fresh.deletedLineCount;
    (this.purchase as any).processingProgress = fresh.processingProgress;
    this.updateBarcodeIndexes();

    if (previousActive) {
      const sameSlot = items[previousActive.index];
      const next = (sameSlot && String(sameSlot.accountId) === String(previousActive.accountId))
        ? sameSlot
        : items.find(item => String(item.accountId) === String(previousActive.accountId)) || null;
      if (next && (previousActive.edited || previousActive.pendingSave)) {
        // Keep the count being typed on this scanner; the server copy is older.
        for (const key of ['qty', 'targetQtyCalculated', 'sourceQtyCalculated', 'previousCount', 'previousCountField', 'edited', 'count']) {
          if (previousActive[key] !== undefined) {
            next[key] = previousActive[key];
          }
        }
      }
      this.activeItem = next;
      if (this.activeItemBaseline && next) {
        this.activeItemBaseline.index = next.index;
      }
    }

    this.checkCountComplete();
    this.updateCalculations();
    if (this.inventoryCacheService.isEnabled()) {
      this.transferQueueService.savePurchase(this.purchase);
    }
  }

  checkCountComplete() {
    let total = 0;
    let counted = 0;
    for (let item of this.purchase.items) {
      total ++;
      if (item.counted) {
        counted++;
      } else {
        if (item.targetQty == 0) {
          counted++;
        }
      }
    }

    if (counted == total) {
      this.purchase.countComplete = true;
    } else {
      this.purchase.countComplete = false;
    }

    return {
      total,
      counted,
    };
  }



  stopPropagation(event) {
    event.stopPropagation();
  }

  startEditSettings() {
    if (this.canEdit()) {
      this.purchase.settingsEdit = {
        name: this.purchase.name,
        note: this.purchase.note[0]
      };
      for (const field of this.customFields()) {
        this.purchase.settingsEdit[field.key] = this.purchase[field.key];
      }
    }
  }

  cancelSettingsEdit() {
    this.purchase.settingsEdit = null;
  }

  saveSettingsEdit = async () => {
    const missing = this.missingRequiredCustomFields(this.purchase.settingsEdit);
    if (missing.length > 0) {
      this.snackMissingCustomFields(missing);
      return;
    }
    this.purchase.name = this.purchase.settingsEdit.name;
    this.purchase.note[0] = this.purchase.settingsEdit.note;
    for (const field of this.customFields()) {
      this.purchase[field.key] = this.purchase.settingsEdit[field.key];
    }
    this.save();
  }

  cancelMissingBarcode() {
    this.purchase.missingBarcode = null;
  }

  acknowledgeErrors() {
    this.purchase.errors = null;
  }

  transferControlsLaunch() {
    console.log('transferControlsLaunch');
    this.purchase.controls = true;
  }

  cancelPurchaseControls() {
    this.purchase.controls = null;
  }


  // ==========================================================================
  // Local Inventory Cache: cache-first scans, queued saves, per-device shares
  // ==========================================================================

  // Scanning/saving through the device cache and queue: the setting is on and
  // the catalog has been loaded at least once.
  cacheMode(): boolean {
    return this.inventoryCacheService.isReady();
  }

  // The step's input field this device's share is counted in (counted units,
  // units to remove, or plain qty) — the same choice the server makes.
  private shareField(purchase = this.purchase): string {
    if (!purchase || !purchase.typeObject) {
      return 'qty';
    }
    const fields = this.getStatusFields(purchase.typeObject, purchase.status)
      .filter(field => field && field.editable === true && !field.unitOf
        && ['targetQtyCalculated', 'sourceQtyCalculated', 'qtyRemove', 'qty'].indexOf(field.key) !== -1);
    const preferred = fields.find(field => field.default) || fields[0];
    return preferred ? preferred.key : 'qty';
  }

  // A line's value in share units, derived from its stored qty.
  private inputValueOf(item, purchase = this.purchase): number {
    const qty = Number(item.qty) || 0;
    switch (item.contributionField || this.shareField(purchase)) {
      case 'targetQtyCalculated': return (Number(item.targetQty) || 0) + qty;
      case 'sourceQtyCalculated': return (Number(item.sourceQty) || 0) - qty;
      case 'qtyRemove': return -qty;
      default: return qty;
    }
  }

  private qtyFromInputValue(item, value: number, purchase = this.purchase): number {
    switch (item.contributionField || this.shareField(purchase)) {
      case 'targetQtyCalculated': return value - (Number(item.targetQty) || 0);
      case 'sourceQtyCalculated': return (Number(item.sourceQty) || 0) - value;
      case 'qtyRemove': return -value;
      default: return value;
    }
  }

  // Splits a line's value into this device's share and everyone else's from
  // the server's contributions map (a line without one is all "others").
  // Shares are in the step's share field of the purchase being loaded — at
  // load time this.purchase is still the previous transfer (or the
  // placeholder), whose share field would be plain qty: a fresh count line
  // (qty = -onHand) then started as otherShares -onHand and this device's
  // first scan persisted as onHand + 1 ("1 + 310").
  private initShares(item, purchase) {
    const deviceId = this.deviceSettingsService.getDeviceId();
    const contributions = item.contributions || null;
    if (!contributions) {
      item.myShare = 0;
      item.otherShares = this.inputValueOf(item, purchase);
      return;
    }
    let others = 0;
    for (const key of Object.keys(contributions)) {
      if (key !== deviceId) {
        others += Number(contributions[key] && contributions[key].qty) || 0;
      }
    }
    item.otherShares = others;
    item.myShare = contributions[deviceId] ? (Number(contributions[deviceId].qty) || 0) : 0;
  }

  // ---- in-flight line adds/removes (server mode) --------------------------
  // A scan lookup, a save or a removal must not race a just-posted add or
  // removal of the same line: the server lookup would still see the removed
  // line (and report the item "not found"), a removal could land before its
  // line's add. Structural writes are awaited per account.
  private trackLineWrite(accountId, write: Promise<any>) {
    const key = String(accountId);
    this.lineWritesInFlight.set(key, write);
    write.catch(() => undefined).then(() => {
      if (this.lineWritesInFlight.get(key) === write) {
        this.lineWritesInFlight.delete(key);
      }
    });
  }

  private async awaitLineWrites(accountId: string = null) {
    const writes = accountId
      ? [this.lineWritesInFlight.get(String(accountId))].filter(Boolean)
      : Array.from(this.lineWritesInFlight.values());
    if (writes.length > 0) {
      await Promise.all(writes.map(write => write.catch(() => undefined)));
    }
  }

  // ---- deferred line saves ----------------------------------------------
  // A re-scan of the selected line changes the count on screen at once, but
  // the save waits: scanning the same item several times in a row sends the
  // final count once — when another item is scanned or selected, Save Change
  // is tapped, the transfer moves on, or scanning pauses for a moment. Every
  // save is the line's absolute value, so coalescing never loses a count.
  static readonly DEFERRED_SAVE_MS = 2000;
  private deferredSaves = new Map<any, any>();

  deferLineSave(item) {
    if (!item) {
      return;
    }
    this.markCountedByMe(item);
    item.pendingSave = true;
    this.refreshDisplayItems();
    const existing = this.deferredSaves.get(item);
    if (existing) {
      clearTimeout(existing);
    }
    this.deferredSaves.set(item, setTimeout(() => this.flushLineSave(item), TransferComponent.DEFERRED_SAVE_MS));
  }

  // Drops a line's deferred save without sending it (the caller is saving or
  // discarding the line itself).
  private cancelDeferredSave(item) {
    const timer = this.deferredSaves.get(item);
    if (timer) {
      clearTimeout(timer);
    }
    this.deferredSaves.delete(item);
    if (item) {
      delete item.pendingSave;
    }
  }

  private async flushLineSave(item) {
    if (!item || !item.pendingSave) {
      this.cancelDeferredSave(item);
      return;
    }
    this.cancelDeferredSave(item);
    try {
      await this.confirmChange(item);
    } catch (e) {
      console.log('deferred save failed: ', e);
    }
  }

  hasDeferredSaves(): boolean {
    return this.deferredSaves.size > 0;
  }

  async flushDeferredSaves(except = null) {
    const items = Array.from(this.deferredSaves.keys());
    for (const item of items) {
      if (except && item === except) {
        continue;
      }
      await this.flushLineSave(item);
    }
  }

  // A line saved through the server path while the cache is enabled (catalog
  // still loading, or a desktop-style edit): the server folded the change into
  // the line's shares, so re-derive this device's share and the others' from
  // its response — otherwise the next cache-mode edit of the same line counts
  // the change twice.
  private adoptSavedLine(item, response) {
    if (!item || !response) {
      return;
    }
    const key = Object.keys(response).find(name => name.startsWith('items.'));
    const saved = key ? response[key] : null;
    if (saved) {
      // "Time Counted" / "Counted By" come from the server's stamp.
      for (const stamp of ['countedTime', 'countedUserId', 'countedUserName']) {
        if (saved[stamp] !== undefined) {
          item[stamp] = saved[stamp];
        }
      }
      // Count-as-of per line (PurchaseType.countAsOfLine): the server re-based
      // the line's On Hand on the balance when it was counted. Adopt the basis
      // and keep the counted value shown here — digits typed since the save
      // went out ride on top of it, as they always have.
      if (saved.targetQtyAsOf !== undefined) {
        item.targetQtyAsOf = saved.targetQtyAsOf;
        if (saved.targetQty !== undefined && Number(saved.targetQty) !== Number(item.targetQty)) {
          const counted = this.inputValueOf(item);
          item.targetQty = saved.targetQty;
          item.qty = this.qtyFromInputValue(item, counted);
          item.serverQty = item.qty;
          this.updateLineCalculations(item);
        }
      }
    }
    if (!this.inventoryCacheService.isEnabled()) {
      return;
    }
    if (saved && saved.contributions) {
      item.contributions = saved.contributions;
      if (saved.contributionField) {
        item.contributionField = saved.contributionField;
      }
    }
    this.initShares(item, this.purchase);
  }

  // Other devices' shares (by name) for the breakdown shown on a line.
  shareBreakdown(item): Array<{ name: string; qty: number; mine: boolean }> {
    const deviceId = this.deviceSettingsService.getDeviceId();
    const contributions = (item && item.contributions) || {};
    return Object.keys(contributions).map(key => ({
      name: key === deviceId ? 'This device' : (contributions[key].name || (key === 'manual' ? 'Entered on transfer' : 'Another device')),
      qty: Number(contributions[key].qty) || 0,
      mine: key === deviceId,
    }));
  }

  hasOtherShares(item): boolean {
    return !!item && typeof item.otherShares === 'number' && item.otherShares !== 0 && !!item.contributions;
  }

  // Server line → local line: on-hand filled in, contributions, derived qty.
  // The local share wins while a change of this device's is still queued.
  // A server copy of a line older than the one already merged (a poll reply
  // overtaken by a later save's reply) must not roll the line back.
  private isStaleRemoteLine(local, remote): boolean {
    if (!remote || !remote.timeUpdated || !local || !local.timeUpdated) {
      return false;
    }
    return new Date(remote.timeUpdated).getTime() < new Date(local.timeUpdated).getTime();
  }

  private mergeServerLine(local, remote) {
    if (this.isStaleRemoteLine(local, remote)) {
      return;
    }
    const deviceId = this.deviceSettingsService.getDeviceId();
    const pending = this.pendingAccountIds.has(String(local.accountId)) || local.pendingSave === true;
    // Scans this device has made but not yet saved (a deferred save) are
    // the gap between the line's value and its known shares. They must
    // survive a merge: before this, a server reply landing between a re-scan
    // and its save recomputed the line from the stale shares and the count
    // visibly dropped — and the dropped scan was then never saved.
    // A line whose shares were never derived (no baseline to measure the
    // gap from) has nothing unsaved to protect; measuring it would count
    // the whole value as unsaved and double it onto the server's.
    const sharesKnown = typeof local.myShare === 'number' || typeof local.otherShares === 'number';
    const unsaved = sharesKnown
      ? this.inputValueOf(local) - ((Number(local.myShare) || 0) + (Number(local.otherShares) || 0))
      : 0;
    if (remote.targetQty !== undefined) { local.targetQty = remote.targetQty; }
    if (remote.targetQtyAsOf !== undefined) { local.targetQtyAsOf = remote.targetQtyAsOf; }
    if (remote.sourceQty !== undefined) { local.sourceQty = remote.sourceQty; }
    if (remote.cost !== undefined && local.cost !== null) { local.cost = remote.cost; }
    if (remote.price !== undefined && local.price !== null) { local.price = remote.price; }
    if (remote.stockLocation !== undefined) { local.stockLocation = remote.stockLocation; }
    if (remote.categoryName !== undefined) { local.categoryName = remote.categoryName; }
    if (remote.subcategoryName !== undefined) { local.subcategoryName = remote.subcategoryName; }
    if (remote.countedTime !== undefined) { local.countedTime = remote.countedTime; }
    if (remote.countedUserId !== undefined) { local.countedUserId = remote.countedUserId; }
    if (remote.countedUserName !== undefined) { local.countedUserName = remote.countedUserName; }
    if (remote.expectedQty !== undefined) { local.expectedQty = remote.expectedQty; }
    if (remote.contributionField) { local.contributionField = remote.contributionField; }
    local.contributions = remote.contributions || local.contributions;
    if (remote.timeUpdated) { local.timeUpdated = remote.timeUpdated; }
    if (!pending) {
      local.pendingOnHand = false;
    }
    if (local.contributions) {
      let others = 0;
      for (const key of Object.keys(local.contributions)) {
        if (key !== deviceId) {
          others += Number(local.contributions[key] && local.contributions[key].qty) || 0;
        }
      }
      local.otherShares = others;
      if (!pending) {
        local.myShare = local.contributions[deviceId] ? (Number(local.contributions[deviceId].qty) || 0) : 0;
        local.qty = remote.qty;
        local.counted = remote.counted;
      } else {
        local.qty = this.qtyFromInputValue(local, local.myShare + local.otherShares + unsaved);
      }
    } else if (!pending) {
      local.qty = remote.qty;
      local.counted = remote.counted;
      local.otherShares = this.inputValueOf(local);
      local.myShare = 0;
    }
    if (unsaved && !pending) {
      // Not queued, but a deferred scan exists: keep it on top of the server's value.
      local.qty = this.qtyFromInputValue(local, this.inputValueOf(local) + unsaved);
      local.otherShares = this.inputValueOf(local) - unsaved - (Number(local.myShare) || 0);
    }
    local.serverQty = local.qty;
  }

  // The catalog lookup getInventoryAccounts would do, from the device cache:
  // eligible accounts for this transfer type, lines already on the transfer
  // included only when the caller asks for them (decoded labels).
  // Cache first; a miss while online asks the server (a product added or
  // re-coded since the last catalog sync, or a code the cache does not index)
  // and schedules a catalog sync so the cache catches up. Reasons that are
  // not about the cache (already on the transfer, not available here) stand.
  // Reasons that are not about the cache (already on the transfer) stand.
  // A code the cache does not know, OR one whose cached copy says ineligible
  // (not countable, not at this location), asks the server while online: the
  // cached row may simply be stale — an item toggled countable minutes ago
  // scanned as "not available for this transfer" until the next catalog sync,
  // with no way to add it from the handheld. The server's answer is the
  // authority; a hit also schedules a sync so the cache catches up.
  private async cachedCatalogLookupWithFallback(barcode: string, includeExisting: boolean): Promise<any[]> {
    const cached = await this.cachedCatalogLookup(barcode, includeExisting);
    const cacheMiss = this.lastCacheLookupNote === TransferComponent.CACHE_MISS_NOTE;
    const cacheIneligible = this.lastCacheLookupNote === TransferComponent.CACHE_INELIGIBLE_NOTE;
    if (cached.length > 0 || !this.connectivityService.isOnline() || !(cacheMiss || cacheIneligible)) {
      return cached;
    }
    try {
      await this.awaitLineWrites();
      const remote: any = await this.ioService.post('/transfer/getInventoryAccounts', {
        purchaseId: this.purchase._id,
        barcode,
        includeExisting,
      });
      if (Array.isArray(remote) && remote.length > 0) {
        this.lastCacheLookupNote = '';
        this.inventoryCacheService.sync();
        return remote;
      }
      // The server agrees: keep the cache's reason for an ineligible item.
      if (cacheMiss) {
        this.lastCacheLookupNote = 'not in the item catalog';
      }
    } catch (e) {
      console.log('server lookup after a cache ' + (cacheMiss ? 'miss' : 'ineligible hit') + ' failed: ', e);
      if (cacheMiss) {
        this.lastCacheLookupNote = 'not in this device\'s item cache and the server could not be reached';
      }
    }
    return cached;
  }

  private async cachedCatalogLookup(barcode: string, includeExisting: boolean): Promise<any[]> {
    const typeObject = this.purchase.typeObject || {};
    const rows: CachedItem[] = await this.inventoryCacheService.lookupByCode(barcode, {
      vendorLinked: this.isVendorLinkedType(typeObject),
    });
    const results: any[] = [];
    let ineligible = 0;
    let alreadyOn = 0;
    for (const row of rows) {
      if (!this.inventoryCacheService.isEligible(row, this.purchase, typeObject)) {
        ineligible++;
        continue;
      }
      const onTransfer = this.purchase.items.some(line => String(line.accountId) === row._id);
      if (onTransfer && !includeExisting) {
        alreadyOn++;
        continue;
      }
      results.push(this.inventoryCacheService.toCatalogItem(row, this.purchase));
    }
    this.lastCacheLookupNote = results.length > 0 ? ''
      : rows.length === 0 ? TransferComponent.CACHE_MISS_NOTE
      : alreadyOn > 0 ? 'already on this transfer'
      : TransferComponent.CACHE_INELIGIBLE_NOTE;
    return results;
  }

  // Queues one change for a line and remembers it is in flight.
  private async enqueueLineOp(item, kind: 'add' | 'set' | 'remove' | 'text', extra: any) {
    // Keypad and text-field edits reach here without passing the scan guard.
    if (this.unavailable) {
      this.notifyUnavailable();
      return;
    }
    const accountId = String(item.accountId);
    this.pendingAccountIds.add(accountId);
    item.serverQty = item.qty;
    await this.transferQueueService.enqueue({
      purchaseId: String(this.purchase._id),
      transferLabel: transferLabelOf(this.purchase),
      accountId,
      kind,
      value: extra.value,
      set: extra.set,
      fieldKey: extra.fieldKey,
      entryMethod: extra.entryMethod,
    });
  }

  // What confirmChange/saveItem do in cache mode: this device's share becomes
  // the line's value minus everyone else's, queued as an absolute "set" (safe
  // to replay twice), plus any text field that changed.
  private async persistLineViaQueue(item) {
    if (!item) {
      return;
    }
    // A deferred flush of the OPEN line must not reset the keypad's
    // append state mid-entry ("5" flushed, then "7" typed → 57, not 7);
    // saveItem clears the flag itself before calling here.
    if (item !== this.activeItem) {
      delete item.edited;
    }
    if (this.hasSteps() && (this.currentStepInfo()?.step?.requireCount || this.purchase.typeObject?.countAsOf)) {
      item.counted = true;
    }
    const value = this.inputValueOf(item);
    item.myShare = value - (Number(item.otherShares) || 0);
    await this.enqueueLineOp(item, 'set', { value: item.myShare });
    for (const field of this.textFields()) {
      const current = item[field.key] === undefined ? null : item[field.key];
      const saved = item._savedText && item._savedText[field.key] !== undefined ? item._savedText[field.key] : null;
      if (current !== saved) {
        item._savedText = { ...(item._savedText || {}), [field.key]: current };
        await this.enqueueLineOp(item, 'text', { fieldKey: field.key, value: current });
      }
    }
    this.updateLineCalculations(item);
  }

  // A drained batch: the server's lines (on-hand filled, every device's
  // share) replace what this device assumed; errors are kept to show.
  private async onQueueApplied(batch: AppliedBatch) {
    if (!this.purchase || !this.purchase._id || String(this.purchase._id) !== String(batch.purchaseId)) {
      return;
    }
    // The push was refused because the transfer is deleted / complete /
    // canceled: the page must stop taking scans, not just list the failures.
    if (batch.refused && batch.refused.status) {
      await this.markUnavailable(batch.refused.status, batch.refused.transfer);
      return;
    }
    for (const result of (batch.results || [])) {
      if (result.status === 'error') {
        this.failedOps = await this.transferQueueService.failedFor(String(this.purchase._id));
      }
    }
    const stillPending = new Set((await this.transferQueueService.pendingFor(String(this.purchase._id))).map(op => op.accountId));
    this.pendingAccountIds = stillPending;
    let structural = false;
    const touched: any[] = [];
    for (const entry of (batch.lines || [])) {
      const remote = entry.item;
      const local = this.purchase.items.find(line => String(line.accountId) === String(remote.accountId));
      if (local) {
        this.mergeServerLine(local, remote);
        touched.push(local);
      } else if (entry.index === this.purchase.items.length) {
        const prepared = this.prepareRemoteItem(remote, entry.index);
        this.initShares(prepared, this.purchase);
        this.purchase.items.push(prepared);
        structural = true;
      }
    }
    if (batch.status && this.purchase.status && batch.status !== this.purchase.status && batch.itemCount >= 0) {
      this.loadPurchase();
      return;
    }
    // A count mismatch while this device still has ops queued (e.g. the add
    // that follows a removal) is expected — the next batch reconciles it.
    if (structural || (batch.itemCount >= 0 && batch.itemCount !== this.purchase.items.length && stillPending.size === 0)) {
      if (this.connectivityService.isOnline()) {
        await this.resyncItems();
        return;
      }
      this.updateBarcodeIndexes();
    }
    this.checkCountComplete();
    for (const line of touched) {
      this.updateLineCalculations(line);
    }
    if (touched.length > 0 && (this.filtersActive() || this.sortKey)) {
      this.refreshDisplayItems();
    }
    if (this.inventoryCacheService.isEnabled()) {
      // Only the lines this batch changed go to the offline copy.
      this.transferQueueService.savePurchaseLines(String(this.purchase._id), touched);
    }
  }

  // ---- status dot / sheet / stale alert ----

  // green: online, cache current, nothing queued · yellow: syncing or pushing
  // · amber: online but changes queued or the cache is stale · red: offline
  // (scans still recorded) · grey: cache off.
  syncDotState(): 'off' | 'green' | 'yellow' | 'amber' | 'red' {
    if (!this.inventoryCacheService.isEnabled()) {
      return 'off';
    }
    const cache = this.cacheStatus || this.inventoryCacheService.getStatus();
    const queue = this.queueStatus || this.transferQueueService.getStatus();
    if (!this.connectivityService.isOnline()) {
      return 'red';
    }
    if (queue.replaying || cache.state === 'syncing' || cache.state === 'loading') {
      return 'yellow';
    }
    if (queue.pending > 0 || queue.failed > 0 || cache.state === 'error' || cache.state === 'empty') {
      return 'amber';
    }
    if (cache.lastSync && Date.now() - new Date(cache.lastSync).getTime() > 60 * 60 * 1000) {
      return 'amber';
    }
    return 'green';
  }

  syncDotTitle(): string {
    const queue = this.queueStatus || this.transferQueueService.getStatus();
    switch (this.syncDotState()) {
      case 'red': return 'Offline — scans are being saved on this device' + (queue.pending ? ' (' + queue.pending + ' waiting)' : '');
      case 'yellow': return queue.replaying ? 'Sending ' + queue.pending + ' change' + (queue.pending === 1 ? '' : 's') + '…' : 'Updating the item cache…';
      case 'amber': return queue.pending
        ? queue.pending + ' change' + (queue.pending === 1 ? '' : 's') + ' waiting to send'
        : (queue.failed ? this.failedSummary(queue.failed) : 'Item cache needs a sync');
      case 'green': return 'Up to date';
      default: return 'Local inventory cache is off';
    }
  }

  async toggleSyncSheet() {
    this.syncSheetOpen = !this.syncSheetOpen;
    if (this.syncSheetOpen) {
      await this.loadFailedOps();
    }
  }

  private async loadFailedOps() {
    const id = this.purchase && this.purchase._id ? String(this.purchase._id) : null;
    this.failedOps = id ? await this.transferQueueService.failedFor(id) : [];
    this.otherFailedGroups = (await this.transferQueueService.failedGroups()).filter(group => group.purchaseId !== id);
  }

  // "N changes on a deleted transfer" when every failed change belongs to a
  // transfer that is gone — the dot's own text then explains itself.
  private failedSummary(count: number): string {
    const changes = count + ' change' + (count === 1 ? '' : 's');
    const statuses = this.otherFailedGroups.map(group => group.status);
    if (this.failedOps.length > 0) {
      statuses.push(this.unavailable ? this.unavailable.status : null);
    }
    const allDead = statuses.length > 0 && statuses.every(status => status === 'deleted' || status === 'missing');
    return changes + (allDead ? ' on ' + (statuses.length === 1 ? 'a deleted transfer' : 'deleted transfers') : ' could not be applied');
  }

  async retryFailedGroup(group: FailedGroup) {
    const count = await this.transferQueueService.retryFailed(group.purchaseId);
    await this.loadFailedOps();
    AppComponent.showSnack(count ? 'Retrying ' + count + ' change' + (count === 1 ? '' : 's') + '…' : 'Nothing to retry.', 'OK', 3000);
  }

  async dismissFailedGroup(group: FailedGroup) {
    await this.transferQueueService.dismissFailed(group.purchaseId);
    await this.loadFailedOps();
  }

  // Jump to the transfer a failed change belongs to. init handles the id-only
  // route only while no transfer is open, so use the typed route when the
  // type is known and reload the page otherwise.
  openTransfer(group: FailedGroup) {
    this.syncSheetOpen = false;
    if (group.type) {
      this.router.navigate(['transfers', group.type, group.purchaseId]);
    } else {
      window.location.href = '/transfers/' + group.purchaseId;
    }
  }

  // ---- a transfer the server has closed ----

  // getTransfer / getTransferUpdates / applyItemOps all answer this way for a
  // transfer that cannot be loaded; older APIs send the message only.
  private isUnavailableError(e): boolean {
    const err = e && e.error ? e.error : null;
    return !!err && (err.key === 'purchaseFindError' || err.msg === 'No matching purchase found.');
  }

  private unavailableFromError(e): { status: string; detail: any } {
    const err = e && e.error ? e.error : {};
    return { status: err.status ? String(err.status) : 'missing', detail: err.transfer || null };
  }

  // The server says the open transfer is gone — deleted, completed or canceled
  // elsewhere, or no such id (an older API cannot say which). Polling and
  // scanning stop, the banner says why, and this transfer's queued changes
  // become failed now, with that reason, rather than after a push that cannot
  // succeed. Setting the status is what hides the scan UI: the existing
  // guards (canEdit, the search box's *ngIf) key off it.
  private async markUnavailable(status: string, detail: any = null) {
    const first = !this.unavailable;
    this.unavailable = { status, detail: detail || null, at: new Date() };
    this.pollStopped = true;
    if (this.autoSyncTimeout) {
      clearTimeout(this.autoSyncTimeout);
      this.autoSyncTimeout = null;
    }
    if (this.purchase && this.purchase._id) {
      this.purchase.status = status === 'missing' ? 'deleted' : status;
      for (const field of ['timeDeleted', 'timeCanceled', 'cancelUserName', 'timeCompleted', 'completeUserName']) {
        if (detail && detail[field] && !this.purchase[field]) {
          this.purchase[field] = detail[field];
        }
      }
      const id = String(this.purchase._id);
      await this.transferQueueService.failPending(id, this.unavailableText(status, detail || this.purchase), status, detail);
      if (this.inventoryCacheService.isEnabled()) {
        // The copy saved on this device reopens closed too, even offline.
        this.transferQueueService.savePurchase(this.purchase);
      }
      await this.loadFailedOps();
    }
    if (first) {
      try {
        if ((navigator as any).vibrate) {
          (navigator as any).vibrate([200, 100, 200]);
        }
      } catch (e) {
      }
    }
  }

  // One sentence for a transfer that refuses changes. `source` carries the
  // who/when fields: the server's description, or the purchase itself.
  unavailableText(status: string, source: any = null): string {
    const s = source || {};
    const when = time => time ? ' on ' + new Date(time).toLocaleString() : '';
    const by = name => name ? ' by ' + name : '';
    switch (status) {
      case 'deleted': return 'This transfer was deleted' + when(s.timeDeleted) + '.';
      case 'complete': return 'This transfer is complete' + by(s.completeUserName) + when(s.timeCompleted) + '.';
      case 'canceled':
      case 'canceling': return 'This transfer was canceled' + by(s.cancelUserName) + when(s.timeCanceled) + '.';
      case 'reversed': return 'This transfer was reversed' + by(s.reverseUserName) + when(s.timeReversed) + '.';
      case 'reversing': return 'This transfer is being reversed' + by(s.reverseUserName) + '.';
      case 'processing': return 'This transfer is processing.';
      case 'missing': return 'This transfer no longer exists on the server.';
      default: return 'This transfer is ' + status + '.';
    }
  }

  // A snack, not a dialog (a focused button would swallow the scanner's
  // Enter), and at most one every few seconds however fast the scans come.
  private notifyUnavailable() {
    if (Date.now() - this.lastUnavailableNoticeAt < 3000) {
      return;
    }
    this.lastUnavailableNoticeAt = Date.now();
    const why = this.unavailable ? this.unavailableText(this.unavailable.status, this.unavailable.detail || this.purchase) : '';
    AppComponent.showSnack((why + ' Scans here are not saved.').trim(), 'OK', 4000);
  }

  syncNow() {
    this.inventoryCacheService.sync();
    this.transferQueueService.forceReplay();
  }

  // Scans that have waited more than a minute: the sheet offers "Send now" so
  // the user always has a manual way to push them (the automatic retry may
  // be stuck behind a request that never answered).
  pendingStuck(): boolean {
    const status = this.queueStatus;
    return !!status && status.pending > 0 && !!status.oldestPendingAt
      && Date.now() - new Date(status.oldestPendingAt).getTime() > 60 * 1000;
  }

  sendPendingNow() {
    this.transferQueueService.forceReplay();
  }

  // Re-queue this transfer's failed changes and push them again.
  async retryFailedOps() {
    if (!this.purchase || !this.purchase._id) {
      return;
    }
    const count = await this.transferQueueService.retryFailed(String(this.purchase._id));
    this.failedOps = [];
    AppComponent.showSnack(count ? 'Retrying ' + count + ' change' + (count === 1 ? '' : 's') + '…' : 'Nothing to retry.', 'OK', 3000);
  }

  async dismissFailedOps() {
    if (this.purchase && this.purchase._id) {
      await this.transferQueueService.dismissFailed(String(this.purchase._id));
      this.failedOps = [];
    }
  }

  // The 15-minute alert: a banner the user must dismiss, never a focused
  // dialog — a focused button would swallow the scanner's Enter.
  private showStaleAlert(alert: { oldestPendingAt: Date; pending: number }) {
    const minutes = Math.max(15, Math.round((Date.now() - alert.oldestPendingAt.getTime()) / 60000));
    this.staleAlertText = alert.pending + ' change' + (alert.pending === 1 ? '' : 's') + ' on this device ' + (alert.pending === 1 ? 'has' : 'have')
      + ' not reached the server for ' + minutes + ' minutes. Check the wifi; scanning can continue.';
    this.staleAlertOpen = true;
    try {
      if ((navigator as any).vibrate) {
        (navigator as any).vibrate([200, 100, 200, 100, 400]);
      }
    } catch (e) {
    }
  }

  dismissStaleAlert() {
    this.staleAlertOpen = false;
  }

  // On-hand (and anything derived from it) is unknown for a line added from
  // the cache until the server fills it in.
  isOnHandPending(item, field): boolean {
    return !!item && item.pendingOnHand === true && !!field
      && ['targetQty', 'sourceQty', 'qty', 'differenceQty', 'sourceQtyCalculated'].indexOf(field.key) !== -1;
  }
}
